/**
 * Shared rendering for Window Generator designs (thumbnails in Visual Designer, document view).
 * Payload: { root, w, h, opens } from window_generator_templates or meta.
 * Design mode uses light-theme canvas to match WG design tab in light mode.
 */
import React from 'react';
import { computeBayGeometry, createDefaultBayConfig, normalizeBayConfig } from '../utils/bayGeometry';
import { renderSharedBayPresentation } from './window-generator/rendering/bayPresentationKernel';

const OB = 18, IB = 18, GI = 4;
const isJambLikeVentMode = (vp) => vp === 'jamb' || vp === 'extender';
const headJambVentY = (innerY, ventH) => (innerY - OB) + OB / 2 - ventH / 2 - 2;
const extenderBarVentY = (windowOriginY, extTopPx, ventH) => windowOriginY - extTopPx / 2 - ventH / 2;
const resolveHeadLikeVentY = (vp, innerY, windowOriginY, extTopPx, ventH) =>
  vp === 'extender' && extTopPx > 0
    ? extenderBarVentY(windowOriginY, extTopPx, ventH)
    : headJambVentY(innerY, ventH);
const extenderVentModeActive = (vp, extTopPx) => vp !== 'extender' || extTopPx > 0;
/** Fixed design canvas background around window previews */
export const DESIGN_CANVAS_BG = '#d9dee7';
const PX2MM = 2.5;
const mm2px = (mm) => Math.round(Number(mm || 0) / PX2MM);
/** Render-only scale so frame extenders read clearly on canvas (mm values unchanged). */
const EXTENDER_VISUAL_SCALE = 1.1;
const extenderMmToPx = (mm) => {
  const px = mm2px(mm);
  return px > 0 ? Math.round(px * EXTENDER_VISUAL_SCALE) : 0;
};
/** Render px for bottom extender — thin bars vanish under cill; enforce a readable minimum. */
const extenderBottomVisualPx = (layoutPx) => {
  if (layoutPx <= 0) return 0;
  return Math.max(layoutPx, 12);
};
const C = {
  fO: '#c8cdd6', fI: '#e0e4ec', fS: '#9aa0ae', fH: '#f4f6fa', mi: '#b0b6c4',
  mut: '#6b7280',
};
/** White frames for design mode (matches WG LIGHT palette) */
const C_DESIGN = {
  fO: '#ffffff', fI: '#f8fafc', fS: '#e5e7eb', fH: '#ffffff', mi: '#e5e7eb',
};
const LOGICAL_TRANSOM_MM = 140;
const LOGICAL_MULLION_MM = 70;
const LOGICAL_DIV_MIN = 0;
const LOGICAL_DIV_MAX = 1;
const LOGICAL_TOP_SNAP_EPS = 0.05;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, Number.isFinite(v) ? v : lo));
const BAY_PRESENTATION_STARKNESS = {
  SUBTLE: 'subtle',
  BALANCED: 'balanced',
  STARK: 'stark',
};
const BAY_PRESENTATION_STARKNESS_OPTIONS = [
  { code: BAY_PRESENTATION_STARKNESS.SUBTLE, depthMult: 0.3 },
  { code: BAY_PRESENTATION_STARKNESS.BALANCED, depthMult: 0.9 },
  { code: BAY_PRESENTATION_STARKNESS.STARK, depthMult: 3.8 },
];
const BAY_PRESENTATION_DEPTH_MULT_MIN = 0.1;
const BAY_PRESENTATION_DEPTH_MULT_MAX = 4.0;
const normalizeBayPresentationDepthMult = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    const def = BAY_PRESENTATION_STARKNESS_OPTIONS.find((o) => o.code === BAY_PRESENTATION_STARKNESS.BALANCED)?.depthMult || 1;
    return clamp(def, BAY_PRESENTATION_DEPTH_MULT_MIN, BAY_PRESENTATION_DEPTH_MULT_MAX);
  }
  return clamp(n, BAY_PRESENTATION_DEPTH_MULT_MIN, BAY_PRESENTATION_DEPTH_MULT_MAX);
};
const normalizeBayPresentationStarkness = (raw) => {
  const v = String(raw || '').trim().toLowerCase();
  return BAY_PRESENTATION_STARKNESS_OPTIONS.some((o) => o.code === v)
    ? v
    : BAY_PRESENTATION_STARKNESS.BALANCED;
};
const resolveBayPresentationDepthMult = ({ bayPresentationDepthMult = null, bayPresentationStarkness = null } = {}) => {
  if (bayPresentationDepthMult !== undefined && bayPresentationDepthMult !== null) {
    return normalizeBayPresentationDepthMult(bayPresentationDepthMult);
  }
  const code = normalizeBayPresentationStarkness(bayPresentationStarkness);
  const preset = BAY_PRESENTATION_STARKNESS_OPTIONS.find((o) => o.code === code);
  return normalizeBayPresentationDepthMult(preset?.depthMult);
};

function inferBayConfigFromRoot(root, ww, wh, rawConfig = null) {
  const fallback = createDefaultBayConfig({
    tangentWidthMm: Math.max(200, Math.round(Number(ww || 400) * PX2MM)),
    heightMm: Math.max(200, Math.round(Number(wh || 300) * PX2MM)),
  });
  if (!root || String(root?.preset || '') !== 'bay') {
    return normalizeBayConfig(rawConfig || fallback, {
      tangentWidthMm: Math.max(200, Math.round(Number(ww || 400) * PX2MM)),
      heightMm: Math.max(200, Math.round(Number(wh || 300) * PX2MM)),
    });
  }
  const tree = isRectUnionRoot(root) ? root.regions?.[0]?.root : root;
  const split = isCellTree(tree) ? tree : null;
  const fractions = (split && split.split === 'vertical' && Array.isArray(split.fractions) && split.fractions.length >= 2)
    ? split.fractions.map((v) => Math.max(0, Number(v) || 0))
    : [0.29, 0.42, 0.29];
  const sum = fractions.reduce((s, v) => s + v, 0) || 1;
  const nFractions = fractions.map((v) => v / sum);
  const tangentWidthMm = Math.max(200, Math.round(Number(ww || 400) * PX2MM));
  const heightMm = Math.max(200, Math.round(Number(wh || 300) * PX2MM));
  const facets = nFractions.map((fr, i) => ({
    id: `facet-${i + 1}`,
    label: nFractions.length === 3 ? (i === 0 ? 'Left return' : i === 1 ? 'Centre' : 'Right return') : `Facet ${i + 1}`,
    width: Math.max(120, Math.round(tangentWidthMm * fr)),
  }));
  const joints = Array.from({ length: Math.max(0, facets.length - 1) }, (_, i) => ({
    id: `joint-${i + 1}`,
    between: [facets[i].id, facets[i + 1].id],
  }));
  const inferred = {
    ...fallback,
    tangentWidth: tangentWidthMm,
    height: heightMm,
    facets,
    joints,
  };
  return normalizeBayConfig(rawConfig || inferred, {
    tangentWidthMm,
    heightMm,
  });
}

function darken(hex, amt) {
  const n = parseInt(String(hex || '#000000').replace('#',''), 16);
  const r = Math.max(0, ((n >> 16) & 0xff) - amt);
  const g = Math.max(0, ((n >> 8) & 0xff) - amt);
  const b = Math.max(0, (n & 0xff) - amt);
  return `rgb(${r},${g},${b})`;
}
function lighten(hex, amt) {
  const n = parseInt(String(hex || '#000000').replace('#',''), 16);
  const r = Math.min(255, ((n >> 16) & 0xff) + amt);
  const g = Math.min(255, ((n >> 8) & 0xff) + amt);
  const b = Math.min(255, (n & 0xff) + amt);
  return `rgb(${r},${g},${b})`;
}
function parseHexTriplet(hex) {
  const s = String(hex || '').trim().replace('#', '');
  const full = /^[0-9a-fA-F]{6}$/.test(s) ? s : (/^[0-9a-fA-F]{3}$/.test(s) ? s.split('').map((c) => c + c).join('') : null);
  if (!full) return null;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}
function handleContrastTokens(handleHex, frameHex) {
  const h = parseHexTriplet(handleHex);
  const f = parseHexTriplet(frameHex);
  if (!h || !f) {
    return {
      low: false,
      outline: 'rgba(0,0,0,0.3)',
      inner: 'rgba(255,255,255,0.2)',
      armStroke: 'rgba(0,0,0,0.3)',
      capStroke: darken(handleHex || '#888', 28),
      pivotFill: darken(handleHex || '#888', 20),
    };
  }
  const luma = (c) => 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
  const dist = Math.abs(h.r - f.r) + Math.abs(h.g - f.g) + Math.abs(h.b - f.b);
  const dL = Math.abs(luma(h) - luma(f));
  const hL = luma(h);
  const lowBase = dist < 140 || dL < 52;
  // Keep white/light handles in the original subtle style.
  const low = hL < 170 ? lowBase : false;
  const frameDark = luma(f) < 120;
  return {
    low,
    outline: low ? (frameDark ? 'rgba(255,255,255,0.98)' : 'rgba(0,0,0,0.98)') : 'rgba(0,0,0,0.3)',
    inner: low ? (frameDark ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.48)') : 'rgba(255,255,255,0.2)',
    armStroke: low ? (frameDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.9)') : 'rgba(0,0,0,0.3)',
    capStroke: low ? (frameDark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.9)') : darken(handleHex, 28),
    pivotFill: low ? (frameDark ? lighten(handleHex, 28) : darken(handleHex, 28)) : darken(handleHex, 20),
  };
}

function makeGk(idPrefix) {
  return (pre, x, y) => `${idPrefix}-${pre}${Math.round(x)}_${Math.round(y)}`;
}

function renderOuterCornerJoinLines(ox, oy, ww, wh, stroke, joinStyle = 'mitre-45', strokeWidth = '1', mode = 'design') {
  if (joinStyle === 'timber-weld') {
    return (
      <>
        <line x1={ox} y1={oy + OB} x2={ox + OB} y2={oy + OB} stroke={stroke} strokeWidth={strokeWidth} data-wg-element="frame-timber-joint-tl" data-wg-mode={mode}/>
        <line x1={ox + ww - OB} y1={oy + OB} x2={ox + ww} y2={oy + OB} stroke={stroke} strokeWidth={strokeWidth} data-wg-element="frame-timber-joint-tr" data-wg-mode={mode}/>
        <line x1={ox} y1={oy + wh - OB} x2={ox + OB} y2={oy + wh - OB} stroke={stroke} strokeWidth={strokeWidth} data-wg-element="frame-timber-joint-bl" data-wg-mode={mode}/>
        <line x1={ox + ww - OB} y1={oy + wh - OB} x2={ox + ww} y2={oy + wh - OB} stroke={stroke} strokeWidth={strokeWidth} data-wg-element="frame-timber-joint-br" data-wg-mode={mode}/>
      </>
    );
  }
  return (
    <>
      <line x1={ox} y1={oy} x2={ox+OB} y2={oy+OB} stroke={stroke} strokeWidth={strokeWidth} data-wg-element="frame-mitre-tl" data-wg-mode={mode}/>
      <line x1={ox+ww} y1={oy} x2={ox+ww-OB} y2={oy+OB} stroke={stroke} strokeWidth={strokeWidth} data-wg-element="frame-mitre-tr" data-wg-mode={mode}/>
      <line x1={ox} y1={oy+wh} x2={ox+OB} y2={oy+wh-OB} stroke={stroke} strokeWidth={strokeWidth} data-wg-element="frame-mitre-bl" data-wg-mode={mode}/>
      <line x1={ox+ww} y1={oy+wh} x2={ox+ww-OB} y2={oy+wh-OB} stroke={stroke} strokeWidth={strokeWidth} data-wg-element="frame-mitre-br" data-wg-mode={mode}/>
    </>
  );
}

function renderSashCornerJoinLines(sx, sy, sW, sH, sw, stroke, joinStyle = 'mitre-45', strokeWidth = '0.6') {
  if (joinStyle === 'timber-weld') {
    return (
      <>
        <line x1={sx + sw} y1={sy} x2={sx + sw} y2={sy + sw} stroke={stroke} strokeWidth={strokeWidth}/>
        <line x1={sx + sW - sw} y1={sy} x2={sx + sW - sw} y2={sy + sw} stroke={stroke} strokeWidth={strokeWidth}/>
        <line x1={sx + sw} y1={sy + sH - sw} x2={sx + sw} y2={sy + sH} stroke={stroke} strokeWidth={strokeWidth}/>
        <line x1={sx + sW - sw} y1={sy + sH - sw} x2={sx + sW - sw} y2={sy + sH} stroke={stroke} strokeWidth={strokeWidth}/>
      </>
    );
  }
  return (
    <>
      <line x1={sx} y1={sy} x2={sx+sw} y2={sy+sw} stroke={stroke} strokeWidth={strokeWidth}/>
      <line x1={sx+sW} y1={sy} x2={sx+sW-sw} y2={sy+sw} stroke={stroke} strokeWidth={strokeWidth}/>
      <line x1={sx} y1={sy+sH} x2={sx+sw} y2={sy+sH-sw} stroke={stroke} strokeWidth={strokeWidth}/>
      <line x1={sx+sW} y1={sy+sH} x2={sx+sW-sw} y2={sy+sH-sw} stroke={stroke} strokeWidth={strokeWidth}/>
    </>
  );
}

let _wgSvgInstanceCounter = 0;

function FrameTop(x, y, w, gk, fc = null) {
  const k = gk('oft', x, y);
  const fH = fc ? lighten(fc, 18) : C.fH;
  const fO = fc ? fc : C.fO;
  const fS = fc ? darken(fc, 35) : C.fS;
  return (
    <g key={k}>
      <defs><linearGradient id={k} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={fH}/><stop offset="40%" stopColor={fO}/><stop offset="100%" stopColor={fS}/></linearGradient></defs>
      <rect x={x} y={y} width={w} height={OB} fill={`url(#${k})`}/>
      <line x1={x} y1={y+OB-1} x2={x+w} y2={y+OB-1} stroke={fS} strokeWidth="1" opacity="0.5"/>
      <line x1={x} y1={y+2} x2={x+w} y2={y+2} stroke={fH} strokeWidth="1" opacity="0.6"/>
    </g>
  );
}
function FrameBot(x, y, w, gk, fc = null) {
  const k = gk('ofb', x, y);
  const fH = fc ? lighten(fc, 18) : C.fH;
  const fO = fc ? fc : C.fO;
  const fS = fc ? darken(fc, 35) : C.fS;
  return (
    <g key={k}>
      <defs><linearGradient id={k} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={fS}/><stop offset="60%" stopColor={fO}/><stop offset="100%" stopColor={fH}/></linearGradient></defs>
      <rect x={x} y={y} width={w} height={OB} fill={`url(#${k})`}/>
      <line x1={x} y1={y+1} x2={x+w} y2={y+1} stroke={fS} strokeWidth="1" opacity="0.5"/>
      <line x1={x} y1={y+OB-2} x2={x+w} y2={y+OB-2} stroke={fH} strokeWidth="1" opacity="0.4"/>
    </g>
  );
}
function FrameLeft(x, y, h, gk, fc = null) {
  const k = gk('ofl', x, y);
  const fH = fc ? lighten(fc, 18) : C.fH;
  const fO = fc ? fc : C.fO;
  const fS = fc ? darken(fc, 35) : C.fS;
  return (
    <g key={k}>
      <defs><linearGradient id={k} x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={fH}/><stop offset="40%" stopColor={fO}/><stop offset="100%" stopColor={fS}/></linearGradient></defs>
      <rect x={x} y={y} width={OB} height={h} fill={`url(#${k})`}/>
      <line x1={x+OB-1} y1={y} x2={x+OB-1} y2={y+h} stroke={fS} strokeWidth="1" opacity="0.5"/>
      <line x1={x+2} y1={y} x2={x+2} y2={y+h} stroke={fH} strokeWidth="1" opacity="0.6"/>
    </g>
  );
}
function FrameRight(x, y, h, gk, fc = null) {
  const k = gk('ofr', x, y);
  const fH = fc ? lighten(fc, 18) : C.fH;
  const fO = fc ? fc : C.fO;
  const fS = fc ? darken(fc, 35) : C.fS;
  return (
    <g key={k}>
      <defs><linearGradient id={k} x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={fS}/><stop offset="60%" stopColor={fO}/><stop offset="100%" stopColor={fH}/></linearGradient></defs>
      <rect x={x} y={y} width={OB} height={h} fill={`url(#${k})`}/>
      <line x1={x+1} y1={y} x2={x+1} y2={y+h} stroke={fS} strokeWidth="1" opacity="0.5"/>
      <line x1={x+OB-2} y1={y} x2={x+OB-2} y2={y+h} stroke={fH} strokeWidth="1" opacity="0.4"/>
    </g>
  );
}

function Mitre(x, y, corner, fc = null) {
  const s = OB;
  const fO = fc ? fc : C.fO;
  const fH = fc ? lighten(fc, 18) : C.fH;
  const fS = fc ? darken(fc, 35) : C.fS;
  const mi = fc ? darken(fc, 20) : C.mi;
  if (corner === 'tl') return <g key={`m-tl-${x}-${y}`}><rect x={x} y={y} width={s} height={s} fill={fO}/><polygon points={`${x},${y} ${x+s},${y} ${x+s},${y+s}`} fill={fH} opacity="0.4"/><line x1={x} y1={y} x2={x+s} y2={y+s} stroke={mi} strokeWidth="1.5"/></g>;
  if (corner === 'tr') return <g key={`m-tr-${x}-${y}`}><rect x={x} y={y} width={s} height={s} fill={fO}/><polygon points={`${x},${y} ${x+s},${y} ${x},${y+s}`} fill={fH} opacity="0.3"/><line x1={x+s} y1={y} x2={x} y2={y+s} stroke={mi} strokeWidth="1.5"/></g>;
  if (corner === 'bl') return <g key={`m-bl-${x}-${y}`}><rect x={x} y={y} width={s} height={s} fill={fO}/><polygon points={`${x},${y} ${x+s},${y+s} ${x},${y+s}`} fill={fH} opacity="0.2"/><line x1={x} y1={y+s} x2={x+s} y2={y} stroke={mi} strokeWidth="1.5"/></g>;
  if (corner === 'br') return <g key={`m-br-${x}-${y}`}><rect x={x} y={y} width={s} height={s} fill={fO}/><polygon points={`${x+s},${y} ${x+s},${y+s} ${x},${y+s}`} fill={fS} opacity="0.2"/><line x1={x} y1={y} x2={x+s} y2={y+s} stroke={mi} strokeWidth="1.5"/></g>;
  return null;
}

/** Design-tab style: simple solid outer frame (uses fc when provided, else C_DESIGN) */
function FrameOuter(ox, oy, ww, wh, fc = null, jointStyle = 'mitre-45') {
  const k = `dof-${Math.round(ox)}-${Math.round(oy)}`;
  const fO = fc || C_DESIGN.fO;
  const fS = fc ? darken(fc, 15) : C_DESIGN.fS;
  const fH = fc ? lighten(fc, 8) : C_DESIGN.fH;
  const mi = fc ? darken(fc, 15) : C_DESIGN.mi;
  return (
    <g key={k}>
      <path d={`M${ox},${oy} L${ox+ww},${oy} L${ox+ww},${oy+wh} L${ox},${oy+wh} Z M${ox+OB},${oy+OB} L${ox+OB},${oy+wh-OB} L${ox+ww-OB},${oy+wh-OB} L${ox+ww-OB},${oy+OB} Z`}
        fill={fO} fillRule="evenodd" data-wg-element="frame-outer-fill" data-wg-mode="design"/>
      <rect x={ox} y={oy} width={ww} height={wh} fill="none" stroke={fS} strokeWidth="0.5" data-wg-element="frame-outer-edge" data-wg-mode="design"/>
      <line x1={ox+0.5} y1={oy+0.5} x2={ox+ww-0.5} y2={oy+0.5} stroke={fH} strokeWidth="1" opacity="0.6" data-wg-element="frame-highlight-top" data-wg-mode="design"/>
      <line x1={ox+0.5} y1={oy+0.5} x2={ox+0.5} y2={oy+wh-0.5} stroke={fH} strokeWidth="0.8" opacity="0.5" data-wg-element="frame-highlight-left" data-wg-mode="design"/>
      {renderOuterCornerJoinLines(ox, oy, ww, wh, mi, jointStyle, '1', 'design')}
    </g>
  );
}

/** Design-tab style: flat solid bars (uses fc when provided, else C_DESIGN) */
function BarHDesign(x, y, w, gk, fc = null) {
  const k = gk('bhd', x, y);
  const fO = fc || C_DESIGN.fO;
  const fS = fc ? darken(fc, 15) : C_DESIGN.fS;
  return (
    <g key={k} data-wg-element="divider-bar-horizontal">
      <rect x={x} y={y} width={w} height={IB} fill={fO} data-wg-element="divider-bar-fill-horizontal"/>
      <line x1={x} y1={y} x2={x+w} y2={y} stroke={fS} strokeWidth="0.15" opacity="0.4" data-wg-element="divider-seam-top"/>
      <line x1={x} y1={y+IB} x2={x+w} y2={y+IB} stroke={fS} strokeWidth="0.15" opacity="0.4" data-wg-element="divider-seam-bottom"/>
    </g>
  );
}
function BarVDesign(x, y, h, gk, fc = null) {
  const k = gk('bvd', x, y);
  const fO = fc || C_DESIGN.fO;
  const fS = fc ? darken(fc, 15) : C_DESIGN.fS;
  return (
    <g key={k} data-wg-element="divider-bar-vertical">
      <rect x={x} y={y} width={IB} height={h} fill={fO} data-wg-element="divider-bar-fill-vertical"/>
      <line x1={x} y1={y} x2={x} y2={y+h} stroke={fS} strokeWidth="0.15" opacity="0.4" data-wg-element="divider-seam-left"/>
      <line x1={x+IB} y1={y} x2={x+IB} y2={y+h} stroke={fS} strokeWidth="0.15" opacity="0.4" data-wg-element="divider-seam-right"/>
    </g>
  );
}

function BarH(x, y, w, gk) {
  const k = gk('bh', x, y);
  return (
    <g key={k} data-wg-element="divider-bar-horizontal">
      <defs><linearGradient id={k} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.fH}/><stop offset="30%" stopColor={C.fI}/><stop offset="70%" stopColor={C.fO}/><stop offset="100%" stopColor={C.fS}/></linearGradient></defs>
      <rect x={x} y={y} width={w} height={IB} fill={`url(#${k})`} data-wg-element="divider-bar-fill-horizontal"/>
      <line x1={x} y1={y+1} x2={x+w} y2={y+1} stroke={C.fH} strokeWidth="0.3" opacity="0.6" data-wg-element="divider-seam-top"/>
      <line x1={x} y1={y+IB-1} x2={x+w} y2={y+IB-1} stroke={C.fS} strokeWidth="0.3" opacity="0.4" data-wg-element="divider-seam-bottom"/>
    </g>
  );
}
function BarV(x, y, h, gk) {
  const k = gk('bv', x, y);
  return (
    <g key={k} data-wg-element="divider-bar-vertical">
      <defs><linearGradient id={k} x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={C.fH}/><stop offset="30%" stopColor={C.fI}/><stop offset="70%" stopColor={C.fO}/><stop offset="100%" stopColor={C.fS}/></linearGradient></defs>
      <rect x={x} y={y} width={IB} height={h} fill={`url(#${k})`} data-wg-element="divider-bar-fill-vertical"/>
      <line x1={x+1} y1={y} x2={x+1} y2={y+h} stroke={C.fH} strokeWidth="0.3" opacity="0.6" data-wg-element="divider-seam-left"/>
      <line x1={x+IB-1} y1={y} x2={x+IB-1} y2={y+h} stroke={C.fS} strokeWidth="0.3" opacity="0.4" data-wg-element="divider-seam-right"/>
    </g>
  );
}

function Glass(x, y, w, h, gk, glassType = 'clear', glassStyleTile = null) {
  const k = gk('gp', x, y);
  const isSatin = glassType === 'satin';
  const tilePath = !isSatin ? glassStyleTile : null;
  const gx = x + GI, gy = y + GI, gw = Math.max(0, w - GI * 2), gh = Math.max(0, h - GI * 2);
  const tileSize = 90; // fixed repeat size (smaller = more detail), keeps texture uniform across panes
  const patId = `${k}-pat`;
  return (
    <g key={k}>
      <defs>
        {tilePath ? (
          <pattern id={patId} patternUnits="userSpaceOnUse" x="0" y="0" width={tileSize} height={tileSize}>
            <image href={tilePath} x="0" y="0" width={tileSize} height={tileSize} preserveAspectRatio="xMidYMid slice" />
          </pattern>
        ) : (
          <linearGradient id={k} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(210,230,255,0.12)"/>
            <stop offset="50%" stopColor="rgba(190,210,240,0.06)"/>
            <stop offset="100%" stopColor="rgba(210,230,255,0.15)"/>
          </linearGradient>
        )}
      </defs>
      <rect
        x={gx}
        y={gy}
        width={gw}
        height={gh}
        fill={isSatin ? "rgba(220,228,238,0.45)" : (tilePath ? `url(#${patId})` : `url(#${k})`)}
        stroke="rgba(180,200,225,0.2)"
        strokeWidth="0.5"
      />
      {/* subtle sheen over patterned glass */}
      {tilePath ? (
        <rect
          x={gx}
          y={gy}
          width={gw}
          height={gh}
          fill="rgba(210,230,255,0.08)"
          stroke="none"
        />
      ) : null}
    </g>
  );
}

const SASH_W = 20;
function SashFrame(x, y, w, h, gk, fc = null) {
  const k = gk('sash', x, y);
  const mid = fc ? fc : C.fO;
  const stroke = fc ? darken(fc, 55) : C.mi;
  return (
    <g key={k}>
      <path d={`M${x},${y} L${x+w},${y} L${x+w},${y+h} L${x},${y+h} Z M${x+SASH_W},${y+SASH_W} L${x+SASH_W},${y+h-SASH_W} L${x+w-SASH_W},${y+h-SASH_W} L${x+w-SASH_W},${y+SASH_W} Z`} fill={mid} fillRule="evenodd"/>
      <rect x={x} y={y} width={w} height={h} fill="none" stroke={stroke} strokeWidth="0.6" opacity="0.8"/>
    </g>
  );
}

function Handle(hx, hy, dir, style, color, gk) {
  const k = gk('hdl', hx, hy);
  const side = dir === 'right' ? 1 : -1;
  const stroke = darken(color, 40);
  if (style === 'teardrop') {
    return <path key={k} d={`M${hx},${hy-9} C${hx+5},${hy-6} ${hx+5},${hy+6} ${hx},${hy+9} C${hx-5},${hy+6} ${hx-5},${hy-6} ${hx},${hy-9} Z`} fill={color} stroke={stroke} strokeWidth="0.8"/>;
  }
  if (style === 'bulb-end') {
    return (
      <g key={k}>
        <rect x={hx-2.3} y={hy-10} width={4.6} height={14} rx="2" fill={color} stroke={stroke} strokeWidth="0.8"/>
        <circle cx={hx} cy={hy+7} r="4.2" fill={color} stroke={stroke} strokeWidth="0.8"/>
      </g>
    );
  }
  return (
    <g key={k}>
      <rect x={hx-2.3} y={hy-10} width={4.6} height={20} rx="2" fill={color} stroke={stroke} strokeWidth="0.8"/>
      <rect x={hx+side*2.3} y={hy-2} width={side*11} height={4} rx="2" fill={color} stroke={stroke} strokeWidth="0.8"/>
    </g>
  );
}

function Vent(x, y, w, h, fc, gk) {
  const k = gk('vent', x, y);
  const dark = darken(fc || '#c8cdd6', 55);
  const mid = darken(fc || '#c8cdd6', 80);
  return (
    <g key={k}>
      <rect x={x} y={y} width={w} height={h} rx="1.5" fill={dark}/>
      <rect x={x+1.2} y={y+1} width={w-2.4} height={h-2} rx="0.5" fill={mid}/>
    </g>
  );
}

const HINGE_PAD = 6;
const OpeningTri = (x, y, w, h, dir, leafId) => {
  const p = HINGE_PAD;
  const lx = x + p, ly = y + p, lw = w - p*2, lh = h - p*2;
  if (lw < 10 || lh < 10) return null;
  const mx = lx + lw/2, my = ly + lh/2;
  let points;
  switch (dir) {
    case 'top': points = `${lx},${ly+lh} ${lx+lw},${ly+lh} ${mx},${ly}`; break;
    case 'bottom': points = `${lx},${ly} ${lx+lw},${ly} ${mx},${ly+lh}`; break;
    case 'left': points = `${lx+lw},${ly} ${lx+lw},${ly+lh} ${lx},${my}`; break;
    case 'right': points = `${lx},${ly} ${lx},${ly+lh} ${lx+lw},${my}`; break;
    default: return null;
  }
  return <polygon key={`tri-${leafId||Math.round(x)}-${Math.round(y)}`} points={points} fill="none" stroke="#e04040" strokeWidth="4.4" opacity="0.9"/>;
};

const OPEN_DIRS = ['top', 'right', 'bottom', 'left'];
const TILT_TURN_DIRS = ['tilt-turn-left', 'tilt-turn-right'];
const SLIDE_DIRS = ['slide-left', 'slide-right', 'slide-both', 'slide-up', 'slide-down'];
const SASH_ROLE_CODES = ['M', 'S', 'F'];
const isTriangleOpening = (val) => OPEN_DIRS.includes(val);
const isTiltTurnOpening = (val) => TILT_TURN_DIRS.includes(val);
const isSlidingOpening = (val) => SLIDE_DIRS.includes(val);
const isPanelOpening = (val) => val === 'panel';
const inferHandleDirForOpening = (openingDir) => {
  if (openingDir === 'left') return 'left';
  if (openingDir === 'right') return 'right';
  if (openingDir === 'top' || openingDir === 'bottom') return 'right';
  return null;
};

function makePanelLabelElement(leafId, b, mirrorText = false, keySuffix = '') {
  const bw = Math.max(0, Number(b?.w) || 0);
  const bh = Math.max(0, Number(b?.h) || 0);
  if (bw < 14 || bh < 10) return null;

  let label = 'PANEL';
  if (bw < 54) label = 'PNL';
  if (bw < 28) label = 'P';

  const maxTextWidth = Math.max(0, bw - 8);
  const fontSize = Math.max(
    6,
    Math.min(
      16,
      bh * 0.42,
      label.length > 1 ? maxTextWidth / Math.max(1, label.length * 0.72) : bw * 0.55
    )
  );
  const cx = b.x + bw / 2;
  const unMirror = mirrorText ? `translate(${2 * cx},0) scale(-1,1)` : undefined;
  const letterSpacing = label === 'PANEL' ? (bw >= 90 ? '0.1em' : '0.03em') : '0.01em';
  const fitText = label.length > 1 && bw < 96 && maxTextWidth > 0;

  return (
    <text
      key={`pnl-${leafId}${keySuffix}`}
      x={cx}
      y={b.y + bh / 2 + fontSize * 0.35}
      textAnchor="middle"
      fill="#e04040"
      fontSize={fontSize}
      fontFamily="'JetBrains Mono',monospace"
      fontWeight="700"
      opacity="0.72"
      transform={unMirror}
      letterSpacing={letterSpacing}
      {...(fitText ? { textLength: maxTextWidth, lengthAdjust: 'spacingAndGlyphs' } : {})}
    >
      {label}
    </text>
  );
}
const getSlideDirForLeaf = (openings, slideOpenings, leafId) => {
  const v = slideOpenings ? slideOpenings[leafId] : null;
  if (isSlidingOpening(v)) return v;
  const legacy = openings ? openings[leafId] : null;
  return isSlidingOpening(legacy) ? legacy : null;
};

const TiltTurnIndicator = (x, y, w, h, dir, leafId) => {
  const p = HINGE_PAD;
  const lx = x + p, ly = y + p, lw = w - p * 2, lh = h - p * 2;
  if (lw < 10 || lh < 10) return null;
  const mx = lx + lw / 2;
  const my = ly + lh / 2;
  const hingeLeft = dir === 'tilt-turn-left';
  const tiltPoints = `${lx},${ly} ${lx + lw},${ly} ${mx},${ly + lh}`;
  const turnPoints = hingeLeft
    ? `${lx},${ly} ${lx},${ly + lh} ${lx + lw},${my}`
    : `${lx + lw},${ly} ${lx + lw},${ly + lh} ${lx},${my}`;
  return (
    <g key={`tt-${leafId || Math.round(x)}-${Math.round(y)}`}>
      <polygon points={tiltPoints} fill="none" stroke="#e04040" strokeWidth="4.4" opacity="0.9" />
      <polygon points={turnPoints} fill="none" stroke="#2f6fe4" strokeWidth="3.4" opacity="0.92" />
    </g>
  );
};

const normalizeSashRoles = (raw = {}) => {
  const out = {};
  const src = raw && typeof raw === 'object' ? raw : {};
  for (const [lid, val] of Object.entries(src)) {
    const role = String(val || '').trim().toUpperCase();
    if (SASH_ROLE_CODES.includes(role)) out[lid] = role;
  }
  return out;
};

/**
 * Be tolerant of persisted payload variants:
 * - legacy payloads with slide direction in `opens`
 * - payloads where `slideOpens` is nested in `presentation`
 * - mixed maps where a sliding leaf is missing a triangle fallback in `opens`
 */
const normalizeRenderOpenMaps = (rootRaw, opensRaw, slideRaw, presRaw = null) => {
  const collectLeafIds = (node, out) => {
    if (!node || typeof node !== 'object') return;
    if (isRectUnionRoot(node)) {
      const regions = Array.isArray(node.regions) ? node.regions : [];
      for (const rg of regions) collectLeafIds(rg?.root, out);
      return;
    }
    if (node.split === 'none') {
      if (node.id != null) out.push(String(node.id));
      return;
    }
    if (Array.isArray(node.children)) {
      for (const ch of node.children) collectLeafIds(ch, out);
    }
  };
  const leafIds = [];
  collectLeafIds(rootRaw, leafIds);
  const leafIdSet = new Set(leafIds);
  const cleanOpens = {};
  const cleanSlides = {};
  const pOpenFallback =
    (presRaw && typeof presRaw === 'object' && presRaw.opens && typeof presRaw.opens === 'object')
      ? presRaw.opens
      : null;
  const o = (opensRaw && typeof opensRaw === 'object') ? opensRaw : (pOpenFallback || {});
  const p = (presRaw && typeof presRaw === 'object') ? presRaw : {};
  const sCandidate =
    (slideRaw && typeof slideRaw === 'object')
      ? slideRaw
      : (
          ((p.slideOpens && typeof p.slideOpens === 'object') ? p.slideOpens : null) ||
          ((p.slide_openings && typeof p.slide_openings === 'object') ? p.slide_openings : null) ||
          ((p.slide_opens && typeof p.slide_opens === 'object') ? p.slide_opens : null) ||
          {}
        );

  const coerceLeafId = (rawId) => {
    const sid = String(rawId);
    if (leafIdSet.has(sid)) return sid;
    return null;
  };
  const remapIfNeeded = (rawMap, acceptFn) => {
    const src = (rawMap && typeof rawMap === 'object') ? rawMap : {};
    const entries = Object.entries(src).filter(([, v]) => acceptFn(v));
    if (!entries.length) return {};
    const hasDirectMatch = entries.some(([k]) => leafIdSet.has(String(k)));
    if (hasDirectMatch || !leafIds.length) return src;
    const remapped = {};
    for (let i = 0; i < Math.min(entries.length, leafIds.length); i += 1) {
      remapped[leafIds[i]] = entries[i][1];
    }
    return remapped;
  };
  const oMap = remapIfNeeded(o, (v) => v === 'dummy' || isTriangleOpening(v) || isTiltTurnOpening(v) || isPanelOpening(v) || isSlidingOpening(v));
  const sMap = remapIfNeeded(sCandidate, (v) => isSlidingOpening(v));

  for (const [lidRaw, val] of Object.entries(oMap)) {
    const lid = coerceLeafId(lidRaw);
    if (!lid) continue;
    if (val === 'dummy' || isTriangleOpening(val) || isTiltTurnOpening(val) || isPanelOpening(val)) {
      cleanOpens[lid] = val;
    } else if (isSlidingOpening(val)) {
      // Backward-compat: older payloads persisted slide direction directly in opens map.
      cleanSlides[lid] = val;
    }
  }

  for (const [lidRaw, val] of Object.entries(sMap)) {
    const lid = coerceLeafId(lidRaw);
    if (!lid) continue;
    if (!isSlidingOpening(val)) continue;
    cleanSlides[lid] = val;
  }

  return { opens: cleanOpens, slideOpens: cleanSlides };
};

const OpeningArrow = (x, y, w, h, dir, leafId) => {
  const p = HINGE_PAD + 3;
  const lx = x + p, ly = y + p, lw = w - p * 2, lh = h - p * 2;
  if (lw < 14 || lh < 14) return null;
  const cy = ly + lh / 2;
  const cx = lx + lw / 2;
  const isVertical = dir === 'slide-up' || dir === 'slide-down';
  const left = dir === 'slide-left';
  const both = dir === 'slide-both';
  const up = dir === 'slide-up';
  if (isVertical) {
    const bodyW = Math.max(8, Math.min(18, lw * 0.34));
    const half = bodyW / 2;
    const spanH = Math.max(14, lh * (2 / 3));
    const yMin = ly + (lh - spanH) / 2;
    const yMax = yMin + spanH;
    const headLen = Math.max(12, Math.min(22, spanH * 0.3));
    const yBodyT = up ? (yMin + headLen) : yMin;
    const yBodyB = up ? yMax : (yMax - headLen);
    const pts = up
      ? `${cx-half},${yBodyB} ${cx-half},${yBodyT} ${cx-bodyW},${yBodyT} ${cx},${yMin} ${cx+bodyW},${yBodyT} ${cx+half},${yBodyT} ${cx+half},${yBodyB}`
      : `${cx-half},${yBodyT} ${cx-half},${yBodyB} ${cx-bodyW},${yBodyB} ${cx},${yMax} ${cx+bodyW},${yBodyB} ${cx+half},${yBodyB} ${cx+half},${yBodyT}`;
    return (
      <polygon
        key={`arr-${leafId || Math.round(x)}-${Math.round(y)}`}
        points={pts}
        fill="#e04040"
        fillOpacity="0.22"
        stroke="#e04040"
        strokeWidth="2.3"
        strokeLinejoin="miter"
        opacity="0.95"
      />
    );
  }
  const bodyH = Math.max(8, Math.min(18, lh * 0.34));
  const half = bodyH / 2;
  const spanW = Math.max(14, lw * (2 / 3));
  const xMin = lx + (lw - spanW) / 2;
  const xMax = xMin + spanW;
  const headLen = Math.max(12, Math.min(22, spanW * 0.3));
  const xBodyL = both ? (xMin + headLen) : (left ? (xMin + headLen) : xMin);
  const xBodyR = both ? (xMax - headLen) : (left ? xMax : (xMax - headLen));
  const pts = both
    ? `${xBodyR},${cy-half} ${xBodyL},${cy-half} ${xBodyL},${cy-bodyH} ${xMin},${cy} ${xBodyL},${cy+bodyH} ${xBodyL},${cy+half} ${xBodyR},${cy+half} ${xBodyR},${cy+bodyH} ${xMax},${cy} ${xBodyR},${cy-bodyH}`
    : left
    ? `${xBodyR},${cy-half} ${xBodyL},${cy-half} ${xBodyL},${cy-bodyH} ${xMin},${cy} ${xBodyL},${cy+bodyH} ${xBodyL},${cy+half} ${xBodyR},${cy+half}`
    : `${xBodyL},${cy-half} ${xBodyR},${cy-half} ${xBodyR},${cy-bodyH} ${xMax},${cy} ${xBodyR},${cy+bodyH} ${xBodyR},${cy+half} ${xBodyL},${cy+half}`;
  return (
    <polygon
      key={`arr-${leafId || Math.round(x)}-${Math.round(y)}`}
      points={pts}
      fill="#e04040"
      fillOpacity="0.22"
      stroke="#e04040"
      strokeWidth="2.3"
      strokeLinejoin="miter"
      opacity="0.95"
    />
  );
};

function SashRoleLabel(leafId, b, role, mirrorText = false, keySuffix = '') {
  const letter = String(role || '').trim().toUpperCase();
  if (!SASH_ROLE_CODES.includes(letter)) return null;
  const bw = Math.max(0, Number(b?.w) || 0);
  const bh = Math.max(0, Number(b?.h) || 0);
  if (bw < 12 || bh < 10) return null;
  const fontSize = Math.max(24, Math.min(54, bw * 0.48, bh * 0.54));
  const pad = Math.max(16, Math.min(54, Math.min(bw, bh) * 0.34, fontSize * 0.95));
  const labelX = b.x + bw - pad;
  const labelY = b.y + bh - pad;
  const unMirror = mirrorText ? `translate(${2 * labelX},0) scale(-1,1)` : undefined;
  return (
    <text
      key={`sash-role-${leafId}${keySuffix}`}
      x={labelX}
      y={labelY}
      textAnchor="end"
      dominantBaseline="text-after-edge"
      fill="#e04040"
      fontSize={fontSize}
      fontFamily="'JetBrains Mono',monospace"
      fontWeight="700"
      opacity="0.78"
      transform={unMirror}
    >
      {letter}
    </text>
  );
}

const normalizeLogicalDivs = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  const sanitize = (arr) => Array.from(new Set((Array.isArray(arr) ? arr : [])
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n >= LOGICAL_DIV_MIN && n <= LOGICAL_DIV_MAX)))
    .sort((a, b) => a - b);
  for (const [leafId, val] of Object.entries(raw)) {
    if (!val || typeof val !== 'object') continue;
    const h = sanitize(val.h);
    const v = sanitize(val.v);
    if (h.length || v.length) out[leafId] = { h, v };
  }
  return out;
};

const renderPanePanelInfill = (leafId, b, els, panelColor = null, insetPx = 0, addPresentationBorders = false) => {
  const insetRaw = Number(insetPx);
  const inset = Number.isFinite(insetRaw) ? Math.max(0, Math.min(16, insetRaw)) : 0;
  const pX = b.x + inset;
  const pY = b.y + inset;
  const pW = Math.max(0, b.w - inset * 2);
  const pH = Math.max(0, b.h - inset * 2);
  if (pW < 4 || pH < 4) return;
  const baseX = addPresentationBorders ? Math.round(pX) : pX;
  const baseY = addPresentationBorders ? Math.round(pY) : pY;
  const baseW = addPresentationBorders ? Math.max(0, Math.round(pW)) : pW;
  const baseH = addPresentationBorders ? Math.max(0, Math.round(pH)) : pH;
  const base = panelColor || C_DESIGN.fO;
  const k = `pnl-${leafId}-${Math.round(baseX)}-${Math.round(baseY)}-${Math.round(baseW)}-${Math.round(baseH)}`;
  // Keep the subtle grey keyline behavior in design/fallback contexts.
  const defaultInner = 'rgba(176,184,196,0.95)';
  const designEdge = panelColor ? darken(panelColor, 20) : defaultInner;
  const fallbackEdge = addPresentationBorders ? defaultInner : designEdge;
  const fallbackEdgeWidth = addPresentationBorders ? 0.8 : 0.7;
  const presentationOuterStroke = panelColor ? darken(panelColor, 28) : 'rgba(150,160,176,1)';
  const presentationOuterStrokeWidth = 1.05;
  els.push(
    <rect
      key={`${k}-base`}
      x={baseX}
      y={baseY}
      width={baseW}
      height={baseH}
      fill={base}
      stroke={addPresentationBorders ? presentationOuterStroke : fallbackEdge}
      strokeWidth={addPresentationBorders ? presentationOuterStrokeWidth : fallbackEdgeWidth}
      shapeRendering={addPresentationBorders ? "crispEdges" : undefined}
      data-wg-element="panel-base"
      data-wg-leaf={String(leafId)}
      data-wg-mode={addPresentationBorders ? "presentation" : "design"}
    />
  );
  if (addPresentationBorders) {
    // Keep panel utility borders non-directional (flat), so one side does not read heavier by view.
    const innerStroke = panelColor ? darken(panelColor, 28) : 'rgba(150,160,176,0.95)';
    const innerInset = Math.max(2, Math.min(4, Math.round(Math.min(baseW, baseH) * 0.02)));
    if (baseW > innerInset * 2 + 2 && baseH > innerInset * 2 + 2) {
      els.push(
        <rect
          key={`${k}-inner`}
          x={baseX + innerInset}
          y={baseY + innerInset}
          width={Math.max(0, baseW - innerInset * 2)}
          height={Math.max(0, baseH - innerInset * 2)}
          fill="none"
          stroke={innerStroke}
          strokeWidth="0.85"
          data-wg-element="panel-inner-border"
          data-wg-leaf={String(leafId)}
          data-wg-mode="presentation"
        />
      );
    }
  } else {
    const designInnerStroke = panelColor ? darken(panelColor, 30) : 'rgba(150,160,176,0.95)';
    const designInnerInset = Math.max(2, Math.min(4, Math.round(Math.min(baseW, baseH) * 0.02)));
    if (baseW > designInnerInset * 2 + 2 && baseH > designInnerInset * 2 + 2) {
      els.push(
        <rect
          key={`${k}-inner-design`}
          x={baseX + designInnerInset}
          y={baseY + designInnerInset}
          width={Math.max(0, baseW - designInnerInset * 2)}
          height={Math.max(0, baseH - designInnerInset * 2)}
          fill="none"
          stroke={designInnerStroke}
          strokeWidth="0.7"
          data-wg-element="panel-inner-border"
          data-wg-leaf={String(leafId)}
          data-wg-mode="design"
        />
      );
    }
  }
};

const presentationPanelInsertBorders = (leafId, panelRects, opening, frameColor) => {
  if (!opening || opening.w < 8 || opening.h < 8) return [];
  const dark = frameColor ? darken(frameColor, 48) : 'rgba(72,82,98,0.84)';
  const light = frameColor ? lighten(frameColor, 18) : 'rgba(255,255,255,0.78)';
  const pad = 1.25;
  const out = [];
  (panelRects || []).forEach((panel, idx) => {
    const x1 = Math.max(panel.x, opening.x);
    const y1 = Math.max(panel.y, opening.y);
    const x2 = Math.min(panel.x + panel.w, opening.x + opening.w);
    const y2 = Math.min(panel.y + panel.h, opening.y + opening.h);
    const w = x2 - x1;
    const h = y2 - y1;
    if (w < pad * 2 + 4 || h < pad * 2 + 4) return;
    const x = x1 + pad;
    const y = y1 + pad;
    const rw = w - pad * 2;
    const rh = h - pad * 2;
    const k = `pib-${leafId}-${idx}`;
    out.push(
      <rect key={`${k}-edge`} x={x} y={y} width={rw} height={rh} fill="none" stroke={dark} strokeWidth="1" data-wg-element="panel-insert-border" data-wg-leaf={String(leafId)} />,
      <line key={`${k}-ht`} x1={x + 0.5} y1={y + 0.5} x2={x + rw - 0.5} y2={y + 0.5} stroke={light} strokeWidth="1" opacity="0.9" />,
      <line key={`${k}-hl`} x1={x + 0.5} y1={y + 0.5} x2={x + 0.5} y2={y + rh - 0.5} stroke={light} strokeWidth="1.1" opacity="0.9" />,
      <line key={`${k}-sl`} x1={x + 1.5} y1={y + 1} x2={x + 1.5} y2={y + rh - 1} stroke={dark} strokeWidth="0.9" opacity="0.82" />,
      <line key={`${k}-sr`} x1={x + rw - 0.5} y1={y + 0.5} x2={x + rw - 0.5} y2={y + rh - 0.5} stroke={dark} strokeWidth="1" opacity="0.9" />,
      <line key={`${k}-sb`} x1={x + 0.5} y1={y + rh - 0.5} x2={x + rw - 0.5} y2={y + rh - 0.5} stroke={dark} strokeWidth="1.1" opacity="0.95" />,
    );
  });
  return out;
};

const renderLogicalBarsForLeaf = (leafId, b, divCfg, els, panelInfillStyle = 'plain', frameColor = null, panelInsetPx = 0) => {
  if (!divCfg || typeof divCfg !== 'object') return [];
  const hBars = Array.isArray(divCfg.h) ? divCfg.h : [];
  const vBars = Array.isArray(divCfg.v) ? divCfg.v : [];
  const edgeEps = 0.001;
  const hasTopSnap = hBars.some((raw) => Math.abs(Number(raw) - LOGICAL_DIV_MIN) <= LOGICAL_TOP_SNAP_EPS);
  const firstInteriorRatio = (() => {
    let best = null;
    for (const raw of hBars) {
      const n = Number(raw);
      if (!Number.isFinite(n)) continue;
      if (n <= LOGICAL_TOP_SNAP_EPS || n >= 1 - edgeEps) continue;
      if (best == null || n < best) best = n;
    }
    return best;
  })();
  const barH = Math.max(10, mm2px(LOGICAL_TRANSOM_MM));
  const barW = Math.max(8, mm2px(LOGICAL_MULLION_MM));
  const base = frameColor || C_DESIGN.fO;
  const borderDark = frameColor ? darken(frameColor, 38) : 'rgba(95,105,120,0.72)';
  const borderLight = frameColor ? lighten(frameColor, 28) : 'rgba(255,255,255,0.9)';
  const panelBorderDark = frameColor ? darken(frameColor, 48) : 'rgba(72,82,98,0.84)';
  const panelBorderLight = frameColor ? lighten(frameColor, 18) : 'rgba(255,255,255,0.78)';
  const barFill = frameColor || C_DESIGN.fO;
  const panelInset = Math.max(0, Number(panelInsetPx) || 0);
  const drawnPanels = [];
  for (const ratio of hBars) {
    const atTopEdge = Math.abs(Number(ratio) - LOGICAL_DIV_MIN) <= LOGICAL_TOP_SNAP_EPS;
    const atBottomEdge = ratio >= 1 - edgeEps;
    if (atBottomEdge) continue;
    // H* ratio is anchored to the TOP edge of the decorative transom.
    const yTop = b.y + b.h * ratio;
    const y0 = Math.max(b.y, yTop);
    const y1 = Math.min(b.y + b.h, yTop + barH);
    const h = Math.max(0, y1 - y0);
    if (!atTopEdge && h < 1) continue;
    // If top-snap exists, keep full panel from top while still allowing extra transom bars as detail.
    const panelStartY = atTopEdge ? (b.y + panelInset) : y1;
    if (panelInfillStyle !== 'none' && panelStartY < b.y + b.h - 1) {
      const pX = b.x + panelInset;
      const pY = panelStartY;
      const pW = Math.max(0, b.w - panelInset * 2);
      const pBottom = (hasTopSnap && atTopEdge && firstInteriorRatio != null)
        ? (b.y + b.h * firstInteriorRatio)
        : (b.y + b.h - panelInset);
      const pH = Math.max(0, pBottom - pY);
      if (pW < 1 || pH < 1) continue;
      drawnPanels.push({ x: pX, y: pY, w: pW, h: pH });
      els.push(<rect key={`lgp-${leafId}-${Math.round(ratio * 1000)}`} x={pX} y={pY} width={pW} height={pH} fill={base} stroke={panelBorderDark} strokeWidth={panelInfillStyle === 'plain' ? '1.0' : '0.3'} />);
      if (panelInfillStyle === 'plain') {
        els.push(<line key={`lgp-ht-${leafId}-${Math.round(ratio * 1000)}`} x1={pX+0.5} y1={pY+0.5} x2={pX+pW-0.5} y2={pY+0.5} stroke={panelBorderLight} strokeWidth="1.0" opacity="0.9" />);
        els.push(<line key={`lgp-hl-${leafId}-${Math.round(ratio * 1000)}`} x1={pX+0.5} y1={pY+0.5} x2={pX+0.5} y2={pY+pH-0.5} stroke={panelBorderLight} strokeWidth="1.1" opacity="0.9" />);
        els.push(<line key={`lgp-sl-${leafId}-${Math.round(ratio * 1000)}`} x1={pX+1.5} y1={pY+1} x2={pX+1.5} y2={pY+pH-1} stroke={panelBorderDark} strokeWidth="0.9" opacity="0.82" />);
        els.push(<line key={`lgp-sr-${leafId}-${Math.round(ratio * 1000)}`} x1={pX+pW-0.5} y1={pY+0.5} x2={pX+pW-0.5} y2={pY+pH-0.5} stroke={panelBorderDark} strokeWidth="1.0" opacity="0.9" />);
        els.push(<line key={`lgp-sb-${leafId}-${Math.round(ratio * 1000)}`} x1={pX+0.5} y1={pY+pH-0.5} x2={pX+pW-0.5} y2={pY+pH-0.5} stroke={panelBorderDark} strokeWidth="1.1" opacity="0.95" />);
      }
      if (panelInfillStyle !== 'plain' && pW > 30 && pH > 30) {
        const paneInset = Math.max(10, Math.min(pW, pH) * 0.1);
        const drawRaised = (kx, x, y, w, h) => {
          // Double cornice border: two nested profile rings.
          els.push(<rect key={`${kx}-o1`} x={x} y={y} width={w} height={h} fill="none" stroke={panelBorderDark} strokeWidth="1" />);
          els.push(<rect key={`${kx}-o2`} x={x+2} y={y+2} width={Math.max(0, w-4)} height={Math.max(0, h-4)} fill="none" stroke={panelBorderLight} strokeWidth="0.95" />);
          els.push(<rect key={`${kx}-i1`} x={x+6} y={y+6} width={Math.max(0, w-12)} height={Math.max(0, h-12)} fill="none" stroke={panelBorderDark} strokeWidth="0.95" />);
          els.push(<rect key={`${kx}-i2`} x={x+8} y={y+8} width={Math.max(0, w-16)} height={Math.max(0, h-16)} fill="none" stroke={panelBorderLight} strokeWidth="0.9" />);
        };
        if (panelInfillStyle === 'single') {
          drawRaised(`lgps-${leafId}-${Math.round(ratio * 1000)}`, pX + paneInset, pY + paneInset, Math.max(0, pW - paneInset * 2), Math.max(0, pH - paneInset * 2));
        } else if (panelInfillStyle === 'double') {
          const gap = Math.max(10, pW * 0.08);
          const eachW = Math.max(0, (pW - paneInset * 2 - gap) / 2);
          const h2 = Math.max(0, pH - paneInset * 2);
          drawRaised(`lgpd1-${leafId}-${Math.round(ratio * 1000)}`, pX + paneInset, pY + paneInset, eachW, h2);
          drawRaised(`lgpd2-${leafId}-${Math.round(ratio * 1000)}`, pX + paneInset + eachW + gap, pY + paneInset, eachW, h2);
        } else if (panelInfillStyle === 'shiplap') {
          const sx = pX + paneInset * 0.5;
          const sy = pY + paneInset * 0.35;
          const sw = Math.max(0, pW - paneInset);
          const sh = Math.max(0, pH - paneInset * 0.7);
          // Outer border frame around shiplap area.
          els.push(<rect key={`lg-ship-bd-${leafId}-${Math.round(ratio * 1000)}`} x={sx} y={sy} width={sw} height={sh} fill="none" stroke={panelBorderDark} strokeWidth="0.95" />);
          const plankW = Math.max(22, Math.min(56, sw / 6));
          const cols = Math.max(3, Math.floor(sw / plankW));
          for (let i = 1; i < cols; i++) {
            const xx = sx + (sw * i) / cols;
            els.push(<line key={`lg-ship-d-${leafId}-${Math.round(ratio * 1000)}-${i}`} x1={xx} y1={sy} x2={xx} y2={sy+sh} stroke={panelBorderDark} strokeWidth="1.05" opacity="0.78" />);
            els.push(<line key={`lg-ship-l-${leafId}-${Math.round(ratio * 1000)}-${i}`} x1={xx+1} y1={sy} x2={xx+1} y2={sy+sh} stroke={panelBorderLight} strokeWidth="0.8" opacity="0.66" />);
          }
        }
      }
    }
    if (!atTopEdge) {
      els.push(<g key={`lgh-${leafId}-${Math.round(ratio * 1000)}`}><rect x={b.x} y={y0} width={b.w} height={h} fill={barFill} /><line x1={b.x} y1={y0 + 0.5} x2={b.x + b.w} y2={y0 + 0.5} stroke={borderLight} strokeWidth="0.7" opacity="0.85" /><line x1={b.x} y1={y1 - 0.5} x2={b.x + b.w} y2={y1 - 0.5} stroke={borderDark} strokeWidth="0.9" opacity="0.85" /><line x1={b.x + 0.5} y1={y0} x2={b.x + 0.5} y2={y1} stroke={borderDark} strokeWidth="0.5" opacity="0.65" /><line x1={b.x + b.w - 0.5} y1={y0} x2={b.x + b.w - 0.5} y2={y1} stroke={borderDark} strokeWidth="0.5" opacity="0.65" /></g>);
    }
  }
  for (const ratio of vBars) {
    if (ratio <= edgeEps || ratio >= 1 - edgeEps) continue;
    const xMid = b.x + b.w * ratio;
    const x0 = Math.max(b.x, xMid - barW / 2);
    const x1 = Math.min(b.x + b.w, xMid + barW / 2);
    const w = Math.max(0, x1 - x0);
    if (w < 1) continue;
    els.push(<g key={`lgv-${leafId}-${Math.round(ratio * 1000)}`}><rect x={x0} y={b.y} width={w} height={b.h} fill={barFill} /><line x1={x0 + 0.5} y1={b.y} x2={x0 + 0.5} y2={b.y + b.h} stroke={borderLight} strokeWidth="0.7" opacity="0.85" /><line x1={x1 - 0.5} y1={b.y} x2={x1 - 0.5} y2={b.y + b.h} stroke={borderDark} strokeWidth="0.9" opacity="0.85" /><line x1={x0} y1={b.y + 0.5} x2={x1} y2={b.y + 0.5} stroke={borderDark} strokeWidth="0.5" opacity="0.65" /><line x1={x0} y1={b.y + b.h - 0.5} x2={x1} y2={b.y + b.h - 0.5} stroke={borderDark} strokeWidth="0.5" opacity="0.65" /></g>);
  }
  return drawnPanels;
};

const logicalTransomBarRect = (b, divCfg) => {
  if (!divCfg || typeof divCfg !== 'object') return null;
  const hBars = Array.isArray(divCfg.h) ? divCfg.h.slice() : [];
  if (!hBars.length) return null;
  const edgeEps = 0.001;
  const barH = Math.max(10, mm2px(LOGICAL_TRANSOM_MM));
  const sorted = hBars.filter((n) => Number.isFinite(Number(n))).map(Number).sort((a, z) => a - z);
  for (const ratio of sorted) {
    const atTopEdge = Math.abs(Number(ratio) - LOGICAL_DIV_MIN) <= LOGICAL_TOP_SNAP_EPS;
    const atBottomEdge = ratio >= 1 - edgeEps;
    if (atBottomEdge || atTopEdge) continue;
    const yTop = b.y + b.h * ratio;
    const y0 = Math.max(b.y, yTop);
    const y1 = Math.min(b.y + b.h, yTop + barH);
    const h = Math.max(0, y1 - y0);
    if (h < 1) continue;
    return { x: b.x, y: y0, w: b.w, h };
  }
  return null;
};

const getLogicalGlassRectForLeaf = (divCfg, px1, py1, pw, ph) => {
  const fullRect = { x: px1, y: py1, w: pw, h: ph };
  if (!divCfg || typeof divCfg !== 'object') return fullRect;
  const rawHBars = Array.isArray(divCfg.h) ? divCfg.h : [];
  const hBars = rawHBars
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  if (!hBars.length) return fullRect;

  const edgeEps = 0.001;
  const topSnapEps = LOGICAL_TOP_SNAP_EPS;
  const barH = Math.max(10, mm2px(LOGICAL_TRANSOM_MM));
  const hasTopSnap = hBars.some((n) => Math.abs(n - LOGICAL_DIV_MIN) <= topSnapEps);
  const firstInteriorRatio = hBars.find((n) => n > topSnapEps && n < 1 - edgeEps) ?? null;
  const panelSegments = [];

  for (const ratio of hBars) {
    const atTopEdge = Math.abs(ratio - LOGICAL_DIV_MIN) <= topSnapEps;
    const atBottomEdge = ratio >= 1 - edgeEps;
    if (atBottomEdge) continue;

    const yTop = py1 + ph * ratio;
    const y0 = Math.max(py1, yTop);
    const y1 = Math.min(py1 + ph, yTop + barH);
    if (!atTopEdge && y1 - y0 < 1) continue;

    const panelStartY = atTopEdge ? py1 : y1;
    const panelEndY = (hasTopSnap && atTopEdge && firstInteriorRatio != null)
      ? (py1 + ph * firstInteriorRatio)
      : (py1 + ph);
    if (panelEndY - panelStartY > 1) panelSegments.push({ y0: panelStartY, y1: panelEndY });
  }

  if (!panelSegments.length) return fullRect;
  const topMostPanel = panelSegments.reduce((acc, cur) => (cur.y0 < acc.y0 ? cur : acc), panelSegments[0]);
  const panelStartsNearTop = topMostPanel.y0 <= py1 + ph * 0.2;
  const glassY0 = panelStartsNearTop ? topMostPanel.y1 : py1;
  const glassY1 = panelStartsNearTop ? (py1 + ph) : topMostPanel.y0;
  const glassH = Math.max(0, glassY1 - glassY0);
  if (glassH < 5) return fullRect;
  return { x: px1, y: glassY0, w: pw, h: glassH };
};

function childBounds(c, b) {
  if (!c || !Array.isArray(c.children) || c.children.length === 0) return [];
  const n = c.children.length, res = [];
  const fr = Array.isArray(c.fractions) && c.fractions.length === n
    ? c.fractions
    : Array.from({ length: n }, () => 1 / n);
  if (c.split === 'vertical') {
    const aW = b.w - (n-1)*IB; let cx = b.x;
    for (let i = 0; i < n; i++) { const cw = fr[i]*aW; const sx = cx+(i>0?IB/2:0); res.push({x:sx,y:b.y,w:cw,h:b.h}); cx = sx+cw+(i<n-1?IB/2:0); }
  } else {
    const aH = b.h - (n-1)*IB; let cy = b.y;
    for (let i = 0; i < n; i++) { const ch = fr[i]*aH; const sy = cy+(i>0?IB/2:0); res.push({x:b.x,y:sy,w:b.w,h:ch}); cy = sy+ch+(i<n-1?IB/2:0); }
  }
  return res;
}

function isRectUnionRoot(r) {
  return !!r && r.type === 'rectUnion' && Array.isArray(r.regions) && r.regions.length > 0;
}

function isCellTree(r) {
  return !!r && typeof r === 'object' && typeof r.split === 'string' && Array.isArray(r.children);
}

function makeLeafNode(id) {
  return { id, split: 'none', children: [], fractions: [] };
}

function coerceTDoorSideRoot(root, seed) {
  if (
    isCellTree(root) &&
    root.split === 'horizontal' &&
    Array.isArray(root.children) &&
    root.children.length === 2 &&
    root.children.every((ch) => isCellTree(ch) && ch.split === 'none')
  ) {
    return root;
  }
  const topId = `${seed}-top`;
  const botId = `${seed}-bot`;
  return {
    id: (isCellTree(root) && root.id != null) ? String(root.id) : seed,
    split: 'horizontal',
    fractions: [0.5, 0.5],
    children: [makeLeafNode(topId), makeLeafNode(botId)],
  };
}

function normalizeRootForRender(root) {
  if (!isRectUnionRoot(root)) return root;
  const preset = String(root.preset || '');
  if (preset === 'l') {
    const cutX = clamp(Number(root.shapeParams?.cutX), 0.2, 0.8);
    const cutY = clamp(Number(root.shapeParams?.cutY), 0.2, 0.8);
    const r = Array.isArray(root.regions) ? root.regions : [];
    const r0 = r[0] || {};
    const r1 = r[1] || {};
    const inferVoidSide = () => {
      const explicit = String(root.shapeParams?.voidSide || '').toLowerCase();
      if (explicit === 'left' || explicit === 'right') return explicit;
      const x1 = Number(r1?.x);
      if (Number.isFinite(x1) && Math.abs(x1) < 0.02) return 'left';
      return 'right';
    };
    const voidSide = inferVoidSide();
    if (voidSide === 'left') {
      return {
        ...root,
        shapeParams: { ...(root.shapeParams || {}), cutX, cutY, voidSide: 'left' },
        regions: [
          {
            id: r0.id || 'r0',
            x: cutX,
            y: 0,
            w: 1 - cutX,
            h: 1,
            root: isCellTree(r0.root) ? r0.root : makeLeafNode('r0-main'),
          },
          {
            id: r1.id || 'r1',
            x: 0,
            y: 0,
            w: cutX,
            h: cutY,
            root: isCellTree(r1.root) ? r1.root : makeLeafNode('r1-top'),
          },
        ],
      };
    }
    return {
      ...root,
      shapeParams: { ...(root.shapeParams || {}), cutX, cutY, voidSide: 'right' },
      regions: [
        {
          id: r0.id || 'r0',
          x: 0,
          y: 0,
          w: cutX,
          h: 1,
          root: isCellTree(r0.root) ? r0.root : makeLeafNode('r0-main'),
        },
        {
          id: r1.id || 'r1',
          x: cutX,
          y: 0,
          w: 1 - cutX,
          h: cutY,
          root: isCellTree(r1.root) ? r1.root : makeLeafNode('r1-top'),
        },
      ],
    };
  }
  if (preset !== 't-door') return root;
  const topH = clamp(Number(root.shapeParams?.topH), 0.2, 0.8);
  const stemW = clamp(Number(root.shapeParams?.stemW), 0.2, 0.8);
  const stemX = (1 - stemW) / 2;
  const r = Array.isArray(root.regions) ? root.regions : [];
  const leftRegion = r[0] || {};
  const centerRegion = r[1] || {};
  const rightRegion = r[2] || {};
  return {
    ...root,
    shapeParams: { ...(root.shapeParams || {}), topH, stemW },
    regions: [
      {
        id: leftRegion.id || 'r0',
        x: 0,
        y: 0,
        w: stemX,
        h: topH,
        root: coerceTDoorSideRoot(leftRegion.root, 'r0'),
      },
      {
        id: centerRegion.id || 'r1',
        x: stemX,
        y: 0,
        w: stemW,
        h: 1,
        root: isCellTree(centerRegion.root) ? centerRegion.root : makeLeafNode('r1-main'),
      },
      {
        id: rightRegion.id || 'r2',
        x: stemX + stemW,
        y: 0,
        w: stemX,
        h: topH,
        root: coerceTDoorSideRoot(rightRegion.root, 'r2'),
      },
    ],
  };
}

function remapPaneHandleKindsForRender(rawKinds, originalRoot, normalizedRoot, innerBounds) {
  const sourceKinds = (rawKinds && typeof rawKinds === 'object') ? rawKinds : {};
  const sourceEntries = Object.entries(sourceKinds);
  if (!sourceEntries.length || !originalRoot || !normalizedRoot || originalRoot === normalizedRoot) {
    return sourceKinds;
  }

  const originalLeaves = [];
  const normalizedLeaves = [];
  try {
    colLeaves(originalRoot, innerBounds, originalLeaves);
    colLeaves(normalizedRoot, innerBounds, normalizedLeaves);
  } catch {
    return sourceKinds;
  }
  if (!originalLeaves.length || !normalizedLeaves.length) return sourceKinds;

  const hasDirectMatches = normalizedLeaves.some((lf) => Object.prototype.hasOwnProperty.call(sourceKinds, String(lf.id)));
  if (hasDirectMatches) return sourceKinds;

  const sortByPosition = (a, b) => {
    const ay = Number(a?.b?.y ?? 0);
    const by = Number(b?.b?.y ?? 0);
    if (Math.abs(ay - by) > 0.75) return ay - by;
    const ax = Number(a?.b?.x ?? 0);
    const bx = Number(b?.b?.x ?? 0);
    if (Math.abs(ax - bx) > 0.75) return ax - bx;
    const ah = Number(a?.b?.h ?? 0);
    const bh = Number(b?.b?.h ?? 0);
    if (Math.abs(ah - bh) > 0.75) return ah - bh;
    const aw = Number(a?.b?.w ?? 0);
    const bw = Number(b?.b?.w ?? 0);
    return aw - bw;
  };

  const src = [...originalLeaves].sort(sortByPosition);
  const dst = [...normalizedLeaves].sort(sortByPosition);
  const next = { ...sourceKinds };
  const pairCount = Math.min(src.length, dst.length);
  for (let i = 0; i < pairCount; i++) {
    const fromId = String(src[i]?.id ?? '');
    const toId = String(dst[i]?.id ?? '');
    if (!fromId || !toId) continue;
    const kind = sourceKinds[fromId];
    if (kind === 'door' || kind === 'window') {
      next[toId] = kind;
    }
  }
  return next;
}

function remapHandleDirsForRender(rawDirs, originalRoot, normalizedRoot, innerBounds) {
  const sourceDirs = (rawDirs && typeof rawDirs === 'object') ? rawDirs : {};
  const sourceEntries = Object.entries(sourceDirs);
  if (!sourceEntries.length || !originalRoot || !normalizedRoot || originalRoot === normalizedRoot) {
    return sourceDirs;
  }

  const originalLeaves = [];
  const normalizedLeaves = [];
  try {
    colLeaves(originalRoot, innerBounds, originalLeaves);
    colLeaves(normalizedRoot, innerBounds, normalizedLeaves);
  } catch {
    return sourceDirs;
  }
  if (!originalLeaves.length || !normalizedLeaves.length) return sourceDirs;

  const hasDirectMatches = normalizedLeaves.some((lf) => Object.prototype.hasOwnProperty.call(sourceDirs, String(lf.id)));
  if (hasDirectMatches) return sourceDirs;

  const sortByPosition = (a, b) => {
    const ay = Number(a?.b?.y ?? 0);
    const by = Number(b?.b?.y ?? 0);
    if (Math.abs(ay - by) > 0.75) return ay - by;
    const ax = Number(a?.b?.x ?? 0);
    const bx = Number(b?.b?.x ?? 0);
    if (Math.abs(ax - bx) > 0.75) return ax - bx;
    const ah = Number(a?.b?.h ?? 0);
    const bh = Number(b?.b?.h ?? 0);
    if (Math.abs(ah - bh) > 0.75) return ah - bh;
    const aw = Number(a?.b?.w ?? 0);
    const bw = Number(b?.b?.w ?? 0);
    return aw - bw;
  };

  const src = [...originalLeaves].sort(sortByPosition);
  const dst = [...normalizedLeaves].sort(sortByPosition);
  const next = { ...sourceDirs };
  const pairCount = Math.min(src.length, dst.length);
  for (let i = 0; i < pairCount; i++) {
    const fromId = String(src[i]?.id ?? '');
    const toId = String(dst[i]?.id ?? '');
    if (!fromId || !toId) continue;
    const dir = String(sourceDirs[fromId] ?? '').trim().toLowerCase();
    if (dir === 'left' || dir === 'right') {
      next[toId] = dir;
    }
  }
  return next;
}

function absRegionBounds(inner, rg) {
  return {
    x: inner.x + inner.w * Number(rg.x || 0),
    y: inner.y + inner.h * Number(rg.y || 0),
    w: inner.w * Number(rg.w || 0),
    h: inner.h * Number(rg.h || 0),
  };
}

function eachRegion(root, inner, fn) {
  if (isRectUnionRoot(root)) {
    root.regions.forEach((rg, i) => fn(rg, absRegionBounds(inner, rg), i));
    return;
  }
  fn({ id: 'r0', root }, inner, 0);
}

function collectSharedEdges(regions, inner) {
  const out = [];
  const rs = regions.map((rg) => ({ ...rg, b: absRegionBounds(inner, rg) }));
  for (let i = 0; i < rs.length; i++) {
    for (let j = i + 1; j < rs.length; j++) {
      const a = rs[i].b;
      const b = rs[j].b;
      if (Math.abs((a.x + a.w) - b.x) < 0.5 || Math.abs((b.x + b.w) - a.x) < 0.5) {
        const x = Math.abs((a.x + a.w) - b.x) < 0.5 ? (a.x + a.w) : (b.x + b.w);
        const y0 = Math.max(a.y, b.y);
        const y1 = Math.min(a.y + a.h, b.y + b.h);
        if (y1 - y0 > 1) out.push({ o: 'v', x, y0, y1 });
      }
      if (Math.abs((a.y + a.h) - b.y) < 0.5 || Math.abs((b.y + b.h) - a.y) < 0.5) {
        const y = Math.abs((a.y + a.h) - b.y) < 0.5 ? (a.y + a.h) : (b.y + b.h);
        const x0 = Math.max(a.x, b.x);
        const x1 = Math.min(a.x + a.w, b.x + b.w);
        if (x1 - x0 > 1) out.push({ o: 'h', y, x0, x1 });
      }
    }
  }
  return out;
}

function FrameOuterUnionDesign(ox, oy, ww, wh, inner, regions, comp = null, frameColor = null, jointStyle = 'mitre-45', idPrefix = 'wg') {
  const maskId = `${idPrefix}-dru-${Math.round(ox)}-${Math.round(oy)}-${Math.round(ww)}-${Math.round(wh)}`;
  const fO = frameColor || C_DESIGN.fO;
  const fS = frameColor ? darken(frameColor, 15) : C_DESIGN.fS;
  const fH = frameColor ? lighten(frameColor, 8) : C_DESIGN.fH;
  const mi = frameColor ? darken(frameColor, 15) : C_DESIGN.mi;
  const exp = regions.map((rg) => {
    const b = absRegionBounds(inner, rg);
    return {
      x: Math.max(ox, b.x - OB),
      y: Math.max(oy, b.y - OB),
      w: Math.min(ox + ww, b.x + b.w + OB) - Math.max(ox, b.x - OB),
      h: Math.min(oy + wh, b.y + b.h + OB) - Math.max(oy, b.y - OB),
    };
  });
  return (
    <g key={`fru-d-${Math.round(ox)}-${Math.round(oy)}`}>
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse" x={ox} y={oy} width={ww} height={wh}>
          <rect x={ox} y={oy} width={ww} height={wh} fill="#000" />
          {exp.map((r, i) => <rect key={`we-d-${i}`} x={r.x} y={r.y} width={r.w} height={r.h} fill="#fff" />)}
          {regions.map((rg, i) => {
            const b = absRegionBounds(inner, rg);
            return <rect key={`wh-d-${i}`} x={b.x} y={b.y} width={b.w} height={b.h} fill="#000" />;
          })}
        </mask>
      </defs>
      <rect x={ox} y={oy} width={ww} height={wh} fill={fO} mask={`url(#${maskId})`} />
      <rect x={ox} y={oy} width={ww} height={wh} fill="none" stroke={fS} strokeWidth="0.5" mask={`url(#${maskId})`} />
      <line x1={ox+0.5} y1={oy+0.5} x2={ox+ww-0.5} y2={oy+0.5} stroke={fH} strokeWidth="1" opacity="0.6" mask={`url(#${maskId})`} />
      <line x1={ox+0.5} y1={oy+0.5} x2={ox+0.5} y2={oy+wh-0.5} stroke={fH} strokeWidth="0.8" opacity="0.5" mask={`url(#${maskId})`} />
      {jointStyle === 'timber-weld' ? (
        renderOuterCornerJoinLines(ox, oy, ww, wh, mi, 'timber-weld', '1', 'design')
      ) : (comp && comp.preset === 'l') ? (
        (() => {
          const cutX = clamp(Number(comp.shapeParams?.cutX), 0.2, 0.8);
          const cutY = clamp(Number(comp.shapeParams?.cutY), 0.2, 0.8);
          const voidSide = String(comp.shapeParams?.voidSide || '').toLowerCase() === 'left' ? 'left' : 'right';
          const cutXInner = inner.x + inner.w * cutX;
          const cutYInner = inner.y + inner.h * cutY;
          const stepX = voidSide === 'left' ? (cutXInner - OB) : (cutXInner + OB);
          const stepY = cutYInner + OB;
          return (
            <>
              <line x1={ox} y1={oy} x2={ox+OB} y2={oy+OB} stroke={mi} strokeWidth="1"/>
              <line x1={ox+ww} y1={oy} x2={ox+ww-OB} y2={oy+OB} stroke={mi} strokeWidth="1"/>
              {voidSide === 'left' ? (
                <>
                  <line x1={ox+ww} y1={oy+wh} x2={ox+ww-OB} y2={oy+wh-OB} stroke={mi} strokeWidth="1"/>
                  <line x1={ox} y1={stepY} x2={ox+OB} y2={stepY-OB} stroke={mi} strokeWidth="1"/>
                  <line x1={stepX} y1={oy+wh} x2={stepX+OB} y2={oy+wh-OB} stroke={mi} strokeWidth="1"/>
                </>
              ) : (
                <>
                  <line x1={ox} y1={oy+wh} x2={ox+OB} y2={oy+wh-OB} stroke={mi} strokeWidth="1"/>
                  <line x1={ox+ww} y1={stepY} x2={ox+ww-OB} y2={stepY-OB} stroke={mi} strokeWidth="1"/>
                  <line x1={stepX} y1={oy+wh} x2={stepX-OB} y2={oy+wh-OB} stroke={mi} strokeWidth="1"/>
                </>
              )}
            </>
          );
        })()
      ) : (comp && (comp.preset === 't' || comp.preset === 't-door')) ? (
        (() => {
          const topH = clamp(Number(comp.shapeParams?.topH), 0.2, 0.8);
          const stemW = clamp(Number(comp.shapeParams?.stemW), 0.2, 0.8);
          const stemX = (1 - stemW) / 2;
          const leftX = inner.x + inner.w * stemX - OB;
          const rightX = inner.x + inner.w * (stemX + stemW) + OB;
          const stepY = inner.y + inner.h * topH + OB;
          return (
            <>
              <line x1={ox} y1={oy} x2={ox+OB} y2={oy+OB} stroke={mi} strokeWidth="1"/>
              <line x1={ox+ww} y1={oy} x2={ox+ww-OB} y2={oy+OB} stroke={mi} strokeWidth="1"/>
              <line x1={ox} y1={stepY} x2={ox+OB} y2={stepY-OB} stroke={mi} strokeWidth="1"/>
              <line x1={ox+ww} y1={stepY} x2={ox+ww-OB} y2={stepY-OB} stroke={mi} strokeWidth="1"/>
              <line x1={leftX} y1={oy+wh} x2={leftX+OB} y2={oy+wh-OB} stroke={mi} strokeWidth="1"/>
              <line x1={rightX} y1={oy+wh} x2={rightX-OB} y2={oy+wh-OB} stroke={mi} strokeWidth="1"/>
            </>
          );
        })()
      ) : (comp && comp.preset === 'u') ? (
        (() => {
          const legW = clamp(Number(comp.shapeParams?.legW), 0.15, 0.42);
          const leftX = inner.x + inner.w * legW + OB;
          const rightX = inner.x + inner.w * (1 - legW) - OB;
          return (
            <>
              <line x1={ox} y1={oy} x2={ox+OB} y2={oy+OB} stroke={mi} strokeWidth="1"/>
              <line x1={ox+ww} y1={oy} x2={ox+ww-OB} y2={oy+OB} stroke={mi} strokeWidth="1"/>
              <line x1={ox} y1={oy+wh} x2={ox+OB} y2={oy+wh-OB} stroke={mi} strokeWidth="1"/>
              <line x1={ox+ww} y1={oy+wh} x2={ox+ww-OB} y2={oy+wh-OB} stroke={mi} strokeWidth="1"/>
              <line x1={leftX} y1={oy} x2={leftX-OB} y2={oy+OB} stroke={mi} strokeWidth="1"/>
              <line x1={rightX} y1={oy} x2={rightX+OB} y2={oy+OB} stroke={mi} strokeWidth="1"/>
            </>
          );
        })()
      ) : (
        renderOuterCornerJoinLines(ox, oy, ww, wh, mi, jointStyle, '1', 'design')
      )}
    </g>
  );
}

function FrameOuterUnionPresentation(ox, oy, ww, wh, inner, regions, comp = null, frameColor = null, jointStyle = 'mitre-45', mirror = false, idPrefix = 'wg') {
  const maskId = `${idPrefix}-pru-${Math.round(ox)}-${Math.round(oy)}-${Math.round(ww)}-${Math.round(wh)}`;
  const fO = frameColor || C.fO;
  const fS = frameColor ? darken(frameColor, 35) : C.fS;
  const fH = frameColor ? lighten(frameColor, 15) : C.fH;
  const mi = frameColor ? darken(frameColor, mirror ? 10 : 25) : C.mi;
  const exp = regions.map((rg) => {
    const b = absRegionBounds(inner, rg);
    return {
      x: Math.max(ox, b.x - OB),
      y: Math.max(oy, b.y - OB),
      w: Math.min(ox + ww, b.x + b.w + OB) - Math.max(ox, b.x - OB),
      h: Math.min(oy + wh, b.y + b.h + OB) - Math.max(oy, b.y - OB),
    };
  });
  return (
    <g key={`fru-p-${Math.round(ox)}-${Math.round(oy)}`}>
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse" x={ox} y={oy} width={ww} height={wh}>
          <rect x={ox} y={oy} width={ww} height={wh} fill="#000" />
          {exp.map((r, i) => <rect key={`we-p-${i}`} x={r.x} y={r.y} width={r.w} height={r.h} fill="#fff" />)}
          {regions.map((rg, i) => {
            const b = absRegionBounds(inner, rg);
            return <rect key={`wh-p-${i}`} x={b.x} y={b.y} width={b.w} height={b.h} fill="#000" />;
          })}
        </mask>
      </defs>
      <rect x={ox} y={oy} width={ww} height={wh} fill={fO} mask={`url(#${maskId})`} />
      <rect x={ox} y={oy} width={ww} height={wh} fill="none" stroke={fS} strokeWidth="0.5" mask={`url(#${maskId})`} />
      <line x1={ox+0.5} y1={oy+0.5} x2={ox+ww-0.5} y2={oy+0.5} stroke={fH} strokeWidth="1" opacity="0.7" mask={`url(#${maskId})`} />
      <line x1={ox+0.5} y1={oy+0.5} x2={ox+0.5} y2={oy+wh-0.5} stroke={fH} strokeWidth="0.8" opacity="0.5" mask={`url(#${maskId})`} />
      {jointStyle === 'timber-weld' ? (
        renderOuterCornerJoinLines(ox, oy, ww, wh, mi, 'timber-weld', '0.6', 'presentation')
      ) : (comp && comp.preset === 'l') ? (
        (() => {
          const cutX = clamp(Number(comp.shapeParams?.cutX), 0.2, 0.8);
          const cutY = clamp(Number(comp.shapeParams?.cutY), 0.2, 0.8);
          const voidSide = String(comp.shapeParams?.voidSide || '').toLowerCase() === 'left' ? 'left' : 'right';
          const cutXInner = inner.x + inner.w * cutX;
          const cutYInner = inner.y + inner.h * cutY;
          const stepX = voidSide === 'left' ? (cutXInner - OB) : (cutXInner + OB);
          const stepY = cutYInner + OB;
          return (
            <>
              <line x1={ox} y1={oy} x2={ox+OB} y2={oy+OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={ox+ww} y1={oy} x2={ox+ww-OB} y2={oy+OB} stroke={mi} strokeWidth="0.6"/>
              {voidSide === 'left' ? (
                <>
                  <line x1={ox+ww} y1={oy+wh} x2={ox+ww-OB} y2={oy+wh-OB} stroke={mi} strokeWidth="0.6"/>
                  <line x1={ox} y1={stepY} x2={ox+OB} y2={stepY-OB} stroke={mi} strokeWidth="0.6"/>
                  <line x1={stepX} y1={oy+wh} x2={stepX+OB} y2={oy+wh-OB} stroke={mi} strokeWidth="0.6"/>
                </>
              ) : (
                <>
                  <line x1={ox} y1={oy+wh} x2={ox+OB} y2={oy+wh-OB} stroke={mi} strokeWidth="0.6"/>
                  <line x1={ox+ww} y1={stepY} x2={ox+ww-OB} y2={stepY-OB} stroke={mi} strokeWidth="0.6"/>
                  <line x1={stepX} y1={oy+wh} x2={stepX-OB} y2={oy+wh-OB} stroke={mi} strokeWidth="0.6"/>
                </>
              )}
            </>
          );
        })()
      ) : (comp && (comp.preset === 't' || comp.preset === 't-door')) ? (
        (() => {
          const topH = clamp(Number(comp.shapeParams?.topH), 0.2, 0.8);
          const stemW = clamp(Number(comp.shapeParams?.stemW), 0.2, 0.8);
          const stemX = (1 - stemW) / 2;
          const leftX = inner.x + inner.w * stemX - OB;
          const rightX = inner.x + inner.w * (stemX + stemW) + OB;
          const stepY = inner.y + inner.h * topH + OB;
          return (
            <>
              <line x1={ox} y1={oy} x2={ox+OB} y2={oy+OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={ox+ww} y1={oy} x2={ox+ww-OB} y2={oy+OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={ox} y1={stepY} x2={ox+OB} y2={stepY-OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={ox+ww} y1={stepY} x2={ox+ww-OB} y2={stepY-OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={leftX} y1={oy+wh} x2={leftX+OB} y2={oy+wh-OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={rightX} y1={oy+wh} x2={rightX-OB} y2={oy+wh-OB} stroke={mi} strokeWidth="0.6"/>
            </>
          );
        })()
      ) : (comp && comp.preset === 'u') ? (
        (() => {
          const legW = clamp(Number(comp.shapeParams?.legW), 0.15, 0.42);
          const leftX = inner.x + inner.w * legW + OB;
          const rightX = inner.x + inner.w * (1 - legW) - OB;
          return (
            <>
              <line x1={ox} y1={oy} x2={ox+OB} y2={oy+OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={ox+ww} y1={oy} x2={ox+ww-OB} y2={oy+OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={ox} y1={oy+wh} x2={ox+OB} y2={oy+wh-OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={ox+ww} y1={oy+wh} x2={ox+ww-OB} y2={oy+wh-OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={leftX} y1={oy} x2={leftX-OB} y2={oy+OB} stroke={mi} strokeWidth="0.6"/>
              <line x1={rightX} y1={oy} x2={rightX+OB} y2={oy+OB} stroke={mi} strokeWidth="0.6"/>
            </>
          );
        })()
      ) : (
        renderOuterCornerJoinLines(ox, oy, ww, wh, mi, jointStyle, '0.6', 'presentation')
      )}
    </g>
  );
}

function colLeavesNode(c, b, out) {
  if (!c) return;
  if (c.split === 'none') { out.push({ id: c.id, b }); return; }
  const cb = childBounds(c, b);
  for (let i = 0; i < c.children.length; i++) colLeavesNode(c.children[i], cb[i], out);
}

function colLeaves(c, b, out) {
  eachRegion(c, b, (rg, rb) => colLeavesNode(rg.root, rb, out));
}

function getOpeningInsetByLeaf(root, b) {
  let clipInsetByLeaf = null;
  if (isRectUnionRoot(root) && root.regions.length > 1) {
    const preset = root.preset || 'rectangle';
    const isL = preset === 'l';
    const isT = preset === 't' || preset === 't-door';
    const isTDoor = preset === 't-door';
    const isU = preset === 'u';
    const lCutX = isL ? (b.x + b.w * clamp(Number(root.shapeParams?.cutX), 0.2, 0.8)) : null;
    const lVoidLeft = isL && String(root.shapeParams?.voidSide || '').toLowerCase() === 'left';
    const tMidX = isTDoor ? (b.x + b.w / 2) : null;
    const uMidX = isU ? (b.x + b.w / 2) : null;
    const leaves = [];
    colLeaves(root, b, leaves);
    clipInsetByLeaf = {};
    for (const l of leaves) clipInsetByLeaf[l.id] = { l: 0, r: 0, t: 0, b: 0 };
    const edges = collectSharedEdges(root.regions, b);
    const eps = 0.75;
    for (const e of edges) {
      if (e.o === 'v') {
        let bx = e.x - IB / 2;
        if (isL && lCutX !== null && Math.abs(e.x - lCutX) < eps) bx = lVoidLeft ? (e.x - IB) : e.x;
        else if (isTDoor && tMidX !== null) bx = e.x < tMidX ? (e.x - IB) : e.x;
        else if (isU && uMidX !== null) bx = e.x < uMidX ? e.x : (e.x - IB);
        for (const l of leaves) {
          const L = l.b;
          if (Math.abs(L.x - e.x) < eps) {
            clipInsetByLeaf[l.id].l = Math.max(clipInsetByLeaf[l.id].l, Math.max(0, bx + IB - L.x));
          }
          if (Math.abs((L.x + L.w) - e.x) < eps) {
            clipInsetByLeaf[l.id].r = Math.max(clipInsetByLeaf[l.id].r, Math.max(0, (L.x + L.w) - bx));
          }
        }
      } else {
        const by = isT ? e.y : (e.y - IB / 2);
        for (const l of leaves) {
          const L = l.b;
          if (Math.abs(L.y - e.y) < eps) {
            clipInsetByLeaf[l.id].t = Math.max(clipInsetByLeaf[l.id].t, Math.max(0, by + IB - L.y));
          }
          if (Math.abs((L.y + L.h) - e.y) < eps) {
            clipInsetByLeaf[l.id].b = Math.max(clipInsetByLeaf[l.id].b, Math.max(0, (L.y + L.h) - by));
          }
        }
      }
    }
  }
  return clipInsetByLeaf;
}

function colDivsNode(c, b, out) {
  if (!c || c.split === 'none') return;
  const cb = childBounds(c, b);
  for (let i = 0; i < c.children.length - 1; i++) {
    if (c.split === 'vertical') {
      out.push({ cid: c.id, di: i, o: 'v', pos: cb[i].x + cb[i].w, s0: b.y, s1: b.y + b.h, kind: 'tree' });
    } else {
      out.push({ cid: c.id, di: i, o: 'h', pos: cb[i].y + cb[i].h, s0: b.x, s1: b.x + b.w, kind: 'tree' });
    }
  }
  for (let i = 0; i < c.children.length; i++) colDivsNode(c.children[i], cb[i], out);
}

function colDivs(c, b, out) {
  eachRegion(c, b, (rg, rb) => colDivsNode(rg.root, rb, out));
}

function subtractInterval(segments, cut0, cut1) {
  const out = [];
  for (const s of segments) {
    if (cut1 <= s.x0 || cut0 >= s.x1) {
      out.push(s);
      continue;
    }
    if (cut0 > s.x0) out.push({ x0: s.x0, x1: cut0 });
    if (cut1 < s.x1) out.push({ x0: cut1, x1: s.x1 });
  }
  return out.filter((s) => s.x1 - s.x0 > 0.75);
}

function getUnionBottomSegments(root, ox, oy, ww, wh) {
  if (!isRectUnionRoot(root) || root.regions.length <= 1) return [{ y: oy + wh, x0: ox, x1: ox + ww }];
  const inner = { x: ox + OB, y: oy + OB, w: ww - OB * 2, h: wh - OB * 2 };
  const exp = root.regions.map((rg) => {
    const b = absRegionBounds(inner, rg);
    return {
      x: Math.max(ox, b.x - OB),
      y: Math.max(oy, b.y - OB),
      w: Math.min(ox + ww, b.x + b.w + OB) - Math.max(ox, b.x - OB),
      h: Math.min(oy + wh, b.y + b.h + OB) - Math.max(oy, b.y - OB),
    };
  });
  const raw = [];
  for (let i = 0; i < exp.length; i++) {
    const a = exp[i];
    const y = a.y + a.h;
    let parts = [{ x0: a.x, x1: a.x + a.w }];
    const probeY = y + 0.5;
    for (let j = 0; j < exp.length; j++) {
      if (j === i) continue;
      const b = exp[j];
      const coversBelow = b.y <= probeY && (b.y + b.h) >= probeY;
      if (!coversBelow) continue;
      parts = subtractInterval(parts, b.x, b.x + b.w);
      if (!parts.length) break;
    }
    for (const p of parts) raw.push({ y, x0: p.x0, x1: p.x1 });
  }
  const byLevel = new Map();
  for (const s of raw) {
    const key = Math.round(s.y);
    const arr = byLevel.get(key) || [];
    arr.push({ ...s });
    byLevel.set(key, arr);
  }
  const out = [];
  for (const [key, arr] of byLevel.entries()) {
    arr.sort((a, b) => a.x0 - b.x0);
    let cur = null;
    for (const s of arr) {
      if (!cur) { cur = { y: key, x0: s.x0, x1: s.x1 }; continue; }
      if (s.x0 <= cur.x1 + 0.75) cur.x1 = Math.max(cur.x1, s.x1);
      else { out.push(cur); cur = { y: key, x0: s.x0, x1: s.x1 }; }
    }
    if (cur) out.push(cur);
  }
  out.sort((a, b) => (a.y - b.y) || (a.x0 - b.x0));
  return out.filter((s) => s.x1 - s.x0 > 1);
}

function renderAssemblyShadow({ px, py, drawW, drawH, offset = 3, fill = 'rgba(0,0,0,0.2)', rx = 1 }) {
  return [
    <rect key="asm-shd" x={px + offset} y={py + offset} width={drawW} height={drawH} rx={rx} fill={fill} />,
  ];
}

function renderBottomExtenderSegments({ px, py, drawW, ePxT, ePxB, ePxL, effH, fillCol, stroke, edgeStroke, root, wx, wy, effW }) {
  if (ePxB <= 0) return [];
  const visualH = extenderBottomVisualPx(ePxB);
  const fc = fillCol || C.fO;
  const winOx = Number.isFinite(wx) ? wx : (px + ePxL);
  const winOy = Number.isFinite(wy) ? wy : (py + ePxT);
  const winW = Number.isFinite(effW) ? effW : Math.max(1, drawW - ePxL);
  const isUnion = root && isRectUnionRoot(root) && root.regions.length > 1;
  const bottomSegs = isUnion
    ? getUnionBottomSegments(root, winOx, winOy, winW, effH)
    : [{ y: py + ePxT + effH, x0: px, x1: px + drawW }];
  const els = [];
  for (let i = 0; i < bottomSegs.length; i++) {
    const s = bottomSegs[i];
    const segW = s.x1 - s.x0;
    if (segW <= 1) continue;
    const botY = s.y;
    const key = isUnion ? `ext-bot-u-${i}-${Math.round(s.x0)}-${Math.round(s.y)}` : 'ext-bot';
    els.push(
      <g key={key} data-wg-element="frame-extender-bottom">
        <rect x={s.x0} y={botY} width={segW} height={visualH} fill={fc} />
        <line x1={s.x0} y1={botY} x2={s.x1} y2={botY} stroke={stroke} strokeWidth="0.5" />
        <rect x={s.x0} y={botY} width={segW} height={visualH} fill="none" stroke={edgeStroke} strokeWidth="0.3" />
      </g>
    );
  }
  return els;
}

/** part: 'all' | 'sides' (top/left/right) | 'bottom' — bottom must render after frame so it is not covered. */
function renderExtenders({ px, py, drawW, drawH, ePxT, ePxB, ePxL, ePxR, effH, cillPx, fillCol = null, root = null, wx = null, wy = null, effW = null, part = 'all' }) {
  const els = [];
  const fc = fillCol || C.fO;
  const stroke = fillCol ? darken(fillCol, 20) : C.fS;
  const edgeStroke = fillCol ? darken(fillCol, 10) : C.fS;
  const includeSides = part === 'all' || part === 'sides';
  const includeBottom = part === 'all' || part === 'bottom';
  if (includeSides && ePxT > 0) {
    els.push(<g key="ext-top"><rect x={px} y={py} width={drawW} height={ePxT} fill={fc}/><line x1={px} y1={py+ePxT} x2={px+drawW} y2={py+ePxT} stroke={stroke} strokeWidth="0.5"/><rect x={px} y={py} width={drawW} height={ePxT} fill="none" stroke={edgeStroke} strokeWidth="0.3"/></g>);
  }
  if (includeBottom) {
    els.push(...renderBottomExtenderSegments({ px, py, drawW, ePxT, ePxB, ePxL, effH, fillCol: fc, stroke, edgeStroke, root, wx, wy, effW }));
  }
  if (includeSides && ePxL > 0) {
    const sideH = drawH - ePxT - cillPx;
    els.push(<g key="ext-left"><rect x={px} y={py+ePxT} width={ePxL} height={sideH} fill={fc}/><line x1={px+ePxL} y1={py+ePxT} x2={px+ePxL} y2={py+ePxT+sideH} stroke={stroke} strokeWidth="0.5"/><rect x={px} y={py+ePxT} width={ePxL} height={sideH} fill="none" stroke={edgeStroke} strokeWidth="0.3"/></g>);
  }
  if (includeSides && ePxR > 0) {
    const sideH = drawH - ePxT - cillPx;
    els.push(<g key="ext-right"><rect x={px+drawW-ePxR} y={py+ePxT} width={ePxR} height={sideH} fill={fc}/><line x1={px+drawW-ePxR} y1={py+ePxT} x2={px+drawW-ePxR} y2={py+ePxT+sideH} stroke={stroke} strokeWidth="0.5"/><rect x={px+drawW-ePxR} y={py+ePxT} width={ePxR} height={sideH} fill="none" stroke={edgeStroke} strokeWidth="0.3"/></g>);
  }
  return els;
}

function normalizeCillUnionMode(rawMode, rawIncludeUpper = null) {
  const mode = String(rawMode || '').trim().toLowerCase();
  if (mode === 'door-only' || mode === 'both' || mode === 'window-only') return mode;
  if (rawIncludeUpper === true) return 'both';
  return 'door-only';
}

function renderCill({ cillOn, cillPx, cillDepth, cillStub, px, py, drawW, drawH, fillCol = null, viewType = 'external', root = null, ox = null, oy = null, ow = null, oh = null, cillUnionIncludeUpper = false, cillUnionMode = 'door-only', segmentedUnionCill = false, extBottomPx = 0 }) {
  if (!cillOn || cillPx <= 0) return [];
  const els = [];
  const fc = fillCol || C.fO;
  const stroke = fillCol ? darken(fillCol, 20) : C.fS;
  const edgeStroke = fillCol ? darken(fillCol, 10) : C.fS;
  const isInternal = viewType === 'internal';
  const overhang = 0; // Flush cill to frame edge (no left/right overhang)
  const isUnion = segmentedUnionCill && isRectUnionRoot(root) && root.regions.length > 1 && Number.isFinite(ox) && Number.isFinite(oy) && Number.isFinite(ow) && Number.isFinite(oh);
  let segs = [{ key: 'rect', x0: px, x1: px + drawW, y: py + drawH - cillPx }];
  let unionMinX = null;
  let unionMaxX = null;
  if (isUnion) {
    const allSegs = getUnionBottomSegments(root, Number(ox), Number(oy), Number(ow), Number(oh));
    if (allSegs.length) {
      unionMinX = allSegs.reduce((m, s) => Math.min(m, s.x0), Number.POSITIVE_INFINITY);
      unionMaxX = allSegs.reduce((m, s) => Math.max(m, s.x1), Number.NEGATIVE_INFINITY);
      const maxY = allSegs.reduce((m, s) => Math.max(m, s.y), Number.NEGATIVE_INFINITY);
      const mode = normalizeCillUnionMode(cillUnionMode, cillUnionIncludeUpper);
      const filtered = mode === 'both'
        ? allSegs
        : mode === 'window-only'
          ? allSegs.filter((s) => Math.abs(s.y - maxY) > 0.75)
          : allSegs.filter((s) => Math.abs(s.y - maxY) <= 0.75);
      segs = filtered.map((s, i) => ({ key: `u-${i}-${Math.round(s.x0)}-${Math.round(s.y)}`, x0: s.x0, x1: s.x1, y: s.y + extBottomPx }));
    }
  }
  const ch = cillPx;
  for (const s of segs) {
    const segW = s.x1 - s.x0;
    const leftOverhang = (isUnion && Number.isFinite(unionMinX) && Math.abs(s.x0 - unionMinX) > 0.75) ? 0 : overhang;
    const rightOverhang = (isUnion && Number.isFinite(unionMaxX) && Math.abs(s.x1 - unionMaxX) > 0.75) ? 0 : overhang;
    const cx = s.x0 - leftOverhang;
    const cw = segW + leftOverhang + rightOverhang;
    const cy = s.y;
    if (cw <= 1) continue;
    if (isInternal) {
      els.push(<rect key={`cill-body-${s.key}`} x={s.x0} y={cy} width={segW} height={ch} fill={fc}/>);
      els.push(<line key={`cill-top-${s.key}`} x1={s.x0} y1={cy} x2={s.x1} y2={cy} stroke={stroke} strokeWidth="0.5"/>);
      els.push(<rect key={`cill-outline-${s.key}`} x={s.x0} y={cy} width={segW} height={ch} fill="none" stroke={edgeStroke} strokeWidth="0.3"/>);
    } else {
      const shadowCol = fillCol ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.08)";
      // External view — intentionally flat 2D cill with symmetric overhang.
      els.push(<rect key={`cill-shd-${s.key}`} x={cx} y={cy+ch} width={cw} height={1} fill={shadowCol}/>);
      els.push(<rect key={`cill-body-${s.key}`} x={cx} y={cy} width={cw} height={ch} fill={fc}/>);
      els.push(<line key={`cill-top-${s.key}`} x1={cx} y1={cy} x2={cx+cw} y2={cy} stroke={stroke} strokeWidth="0.5"/>);
      // Matching end caps on both sides make left/right overhang visually explicit.
      els.push(<line key={`cill-cap-l-${s.key}`} x1={cx} y1={cy} x2={cx} y2={cy+ch} stroke={edgeStroke} strokeWidth="0.35"/>);
      els.push(<line key={`cill-cap-r-${s.key}`} x1={cx+cw} y1={cy} x2={cx+cw} y2={cy+ch} stroke={edgeStroke} strokeWidth="0.35"/>);
      els.push(<rect key={`cill-outline-${s.key}`} x={cx} y={cy} width={cw} height={ch} fill="none" stroke={edgeStroke} strokeWidth="0.3"/>);
    }
  }
  return els;
}

function renderGeorgianBars({ root, iB, fillCol = null, sashInset = 0, gBarOn, gBarCols, gBarRows, gBarWidthMm, gBarOffX, gBarOffY, idPrefix = 'wg', logicalDivs = null }) {
  const colsCount = Math.max(0, Number(gBarCols) || 0);
  const rowsCount = Math.max(0, Number(gBarRows) || 0);
  if (!gBarOn || (colsCount < 2 && rowsCount < 2)) return [];
  const els = [];
  const fc = fillCol || C.fO;
  const stroke = fillCol ? darken(fillCol, 25) : C.fS;
  const bw = mm2px(gBarWidthMm);
  const si = sashInset || 0;
  const pid = String(idPrefix || 'wg');

  const colDivs = Math.max(1, colsCount);
  const rowDivs = Math.max(1, rowsCount);
  const cellW = iB.w / colDivs;
  const cellH = iB.h / rowDivs;
  const offXpx = (Number(gBarOffX || 0) / 100) * cellW;
  const offYpx = (Number(gBarOffY || 0) / 100) * cellH;

  const globalHBars = [];
  for (let i = 1; i < rowsCount; i++) globalHBars.push(iB.y + iB.h * (i / rowDivs) + offYpx);

  const lvs = [];
  colLeaves(root, iB, lvs);
  let maxPW = 0;
  for (const l of lvs) { const pw = l.b.w - si * 2; if (pw > maxPW) maxPW = pw; }
  const cellWRef = (maxPW || 1) / colDivs;

  const logicalMap = normalizeLogicalDivs(logicalDivs || {});
  for (const l of lvs) {
    const b = l.b;
    const px1 = b.x + si, py1 = b.y + si;
    const pw = b.w - si * 2, ph = b.h - si * 2;
    if (pw < 5 || ph < 5) continue;
    const glassRect = getLogicalGlassRectForLeaf(logicalMap[l.id], px1, py1, pw, ph);
    const gx1 = glassRect.x, gy1 = glassRect.y;
    const gw = glassRect.w, gh = glassRect.h;
    if (gw < 5 || gh < 5) continue;
    const gx2 = gx1 + gw, gy2 = gy1 + gh;

    const paneCols = colsCount >= 2 ? Math.max(2, Math.round(gw / cellWRef)) : 1;
    const clipId = `gbc-${pid}-${l.id}`;
    els.push(
      <defs key={`gbd-${pid}-${l.id}`}>
        <clipPath id={clipId}>
          <rect x={gx1} y={gy1} width={gw} height={gh}/>
        </clipPath>
      </defs>
    );

    const paneEls = [];
    for (let i = 1; i < paneCols; i++) {
      const x = gx1 + gw * (i / paneCols) - bw / 2 + offXpx;
      paneEls.push(<rect key={`gb-${l.id}-v${i}`} x={x} y={gy1} width={bw} height={gh} fill={fc} stroke={stroke} strokeWidth="0.3"/>);
      if (fillCol) paneEls.push(<line key={`gb-${l.id}-vh${i}`} x1={x + bw/2} y1={gy1+1} x2={x + bw/2} y2={gy2-1} stroke={lighten(fillCol, 20)} strokeWidth="0.5" opacity="0.35"/>);
    }

    let hCount = 0;
    for (let i = 0; i < globalHBars.length; i++) {
      const cy = globalHBars[i];
      const by = cy - bw / 2;
      if (by + bw <= gy1 + 2 || by >= gy2 - 2) continue;
      hCount++;
      paneEls.push(<rect key={`gb-${l.id}-h${i}`} x={gx1} y={by} width={gw} height={bw} fill={fc} stroke={stroke} strokeWidth="0.3"/>);
      if (fillCol) paneEls.push(<line key={`gb-${l.id}-hh${i}`} x1={gx1+1} y1={cy} x2={gx2-1} y2={cy} stroke={lighten(fillCol, 20)} strokeWidth="0.5" opacity="0.35"/>);
    }

    if (rowsCount >= 2 && hCount === 0 && gh > cellH * 1.5) {
      const fallbackRows = Math.max(2, Math.round(gh / cellH));
      for (let i = 1; i < fallbackRows; i++) {
        const y = gy1 + gh * (i / fallbackRows) - bw / 2;
        paneEls.push(<rect key={`gb-${l.id}-fh${i}`} x={gx1} y={y} width={gw} height={bw} fill={fc} stroke={stroke} strokeWidth="0.3"/>);
        if (fillCol) paneEls.push(<line key={`gb-${l.id}-fhh${i}`} x1={gx1+1} y1={y+bw/2} x2={gx2-1} y2={y+bw/2} stroke={lighten(fillCol, 20)} strokeWidth="0.5" opacity="0.35"/>);
      }
    }

    els.push(<g key={`gbg-${pid}-${l.id}`} clipPath={`url(#${clipId})`}>{paneEls}</g>);
  }
  return els;
}

function renderAstragalBars({ root, iB, fillCol = null, sashInset = 0, aBarOn, aBarCols, aBarRows, aBarWidthMm, aBarOffX, aBarOffY, idPrefix = 'wg', logicalDivs = null }) {
  const colsCount = Math.max(0, Number(aBarCols) || 0);
  const rowsCount = Math.max(0, Number(aBarRows) || 0);
  if (!aBarOn || (colsCount < 2 && rowsCount < 2)) return [];
  const els = [];
  const fc = fillCol || C.fO;
  const stroke = fillCol ? darken(fillCol, 25) : C.fS;
  const bw = mm2px(aBarWidthMm);
  const si = sashInset || 0;
  const pid = String(idPrefix || 'wg');

  const colDivs = Math.max(1, colsCount);
  const rowDivs = Math.max(1, rowsCount);
  const cellW = iB.w / colDivs;
  const cellH = iB.h / rowDivs;
  const offXpx = (Number(aBarOffX || 0) / 100) * cellW;
  const offYpx = (Number(aBarOffY || 0) / 100) * cellH;

  const globalHBars = [];
  for (let i = 1; i < rowsCount; i++) globalHBars.push(iB.y + iB.h * (i / rowDivs) + offYpx);

  const lvs = [];
  colLeaves(root, iB, lvs);
  let maxPW = 0;
  for (const l of lvs) { const pw = l.b.w - si * 2; if (pw > maxPW) maxPW = pw; }
  const cellWRef = (maxPW || 1) / colDivs;

  const logicalMap = normalizeLogicalDivs(logicalDivs || {});
  for (const l of lvs) {
    const b = l.b;
    const px1 = b.x + si, py1 = b.y + si;
    const pw = b.w - si * 2, ph = b.h - si * 2;
    if (pw < 5 || ph < 5) continue;
    const glassRect = getLogicalGlassRectForLeaf(logicalMap[l.id], px1, py1, pw, ph);
    const gx1 = glassRect.x, gy1 = glassRect.y;
    const gw = glassRect.w, gh = glassRect.h;
    if (gw < 5 || gh < 5) continue;
    const gx2 = gx1 + gw, gy2 = gy1 + gh;

    const paneCols = colsCount >= 2 ? Math.max(2, Math.round(gw / cellWRef)) : 1;
    const clipId = `abc-${pid}-${l.id}`;
    els.push(
      <defs key={`abd-${pid}-${l.id}`}>
        <clipPath id={clipId}>
          <rect x={gx1} y={gy1} width={gw} height={gh}/>
        </clipPath>
      </defs>
    );

    const paneEls = [];
    for (let i = 1; i < paneCols; i++) {
      const x = gx1 + gw * (i / paneCols) - bw / 2 + offXpx;
      paneEls.push(<rect key={`ab-${l.id}-v${i}`} x={x} y={gy1} width={bw} height={gh} fill={fc} stroke={stroke} strokeWidth="0.3"/>);
      if (fillCol) paneEls.push(<line key={`ab-${l.id}-vh${i}`} x1={x + bw/2} y1={gy1+1} x2={x + bw/2} y2={gy2-1} stroke={lighten(fillCol, 20)} strokeWidth="0.5" opacity="0.35"/>);
    }

    let hCount = 0;
    for (let i = 0; i < globalHBars.length; i++) {
      const cy = globalHBars[i];
      const by = cy - bw / 2;
      if (by + bw <= gy1 + 2 || by >= gy2 - 2) continue;
      hCount++;
      paneEls.push(<rect key={`ab-${l.id}-h${i}`} x={gx1} y={by} width={gw} height={bw} fill={fc} stroke={stroke} strokeWidth="0.3"/>);
      if (fillCol) paneEls.push(<line key={`ab-${l.id}-hh${i}`} x1={gx1+1} y1={cy} x2={gx2-1} y2={cy} stroke={lighten(fillCol, 20)} strokeWidth="0.5" opacity="0.35"/>);
    }

    if (rowsCount >= 2 && hCount === 0 && gh > cellH * 1.5) {
      const fallbackRows = Math.max(2, Math.round(gh / cellH));
      for (let i = 1; i < fallbackRows; i++) {
        const y = gy1 + gh * (i / fallbackRows) - bw / 2;
        paneEls.push(<rect key={`ab-${l.id}-fh${i}`} x={gx1} y={y} width={gw} height={bw} fill={fc} stroke={stroke} strokeWidth="0.3"/>);
        if (fillCol) paneEls.push(<line key={`ab-${l.id}-fhh${i}`} x1={gx1+1} y1={y+bw/2} x2={gx2-1} y2={y+bw/2} stroke={lighten(fillCol, 20)} strokeWidth="0.5" opacity="0.35"/>);
      }
    }

    els.push(<g key={`abg-${pid}-${l.id}`} clipPath={`url(#${clipId})`}>{paneEls}</g>);
  }
  return els;
}

function renderPresentationWin({ ww, wh, rootCell, ox, oy, openings, slideOpenings = null, view, fc, hc, hs, doorHs = 'lever', doorCylinderLevel = 'half', vp, hSide, gt, glassStyleTile, pVents, hDirs, pHandleKinds, gBar, aBar, logicalDivs: logicalDivsRaw = null, panelInfillStyle = 'plain', letterplateStyle = 'none', jointStyle = 'mitre-45', idPrefix = 'wg', bayGeometry = null, bayPresentationDepthMult = null, bayPresentationStarkness = null, skipBayProjection = false, extTopPx = 0, extBottomPx = 0, extLeftPx = 0, extRightPx = 0, cillOnForBay = false, cillPxForBay = 0, cillStubForBay = false, cillDepthMmForBay = 0, bayFacetProjectionContext = false, skipDropShadow = false }) {
  const mirror = view === 'internal';
  const resolveFacetOpeningDirection = (opening) => {
    if (!(mirror && bayFacetProjectionContext)) return opening;
    if (opening === 'left') return 'right';
    if (opening === 'right') return 'left';
    if (opening === 'tilt-turn-left') return 'tilt-turn-right';
    if (opening === 'tilt-turn-right') return 'tilt-turn-left';
    return opening;
  };
  const els = [];
  const inner = {x:ox+OB,y:oy+OB,w:ww-OB*2,h:wh-OB*2};
  const presRoot = isRectUnionRoot(rootCell) ? (rootCell.regions.length === 1 ? rootCell.regions[0].root : rootCell) : rootCell;
  const logicalDivs = normalizeLogicalDivs(logicalDivsRaw || {});
  const pid = String(idPrefix || 'wg');
  const resolvePaneHandleStyle = (paneId) => {
    const kind = pHandleKinds && pHandleKinds[paneId];
    if (kind === 'door') return doorHs === 'rim-cylinder' ? 'rim-cylinder' : 'upvc';
    // Match editor behavior: when global style is door-only uPVC, explicit window panes
    // must still render with a window handle style.
    if (kind === 'window') return hs === 'upvc' ? 'offset' : hs;
    return hs;
  };
  const renderLetterplate = (key, transomRect) => {
    if (!transomRect || letterplateStyle === 'none') return null;
    const metalBase = (String(hc || '#c0c0c0').startsWith('#') ? hc : '#c0c0c0');
    const edge = darken(metalBase, 30);
    const hi = lighten(metalBase, 24);
    const maxW = Math.max(20, transomRect.w - 6);
    const w = Math.max(20, Math.min(maxW, clamp(transomRect.w * 0.42, 46, 162)));
    const h = Math.max(8, Math.min(Math.max(8, transomRect.h - 2), clamp(transomRect.h * 0.6, 10, 22)));
    const x = transomRect.x + (transomRect.w - w) / 2;
    const y = transomRect.y + (transomRect.h - h) / 2;
    if (letterplateStyle === 'heritage') {
      const flangePad = Math.max(2, Math.min(6, h * 0.38));
      return (
        <g key={key}>
          <rect x={x} y={y} width={w} height={h} rx="1.5" fill={darken(metalBase, 8)} stroke={edge} strokeWidth="0.9" />
          <rect x={x+flangePad} y={y+flangePad} width={Math.max(1, w-flangePad*2)} height={Math.max(1, h-flangePad*2)} rx="1.3" fill={metalBase} stroke={hi} strokeWidth="0.7" />
        </g>
      );
    }
    const rx = letterplateStyle === 'oval' ? Math.max(3, h * 0.45) : 1.4;
    return (
      <g key={key}>
        <rect x={x} y={y} width={w} height={h} rx={rx} fill={metalBase} stroke={edge} strokeWidth="0.85" />
        <line x1={x+0.8} y1={y+0.8} x2={x+w-0.8} y2={y+0.8} stroke={hi} strokeWidth="0.7" opacity="0.85" />
      </g>
    );
  };

  const frameDark = darken(fc, 35);
  const frameMid = fc;
  const frameLight = lighten(fc, 15);
  const frameVDark = darken(fc, 55);
  const seamDark = darken(fc, 22);
  const seamLight = lighten(fc, 12);
  const isUnionShape = isRectUnionRoot(presRoot) && presRoot.regions.length > 1;
  const isBayPreset = String(rootCell?.preset || presRoot?.preset || '') === 'bay';

  const bayNodes = renderSharedBayPresentation({
    enabled: isBayPreset,
    skipBayProjection,
    rootCell,
    presRoot,
    bayGeometry,
    mirror,
    ox,
    oy,
    ww,
    wh,
    idPrefix: pid,
    frameColor: fc || C.fO,
    darken,
    lighten,
    mm2px,
    resolveBayPresentationDepthMult,
    bayPresentationDepthMult,
    bayPresentationStarkness,
    includeAccessories: true,
    extTopPx: Number.isFinite(extTopPx) ? extTopPx : 0,
    extBottomPx: Number.isFinite(extBottomPx) ? extBottomPx : 0,
    extLeftPx: Number.isFinite(extLeftPx) ? extLeftPx : 0,
    extRightPx: Number.isFinite(extRightPx) ? extRightPx : 0,
    cillOn: !!cillOnForBay,
    cillPx: Number.isFinite(cillPxForBay) ? cillPxForBay : 0,
    cillStub: !!cillStubForBay,
    cillDepthMm: Number(cillDepthMmForBay || 0),
    view,
    isCellTree,
    isRectUnionRoot,
    renderFlatWindow: ({ ww: rW, wh: rH, rootCell: rRoot, ox: rX, oy: rY, idPrefix: rPrefix, bayGeometry: rBayGeometry }) => renderPresentationWin({
      ww: rW,
      wh: rH,
      rootCell: rRoot,
      ox: rX,
      oy: rY,
      openings,
      slideOpenings,
      view,
      fc,
      hc,
      hs,
      doorHs,
      doorCylinderLevel,
      vp,
      hSide,
      gt,
      glassStyleTile,
      pVents,
      hDirs,
      pHandleKinds,
      gBar,
      aBar,
      logicalDivs: logicalDivsRaw,
      panelInfillStyle,
      letterplateStyle,
      jointStyle,
      idPrefix: rPrefix,
      bayGeometry: rBayGeometry,
      bayPresentationDepthMult,
      bayPresentationStarkness,
      skipBayProjection: true,
      bayFacetProjectionContext: true,
    }),
  });
  if (bayNodes) return bayNodes;

  // Perimeter shadow: drawn at assembly level (before extenders) when skipDropShadow is set.
  const shadowExtraH = cillOnForBay ? cillPxForBay : 0;
  if (!skipDropShadow) {
    els.push(<rect key="p-shd" x={ox+4} y={oy+4} width={ww} height={wh + shadowExtraH} rx="2" fill="rgba(0,0,0,0.18)"/>);
  }

  if (isUnionShape) {
    // Outer profile only. Shared mullions are painted after the glass, matching design view,
    // so a flag pane cannot cover the stile on its left.
    els.push(FrameOuterUnionPresentation(ox, oy, ww, wh, inner, presRoot.regions, presRoot, fc, jointStyle, mirror, idPrefix));
  } else {
    const k1 = `pof-${ox}-${oy}-${mirror?'i':'e'}`;
    const mitreCol = mirror ? darken(fc, 10) : darken(fc, 25);
    const outerStroke = mirror ? "none" : frameDark;
    els.push(<g key={k1}>
      <g>
        <rect x={ox} y={oy} width={ww} height={OB} fill={frameMid} />
        <rect x={ox} y={oy + wh - OB} width={ww} height={OB} fill={frameMid} />
        <rect x={ox} y={oy + OB} width={OB} height={Math.max(0, wh - OB * 2)} fill={frameMid} />
        <rect x={ox + ww - OB} y={oy + OB} width={OB} height={Math.max(0, wh - OB * 2)} fill={frameMid} />
      </g>
      <line x1={ox+0.5} y1={oy+0.5} x2={ox+ww-0.5} y2={oy+0.5} stroke={frameLight} strokeWidth="1" opacity="0.7"/>
      <line x1={ox+0.5} y1={oy+0.5} x2={ox+0.5} y2={oy+wh-0.5} stroke={frameLight} strokeWidth="0.8" opacity="0.5"/>
      {outerStroke !== "none" && <rect x={ox} y={oy} width={ww} height={wh} fill="none" stroke={outerStroke} strokeWidth="0.5"/>}
      {renderOuterCornerJoinLines(ox, oy, ww, wh, mitreCol, jointStyle, '0.6', 'presentation')}
    </g>);
  }

  const SASH_W2 = 20;
  const SASH_OVERLAP = 5;
  const sashEls = [];
  const topEls = [];
  // Keep sash layering consistent across internal/external presentation views.
  const target = sashEls;
  const leavesForLogical = [];
  colLeaves(presRoot, inner, leavesForLogical);
  const spansOverlap = (a0, a1, b0, b1) => Math.min(a1, b1) - Math.max(a0, b0);
  const sideContactById = new Map();
  const getSideContact = (leaf, side) => {
    const key = `${leaf.id}:${side}`;
    if (sideContactById.has(key)) return sideContactById.get(key);
    const b = leaf.b;
    let maxOv = 0;
    for (const other of leavesForLogical) {
      if (other.id === leaf.id) continue;
      const o = other.b;
      if (side === 'left' && Math.abs(b.x - (o.x + o.w)) < 0.75) maxOv = Math.max(maxOv, spansOverlap(b.y, b.y + b.h, o.y, o.y + o.h));
      if (side === 'right' && Math.abs((b.x + b.w) - o.x) < 0.75) maxOv = Math.max(maxOv, spansOverlap(b.y, b.y + b.h, o.y, o.y + o.h));
      if (side === 'top' && Math.abs(b.y - (o.y + o.h)) < 0.75) maxOv = Math.max(maxOv, spansOverlap(b.x, b.x + b.w, o.x, o.x + o.w));
      if (side === 'bottom' && Math.abs((b.y + b.h) - o.y) < 0.75) maxOv = Math.max(maxOv, spansOverlap(b.x, b.x + b.w, o.x, o.x + o.w));
    }
    const sideLen = (side === 'left' || side === 'right') ? b.h : b.w;
    const val = maxOv <= 1 ? 'none' : (maxOv >= sideLen - 1 ? 'full' : 'partial');
    sideContactById.set(key, val);
    return val;
  };
  const leafById = new Map(leavesForLogical.map((l) => [l.id, l]));

  // Georgian bars — behind glass (draw before renderP)
  // Use design-mode-consistent reference (sashInset 2) so column count matches design mode
  if (gBar?.on && (gBar.cols >= 2 || gBar.rows >= 2)) {
    const gLvs = [];
    colLeaves(rootCell, {x:ox+OB, y:oy+OB, w:ww-OB*2, h:wh-OB*2}, gLvs);
    const gBarPx = mm2px(gBar.widthMm);
    const gCellH = (wh-OB*2) / gBar.rows;
    const gOffYpx = (Number(gBar.offY || 0) / 100) * gCellH;
    const gHBars = [];
    for (let gi = 1; gi < gBar.rows; gi++) gHBars.push(oy+OB + (wh-OB*2) * (gi / gBar.rows) + gOffYpx);
    let gMaxPW = 0;
    for (const gl of gLvs) {
      const pwRef = gl.b.w - 4; // same as design mode (sashInset 2)
      if (pwRef > gMaxPW) gMaxPW = pwRef;
    }
    const gCellWRef = (gMaxPW || 1) / gBar.cols;
    const gOffXpx = (Number(gBar.offX || 0) / 100) * gCellWRef;
    for (const gl of gLvs) {
      const gb = gl.b;
      const leafOp = openings ? openings[gl.id] : null;
      const isOp = leafOp && leafOp !== 'none' && leafOp !== 'panel';
      const hasSash = isOp;
      const ol = hasSash ? (mirror ? 0 : SASH_OVERLAP) : 0;
      // Match Astragal behavior: fixed panes let bars run fully to pane edges in presentation mode.
      const gsi = hasSash ? (SASH_W2 - ol) : 0;
      const gpx1 = gb.x + gsi, gpy1 = gb.y + gsi;
      const gpw = gb.w - gsi*2, gph = gb.h - gsi*2;
      if (gpw < 5 || gph < 5) continue;
      const gGlassRect = getLogicalGlassRectForLeaf(logicalDivs[gl.id], gpx1, gpy1, gpw, gph);
      const ggx1 = gGlassRect.x, ggy1 = gGlassRect.y;
      const ggw = gGlassRect.w, ggh = gGlassRect.h;
      if (ggw < 5 || ggh < 5) continue;
      const ggx2 = ggx1 + ggw, ggy2 = ggy1 + ggh;
      const pwRef = gb.w - 4;
      const gPaneCols = Math.max(2, Math.round(pwRef / gCellWRef));
      const gClipId = `${pid}-gpc-${gl.id}-${mirror?'i':'e'}`;
      els.push(<defs key={`gpd-${gl.id}-${mirror?'i':'e'}`}><clipPath id={gClipId}><rect x={ggx1} y={ggy1} width={ggw} height={ggh}/></clipPath></defs>);
      const gPaneEls = [];
      for (let gi = 1; gi < gPaneCols; gi++) {
        const gx = ggx1 + ggw * (gi / gPaneCols) - gBarPx/2 + gOffXpx;
        gPaneEls.push(<rect key={`gp-${gl.id}-v${gi}`} x={gx} y={ggy1} width={gBarPx} height={ggh} fill={fc} stroke={darken(fc,25)} strokeWidth="0.3"/>);
        gPaneEls.push(<line key={`gp-${gl.id}-vh${gi}`} x1={gx+gBarPx/2} y1={ggy1+1} x2={gx+gBarPx/2} y2={ggy2-1} stroke={lighten(fc,20)} strokeWidth="0.5" opacity="0.35"/>);
      }
      for (let gi = 0; gi < gHBars.length; gi++) {
        const gcy = gHBars[gi];
        const gby = gcy - gBarPx/2;
        // Let the clipPath handle edge cases (avoid “missing segment” look at pane boundaries).
        if (gby + gBarPx <= ggy1 || gby >= ggy2) continue;
        gPaneEls.push(<rect key={`gp-${gl.id}-h${gi}`} x={ggx1} y={gby} width={ggw} height={gBarPx} fill={fc} stroke={darken(fc,25)} strokeWidth="0.3"/>);
        gPaneEls.push(<line key={`gp-${gl.id}-hh${gi}`} x1={ggx1+1} y1={gcy} x2={ggx2-1} y2={gcy} stroke={lighten(fc,20)} strokeWidth="0.5" opacity="0.35"/>);
      }
      els.push(<g key={`gpg-${gl.id}-${mirror?'i':'e'}`} clipPath={`url(#${gClipId})`}>{gPaneEls}</g>);
    }
  }

  const renderP = (c, b) => {
    if (c.split === 'none') {
      const leafOpening = resolveFacetOpeningDirection(openings ? openings[c.id] : null);
      const slideDir = getSlideDirForLeaf(openings, slideOpenings, c.id);
      const openingDir = isTriangleOpening(leafOpening)
        ? leafOpening
        : leafOpening === 'tilt-turn-left'
          ? 'left'
          : leafOpening === 'tilt-turn-right'
            ? 'right'
            : null;
      const isOpener = !!openingDir;
      const isSlide = !!slideDir;
      const isDummy = leafOpening === 'dummy';
      const isPanel = leafOpening === 'panel';
      const hasSash = isOpener || isDummy || isSlide;
      const sw = SASH_W2;
      let sx = b.x;
      let sy = b.y;
      let sW = b.w;
      let sH = b.h;
      if (hasSash) {
        if (isUnionShape) {
          const leafRef = leafById.get(c.id) || { id: c.id, b };
          const cL = getSideContact(leafRef, 'left');
          const cR = getSideContact(leafRef, 'right');
          const cT = getSideContact(leafRef, 'top');
          const cB = getSideContact(leafRef, 'bottom');
          const seamInset = Math.max(2, IB * 0.18);
          const isTPreset = !!(presRoot && (presRoot.preset === 't-door' || presRoot.preset === 't'));
          const isLPreset = !!(presRoot && presRoot.preset === 'l');
          const lCutX = isLPreset ? (inner.x + inner.w * clamp(Number(presRoot.shapeParams?.cutX), 0.2, 0.8)) : null;
          const lVoidLeft = isLPreset && String(presRoot.shapeParams?.voidSide || '').toLowerCase() === 'left';
          // Match WG proper union presentation: avoid over-wide sash reveals at seam joins.
          const doorJoinInset = (isTPreset || isLPreset) ? Math.min(SASH_W2 - 1, SASH_OVERLAP + 1) : seamInset;
          let fullInsetL = cL === 'full';
          let fullInsetR = cR === 'full';
          if (isLPreset && lCutX !== null) {
            const onLeftOfCut = (b.x + b.w) <= (lCutX + 0.75);
            const onRightOfCut = b.x >= (lCutX - 0.75);
            // Keep inset only on the seam-owning side so L sashes stay laterally balanced.
            if (lVoidLeft) {
              if (onRightOfCut && cL === 'full') fullInsetL = false;
            } else {
              if (onLeftOfCut && cR === 'full') fullInsetR = false;
            }
          }
          const inL = fullInsetL ? doorJoinInset : 0;
          const inR = fullInsetR ? doorJoinInset : 0;
          const inT = cT === 'full' ? seamInset : 0;
          const inB = cB === 'full' ? seamInset : 0;
          const lInternalTopBottomComp = isLPreset && mirror;
          const olL = mirror ? 0 : (cL === 'none' ? SASH_OVERLAP : 0);
          const olR = mirror ? 0 : (cR === 'none' ? SASH_OVERLAP : 0);
          const olT = (mirror && !lInternalTopBottomComp) ? 0 : (cT === 'none' ? SASH_OVERLAP : 0);
          const olB = (mirror && !lInternalTopBottomComp) ? 0 : (cB === 'none' ? SASH_OVERLAP : 0);
          sx = b.x + inL - olL;
          sy = b.y + inT - olT;
          sW = b.w - inL - inR + olL + olR;
          sH = b.h - inT - inB + olT + olB;
        } else {
          const ol = mirror ? 0 : SASH_OVERLAP;
          sx = b.x - ol;
          sy = b.y - ol;
          sW = b.w + ol * 2;
          sH = b.h + ol * 2;
        }
      }

      const glk = `${pid}-pg-${Math.round(b.x)}-${Math.round(b.y)}-${mirror?'i':'e'}`;
      const gx = hasSash ? sx + sw : b.x + 2;
      const gy = hasSash ? sy + sw : b.y + 2;
      const gw = hasSash ? sW - sw*2 : b.w - 4;
      const gh = hasSash ? sH - sw*2 : b.h - 4;
      if (!isPanel && gt === 'satin') {
        els.push(<g key={glk}>
          <defs>
            <filter id={`psat-${glk}`}><feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" seed="2"/><feColorMatrix type="saturate" values="0"/><feComponentTransfer><feFuncA type="linear" slope="0.18"/></feComponentTransfer><feBlend in="SourceGraphic" mode="screen"/></filter>
          </defs>
          <rect x={gx} y={gy} width={Math.max(0,gw)} height={Math.max(0,gh)} fill="rgba(220,228,238,0.45)"/>
          <rect x={gx} y={gy} width={Math.max(0,gw)} height={Math.max(0,gh)} fill="rgba(235,240,248,0.3)" filter={`url(#psat-${glk})`}/>
        </g>);
      } else if (!isPanel) {
        const tilePath = glassStyleTile || null;
        const tileSize = 90; // fixed repeat size (smaller = more detail), keeps texture uniform across panes
        const patId = `gg-pat-${glk}`;
        els.push(<g key={glk}>
          <defs>
            {tilePath ? (
              <pattern id={patId} patternUnits="userSpaceOnUse" x="0" y="0" width={tileSize} height={tileSize}>
                <image href={tilePath} x="0" y="0" width={tileSize} height={tileSize} preserveAspectRatio="xMidYMid slice" />
              </pattern>
            ) : (
              <linearGradient id={`gg-${glk}`} x1="0" y1="0" x2="0.3" y2="1">
                  <stop offset="0%" stopColor="rgba(200,220,245,0.25)"/>
                  <stop offset="40%" stopColor="rgba(210,225,245,0.08)"/>
                  <stop offset="100%" stopColor="rgba(195,215,240,0.2)"/>
              </linearGradient>
            )}
          </defs>
          <rect
            x={gx}
            y={gy}
            width={Math.max(0, gw)}
            height={Math.max(0, gh)}
            fill={tilePath ? `url(#${patId})` : `url(#gg-${glk})`}
            stroke={tilePath ? "rgba(180,200,225,0.2)" : "none"}
            strokeWidth={tilePath ? "0.5" : 0}
          />
          {/* subtle sheen over patterned glass */}
          {tilePath ? (
            <rect
              x={gx}
              y={gy}
              width={Math.max(0, gw)}
              height={Math.max(0, gh)}
              fill="rgba(210,230,255,0.08)"
              stroke="none"
            />
          ) : null}
          <rect x={gx+gw*0.1} y={gy+gh*0.05} width={gw*0.1} height={gh*0.3} rx="2" fill="rgba(255,255,255,0.06)" transform={`rotate(-15,${gx+gw*0.15},${gy+gh*0.15})`}/>
        </g>);
      }
      if (isPanel) {
        // Panels should use full pane bounds (not glass inset), otherwise a false rectangular ring appears.
        renderPanePanelInfill(c.id, { x: b.x, y: b.y, w: Math.max(0, b.w), h: Math.max(0, b.h) }, els, fc, 0, true);
      }
      let logicalPanelRects = [];
      if (logicalDivs[c.id]) {
        const leafRef = leafById.get(c.id) || { id: c.id, b };
        const cL = getSideContact(leafRef, 'left');
        const cR = getSideContact(leafRef, 'right');
        const cT = getSideContact(leafRef, 'top');
        const cB = getSideContact(leafRef, 'bottom');
        const seamInset = IB / 2;
        const lb = {
          x: b.x + (cL !== 'none' ? seamInset : 0),
          y: b.y + (cT !== 'none' ? seamInset : 0),
          w: b.w - (cL !== 'none' ? seamInset : 0) - (cR !== 'none' ? seamInset : 0),
          h: b.h - (cT !== 'none' ? seamInset : 0) - (cB !== 'none' ? seamInset : 0),
        };
        if (lb.w > 3 && lb.h > 3) {
          logicalPanelRects = renderLogicalBarsForLeaf(c.id, lb, logicalDivs[c.id], els, panelInfillStyle, fc, 0) || [];
          if (letterplateStyle !== 'none') {
            const transomRect = logicalTransomBarRect(lb, logicalDivs[c.id]);
            const lp = renderLetterplate(`lp-${c.id}`, transomRect);
            if (lp) topEls.push(lp);
          }
        }
      }

      if (hasSash) {
        const sk = `ps-${Math.round(b.x)}-${Math.round(b.y)}-${mirror?'i':'e'}`;
        const sashStroke = frameVDark;
        const mitreStroke2 = darken(fc, 25);
        target.push(<g key={sk} data-wg-element="sash-frame" data-wg-leaf={String(c.id)} data-wg-mode={mirror ? "internal" : "presentation"}>
          <path d={`M${sx},${sy} L${sx+sW},${sy} L${sx+sW},${sy+sH} L${sx},${sy+sH} Z M${sx+sw},${sy+sw} L${sx+sw},${sy+sH-sw} L${sx+sW-sw},${sy+sH-sw} L${sx+sW-sw},${sy+sw} Z`} fill={frameMid} fillRule="evenodd"/>
          <rect x={sx} y={sy} width={sW} height={sH} fill="none" stroke={sashStroke} strokeWidth="0.5" data-wg-element="sash-outer-edge" data-wg-leaf={String(c.id)} />
          {renderSashCornerJoinLines(sx, sy, sW, sH, sw, mitreStroke2, jointStyle, '0.6')}
          <line x1={sx+0.5} y1={sy+0.5} x2={sx+sW-0.5} y2={sy+0.5} stroke={frameLight} strokeWidth="0.8" opacity="0.5"/>
          <line x1={sx+0.5} y1={sy+0.5} x2={sx+0.5} y2={sy+sH-0.5} stroke={frameLight} strokeWidth="0.6" opacity="0.3"/>
          <line x1={sx+1} y1={sy+sH-0.5} x2={sx+sW} y2={sy+sH-0.5} stroke="rgba(0,0,0,0.1)" strokeWidth={0.7} data-wg-element="sash-shadow-bottom" data-wg-leaf={String(c.id)} />
          <line x1={sx+sW-0.5} y1={sy+1} x2={sx+sW-0.5} y2={sy+sH} stroke="rgba(0,0,0,0.08)" strokeWidth={0.55} data-wg-element="sash-shadow-right" data-wg-leaf={String(c.id)} />
          {panelInfillStyle === 'plain' ? presentationPanelInsertBorders(c.id, logicalPanelRects, { x: sx + sw, y: sy + sw, w: Math.max(0, sW - sw * 2), h: Math.max(0, sH - sw * 2) }, fc) : null}
        </g>);

        const paneHs = resolvePaneHandleStyle(c.id);
        const isDoorHardware = paneHs === 'upvc' || paneHs === 'rim-cylinder';
        if (isOpener && (mirror || isDoorHardware)) {
          let hx, hy;
          const offV = Math.min(18, sH * 0.09);
          if (openingDir === 'top') { hx = sx + sW/2; hy = sy + sH - sw/2; }
          else if (openingDir === 'bottom') { hx = sx + sW/2; hy = sy + sw/2; }
          else if (openingDir === 'left') { hx = sx + sW - sw/2; hy = sy + sH/2 + offV; }
          else { hx = sx + sw/2; hy = sy + sH/2 + offV; }

          const isDoorHandle = (pHandleKinds && pHandleKinds[c.id]) === 'door';
          const hBase = hc || (isDoorHandle ? (fc || '#c0c0c0') : '#c0c0c0');
          const hCol = (String(hBase).startsWith('#') ? hBase : '#c0c0c0');
          const hDark = darken(hCol, 20);
          const hLight = lighten(hCol, 25);
          const hC = handleContrastTokens(hCol, fc || '#c8cdd6');
          // Match WG proper precedence exactly for presentation handles.
          // Keep per-pane overrides first, then opener-derived default, then global side.
          const thisDir = (hDirs && hDirs[c.id]) || inferHandleDirForOpening(openingDir) || hSide;
          let armDir = thisDir === 'right' ? 1 : -1;
          const isDefaultWindowHandle = !isDoorHandle && paneHs === 'offset';
          if (bayFacetProjectionContext && isDefaultWindowHandle) {
            // Keep bay default window handle orientation aligned with WG proper.
            armDir *= -1;
          }
          if (paneHs === 'rim-cylinder') {
            const sq = clamp(Math.min(sW, sH) * 0.13, 6, 12);
            const cylY = doorCylinderLevel === 'upper-third' ? (sy + sH * 0.34) : (sy + sH * 0.5);
            const qx = hx - sq / 2;
            const qy = cylY - sq / 2;
            topEls.push(<g key={`hdl-${c.id}`}>
              <rect x={qx} y={qy} width={sq} height={sq} rx="0.8" fill={hCol} stroke={hC.outline} strokeWidth={hC.low ? '1.1' : '0.7'}/>
              <rect x={qx+1.2} y={qy+1.2} width={Math.max(0, sq-2.4)} height={Math.max(0, sq-2.4)} rx="0.5" fill="none" stroke={hC.low ? hC.inner : hLight} strokeWidth="0.55" opacity="0.85"/>
            </g>);
          } else if (paneHs === 'teardrop') {
            topEls.push(<g key={`hdl-${c.id}`}>
              <rect x={hx-3.5} y={hy-7} width={7} height={14} rx="2.5" fill={hCol} stroke={hC.outline} strokeWidth={hC.low ? '1.35' : '0.5'}/>
              <ellipse cx={hx+armDir*7} cy={hy} rx="5.5" ry="3.5" fill={hCol} stroke={hC.outline} strokeWidth={hC.low ? '1.0' : '0.3'}/>
              <ellipse cx={hx+armDir*9} cy={hy} rx="2" ry="1.5" fill={hLight}/>
            </g>);
          } else {
            const bodyH = paneHs === 'upvc' ? Math.max(24, Math.min(96, sH * (2 / 3))) : 16;
            const bodyY = hy - bodyH / 2;
            const armLen = paneHs === 'upvc' ? Math.max(14, Math.min(42, sW * (2 / 3))) : 14;
            const armTh = paneHs === 'upvc' ? 4.2 : 3;
            const armX = armDir > 0 ? (hx + 2) : (hx - (armLen + 2));
            const bodyStroke = paneHs === 'upvc' ? hC.outline : 'rgba(0,0,0,0.3)';
            const bodyStrokeW = paneHs === 'upvc' ? (hC.low ? '1.35' : '0.6') : '0.6';
            const armStroke = paneHs === 'upvc' ? hC.armStroke : bodyStroke;
            const armStrokeW = paneHs === 'upvc' ? (hC.low ? '0.9' : '0.6') : bodyStrokeW;
            const isBulbEnd = paneHs === 'bulb-end';
            const capFill = paneHs === 'upvc' ? hCol : (isBulbEnd ? hCol : hLight);
            const capStroke = paneHs === 'upvc' ? hC.capStroke : bodyStroke;
            const capStrokeW = paneHs === 'upvc' ? '0.45' : bodyStrokeW;
            const capR = isBulbEnd ? 2.7 : 2;
            topEls.push(<g key={`hdl-${c.id}`}>
              <rect x={hx-3.5} y={bodyY} width={7} height={bodyH} rx="2" fill={hCol} stroke={bodyStroke} strokeWidth={bodyStrokeW}/>
              {paneHs === 'upvc' && hC.low ? <line x1={hx} y1={bodyY+1} x2={hx} y2={bodyY+bodyH-1} stroke={hC.inner} strokeWidth="0.6" /> : null}
              <rect x={hx-2} y={hy-2} width={4} height={4} rx="1" fill={paneHs === 'upvc' ? hC.pivotFill : hDark}/>
              {paneHs === 'upvc' && hC.low ? <rect x={armX} y={hy-(armTh/2)} width={armLen} height={armTh} rx="1.6" fill="none" stroke={hC.outline} strokeWidth="1.2" /> : null}
              <rect x={armX} y={hy-(armTh/2)} width={armLen} height={armTh} rx={paneHs === 'upvc' ? 1.5 : 1.2} fill={hCol} stroke={armStroke} strokeWidth={armStrokeW}/>
              <circle
                cx={armDir>0?(armX+armLen):(armX)}
                cy={hy}
                r={capR}
                fill={capFill}
                stroke={capStroke}
                strokeWidth={capStrokeW}
              />
            </g>);
          }
        }
      }

      const isTopRow = Math.abs(b.y - inner.y) < 2;
      const pv = pVents && pVents[c.id];
      const hasJambCentre = pVents && pVents._centre;
      const isCustomMode = vp === 'custom' || (
        vp === 'none' &&
        Object.keys(pVents || {}).some((k) => k === '_centre' || (!!pVents[k] && (pVents[k].sash === true || pVents[k].jamb === true)))
      );
      const activeSash = vp === 'sash'
        ? (hasSash && isTopRow && (pv ? pv.sash !== false : true))
        : (isCustomMode && pv && !!pv.sash);
      const activeJamb = isJambLikeVentMode(vp)
        ? (!hasJambCentre && isTopRow && (pv ? pv.jamb !== false : true) && extenderVentModeActive(vp, extTopPx))
        : (isCustomMode && !hasJambCentre && pv && !!pv.jamb);

      const renderVent = (ventX, ventY, ventW, ventH, keyPfx) => (
        <g key={`${keyPfx}-${c.id}`}>
          <rect x={ventX} y={ventY} width={ventW} height={ventH} rx="1.5" fill={frameVDark}/>
          <rect x={ventX+1.5} y={ventY+1} width={ventW-3} height={ventH-2} rx="0.5" fill={darken(fc,80)}/>
        </g>
      );

      const sashVentW = hasSash ? sW * 0.5 : b.w * 0.5;
      const sashVentH = 4;
      const sashVentCX = hasSash ? sx + sW/2 : b.x + b.w/2;
      const sashVentX = sashVentCX - sashVentW/2;
      const sashVentY = hasSash ? sy + (SASH_W2 - sashVentH)/2 : b.y + 2;

      const jambVentW = b.w * 0.5;
      const jambVentH = 4;
      const jambVentCX = b.x + b.w/2;
      const jambVentX = jambVentCX - jambVentW/2;
      const jambVentY = resolveHeadLikeVentY(vp, inner.y, oy, extTopPx, jambVentH);

      if (activeSash && hasSash) topEls.push(renderVent(sashVentX, sashVentY, sashVentW, sashVentH, 'vent-s'));
      if (activeJamb && isTopRow) topEls.push(renderVent(jambVentX, jambVentY, jambVentW, jambVentH, 'vent-j'));
      return;
    }

    const cb = childBounds(c, b);
    for (let i = 0; i < c.children.length; i++) renderP(c.children[i], cb[i]);
    for (let i = 0; i < c.children.length - 1; i++) {
      const bk = `pbar-${c.id}-${Math.round(cb[i].x)}-${Math.round(cb[i].y)}-${i}-${mirror?'i':'e'}`;
      if (c.split === 'vertical') {
        const bx = cb[i].x + cb[i].w;
        els.push(<g key={bk}>
          <rect x={bx} y={b.y} width={IB} height={b.h} fill={frameMid}/>
          <line x1={bx} y1={b.y} x2={bx} y2={b.y+b.h} stroke={seamLight} strokeWidth="0.3" opacity="0.45"/>
          <line x1={bx+IB} y1={b.y} x2={bx+IB} y2={b.y+b.h} stroke={seamDark} strokeWidth="0.3" opacity="0.42"/>
        </g>);
      } else {
        const by = cb[i].y + cb[i].h;
        els.push(<g key={bk}>
          <rect x={b.x} y={by} width={b.w} height={IB} fill={frameMid}/>
          <line x1={b.x} y1={by} x2={b.x+b.w} y2={by} stroke={seamLight} strokeWidth="0.3" opacity="0.45"/>
          <line x1={b.x} y1={by+IB} x2={b.x+b.w} y2={by+IB} stroke={seamDark} strokeWidth="0.3" opacity="0.42"/>
        </g>);
      }
    }
  };

  eachRegion(presRoot, inner, (rg, rb) => renderP(rg.root, rb));

  // Centre jamb vent — single large vent centred across full window head (only for 2+ columns)
  // The interactive designer supports this via `paneVents._centre`; ensure static renders (quote wizard / exports) match.
  {
    const centreJambActive = !!(pVents && pVents._centre);
    const hasAnyVentOverrides = Object.keys(pVents || {}).some((k) =>
      k === '_centre' || (!!pVents[k] && (pVents[k].sash === true || pVents[k].jamb === true))
    );
    const canJamb = vp === 'custom' || isJambLikeVentMode(vp) || (vp === 'none' && hasAnyVentOverrides);
    if (centreJambActive && canJamb && extenderVentModeActive(vp, extTopPx)) {
      const topRowLeaves = [];
      const countTopRow = (c, b) => {
        if (c.split === 'none') {
          if (Math.abs(b.y - inner.y) < 2) topRowLeaves.push(c.id);
          return;
        }
        const cb = childBounds(c, b);
        for (let i = 0; i < c.children.length; i++) countTopRow(c.children[i], cb[i]);
      };
      eachRegion(presRoot, inner, (rg, rb) => countTopRow(rg.root, rb));
      const hasMultiCols = topRowLeaves.length >= 2;
      if (hasMultiCols) {
        const cjVentW = inner.w * 0.33;
        const cjVentH = 4;
        const cjVentX = inner.x + inner.w / 2 - cjVentW / 2;
        const cjVentY = resolveHeadLikeVentY(vp, inner.y, oy, extTopPx, cjVentH);
        topEls.push(
          <g key="vent-centre">
            <rect x={cjVentX} y={cjVentY} width={cjVentW} height={cjVentH} rx="1.5" fill={frameVDark} />
            <rect x={cjVentX + 1.5} y={cjVentY + 1} width={cjVentW - 3} height={cjVentH - 2} rx="0.5" fill={darken(fc, 80)} />
          </g>
        );
      }
    }
  }

  // Astragal bars — in front of glass (draw after renderP)
  // Use design-mode-consistent reference (sashInset 2) so column count matches design mode
  const aBarColsCount = Math.max(0, Number(aBar?.cols) || 0);
  const aBarRowsCount = Math.max(0, Number(aBar?.rows) || 0);
  const aColDivs = Math.max(1, aBarColsCount);
  const aRowDivs = Math.max(1, aBarRowsCount);
  if (aBar?.on && (aBarColsCount >= 2 || aBarRowsCount >= 2)) {
    const aLvs = [];
    colLeaves(rootCell, {x:ox+OB, y:oy+OB, w:ww-OB*2, h:wh-OB*2}, aLvs);
    const aBarPx = mm2px(aBar.widthMm);
    const aCellH = (wh-OB*2) / aRowDivs;
    const aOffYpx = (Number(aBar.offY || 0) / 100) * aCellH;
    const aHBars = [];
    for (let ai = 1; ai < aBarRowsCount; ai++) aHBars.push(oy+OB + (wh-OB*2) * (ai / aRowDivs) + aOffYpx);
    let aMaxPW = 0;
    for (const al of aLvs) {
      const pwRef = al.b.w - 4; // same as design mode (sashInset 2)
      if (pwRef > aMaxPW) aMaxPW = pwRef;
    }
    const aCellWRef = (aMaxPW || 1) / aColDivs;
    const aOffXpx = (Number(aBar.offX || 0) / 100) * aCellWRef;
    for (const al of aLvs) {
      const ab = al.b;
      const leafOp = openings ? openings[al.id] : null;
      const isOp = leafOp && leafOp !== 'none' && leafOp !== 'panel';
      const hasSash = isOp;
      const ol = hasSash ? (mirror ? 0 : SASH_OVERLAP) : 0;
      // Fixed panes should let astragal bars run fully to pane edges in presentation mode.
      const asi = hasSash ? (SASH_W2 - ol) : 0;
      const apx1 = ab.x + asi, apy1 = ab.y + asi;
      const apw = ab.w - asi*2, aph = ab.h - asi*2;
      if (apw < 5 || aph < 5) continue;
      const aGlassRect = getLogicalGlassRectForLeaf(logicalDivs[al.id], apx1, apy1, apw, aph);
      const agx1 = aGlassRect.x, agy1 = aGlassRect.y;
      const agw = aGlassRect.w, agh = aGlassRect.h;
      if (agw < 5 || agh < 5) continue;
      const agx2 = agx1 + agw, agy2 = agy1 + agh;
      const pwRef = ab.w - 4;
      const aPaneCols = aBarColsCount >= 2 ? Math.max(2, Math.round(pwRef / aCellWRef)) : 1;
      const aClipId = `${pid}-apc-${al.id}-${mirror?'i':'e'}`;
      els.push(<defs key={`apd-${al.id}-${mirror?'i':'e'}`}><clipPath id={aClipId}><rect x={agx1} y={agy1} width={agw} height={agh}/></clipPath></defs>);
      const aPaneEls = [];
      for (let ai = 1; ai < aPaneCols; ai++) {
        const ax = agx1 + agw * (ai / aPaneCols) - aBarPx/2 + aOffXpx;
        aPaneEls.push(<rect key={`ap-${al.id}-v${ai}`} x={ax} y={agy1} width={aBarPx} height={agh} fill={fc} stroke={darken(fc,25)} strokeWidth="0.3"/>);
        aPaneEls.push(<line key={`ap-${al.id}-vh${ai}`} x1={ax+aBarPx/2} y1={agy1+1} x2={ax+aBarPx/2} y2={agy2-1} stroke={lighten(fc,20)} strokeWidth="0.5" opacity="0.35"/>);
      }
      for (let ai = 0; ai < aHBars.length; ai++) {
        const acy = aHBars[ai];
        const aby = acy - aBarPx/2;
        // Let the clipPath handle edge cases (avoid “missing segment” look at pane boundaries).
        if (aby + aBarPx <= agy1 || aby >= agy2) continue;
        aPaneEls.push(<rect key={`ap-${al.id}-h${ai}`} x={agx1} y={aby} width={agw} height={aBarPx} fill={fc} stroke={darken(fc,25)} strokeWidth="0.3"/>);
        aPaneEls.push(<line key={`ap-${al.id}-hh${ai}`} x1={agx1+1} y1={acy} x2={agx2-1} y2={acy} stroke={lighten(fc,20)} strokeWidth="0.5" opacity="0.35"/>);
      }
      els.push(<g key={`apg-${al.id}-${mirror?'i':'e'}`} clipPath={`url(#${aClipId})`}>{aPaneEls}</g>);
    }
  }

  // Union joint bars sit in front of glazing, same as design view. Sashes stay above them.
  if (isUnionShape) {
    const edges = collectSharedEdges(presRoot.regions, inner);
    const preset = presRoot.preset || 'rectangle';
    const isL = preset === 'l';
    const isT = preset === 't' || preset === 't-door';
    const isTDoor = preset === 't-door';
    const isU = preset === 'u';
    const lCutX = isL ? (inner.x + inner.w * clamp(Number(presRoot.shapeParams?.cutX), 0.2, 0.8)) : null;
    const lVoidLeft = isL && String(presRoot.shapeParams?.voidSide || '').toLowerCase() === 'left';
    const tMidX = isTDoor ? (inner.x + inner.w / 2) : null;
    const uMidX = isU ? (inner.x + inner.w / 2) : null;
    for (const e of edges) {
      if (e.o === 'v') {
        let bx = e.x - IB / 2;
        if (isL && lCutX !== null && Math.abs(e.x - lCutX) < 0.75) bx = lVoidLeft ? (e.x - IB) : e.x;
        else if (isTDoor && tMidX !== null) bx = e.x < tMidX ? (e.x - IB) : e.x;
        else if (isU && uMidX !== null) bx = e.x < uMidX ? e.x : (e.x - IB);
        els.push(
          <g key={`pub-${Math.round(bx)}-${Math.round(e.y0)}-${Math.round(e.y1)}`}>
            <rect x={bx} y={e.y0} width={IB} height={e.y1 - e.y0} fill={frameMid}/>
            <line x1={bx} y1={e.y0} x2={bx} y2={e.y1} stroke={seamLight} strokeWidth="0.3" opacity="0.45"/>
            <line x1={bx+IB} y1={e.y0} x2={bx+IB} y2={e.y1} stroke={seamDark} strokeWidth="0.3" opacity="0.42"/>
          </g>
        );
      } else {
        const by = isT ? e.y : (e.y - IB / 2);
        els.push(
          <g key={`puh-${Math.round(e.x0)}-${Math.round(by)}-${Math.round(e.x1)}`}>
            <rect x={e.x0} y={by} width={e.x1 - e.x0} height={IB} fill={frameMid}/>
            <line x1={e.x0} y1={by} x2={e.x1} y2={by} stroke={seamLight} strokeWidth="0.3" opacity="0.45"/>
            <line x1={e.x0} y1={by+IB} x2={e.x1} y2={by+IB} stroke={seamDark} strokeWidth="0.3" opacity="0.42"/>
          </g>
        );
      }
    }
  }

  els.push(...sashEls);
  els.push(...topEls);
  return els;
}


/** Design-tab style: Glass + OpeningTri + BarH/BarV only (no SashFrame, Handle, Vent). skipOpenings defers triangles to a later pass so they render on top of Georgian bars. */
function renderDesignTree(c, b, els, openings, slideOpenings, gk, glassType, glassStyleTile, frameColor = null, skipOpenings = false, mirrorText = false, logicalDivs = null, panelInfillStyle = 'plain', sashRoles = null, openingInsetByLeaf = null) {
  if (c.split === 'none') {
    const val = openings ? openings[c.id] : null;
    const hasLogicalForLeaf = !!(logicalDivs && logicalDivs[c.id]);
    if (!isPanelOpening(val)) {
      els.push(Glass(b.x, b.y, b.w, b.h, gk, glassType, glassStyleTile));
    }
    if (hasLogicalForLeaf) renderLogicalBarsForLeaf(c.id, b, logicalDivs[c.id], els, panelInfillStyle, frameColor);
    const slideDir = getSlideDirForLeaf(openings, slideOpenings, c.id);
    if (isPanelOpening(val) && !skipOpenings) {
      if (!hasLogicalForLeaf) {
        renderPanePanelInfill(c.id, b, els, frameColor, 0);
      }
      const panelLabel = makePanelLabelElement(c.id, b, mirrorText, '-design');
      if (panelLabel) els.push(panelLabel);
    }
    if (!skipOpenings && (val || slideDir)) {
      const inset = openingInsetByLeaf && openingInsetByLeaf[c.id] ? openingInsetByLeaf[c.id] : { l: 0, r: 0, t: 0, b: 0 };
      const tx = b.x + inset.l;
      const ty = b.y + inset.t;
      const tw = Math.max(0, b.w - inset.l - inset.r);
      const th = Math.max(0, b.h - inset.t - inset.b);
      if (val === 'dummy') {
        const fontSize = 2 * Math.min(Math.max(9, Math.min(tw * 0.22, th * 0.26)), 19);
        const cx = tx + tw / 2;
        const unMirror = mirrorText ? `translate(${2 * cx},0) scale(-1,1)` : undefined;
        els.push(
          <text
            key={`dum-${c.id}`}
            x={cx}
            y={ty + th / 2 + fontSize * 0.35}
            textAnchor="middle"
            fill="#e04040"
            fontSize={fontSize}
            fontFamily="'JetBrains Mono',monospace"
            fontWeight="700"
            opacity="0.7"
            transform={unMirror}
          >
            DUMMY
          </text>
        );
      }
      if (isTriangleOpening(val)) {
        const tri = OpeningTri(tx, ty, tw, th, val, c.id);
        if (tri) els.push(React.cloneElement(tri, { key: `op-${c.id}` }));
      }
      if (isTiltTurnOpening(val)) {
        const tt = TiltTurnIndicator(tx, ty, tw, th, val, c.id);
        if (tt) els.push(React.cloneElement(tt, { key: `op-tt-${c.id}` }));
      }
      if (slideDir) {
        const ar = OpeningArrow(tx, ty, tw, th, slideDir, c.id);
        if (ar) els.push(React.cloneElement(ar, { key: `op-slide-${c.id}` }));
      }
    }
    const role = sashRoles ? sashRoles[c.id] : null;
    if (!skipOpenings && !isPanelOpening(val) && val !== 'dummy' && role) {
      const roleEl = SashRoleLabel(c.id, b, role, mirrorText, '-design');
      if (roleEl) els.push(roleEl);
    }
    return;
  }
  const n = c.children.length; if (!n) return;
  const cb = childBounds(c, b);
  for (let i = 0; i < n; i++) renderDesignTree(c.children[i], cb[i], els, openings, slideOpenings, gk, glassType, glassStyleTile, frameColor, skipOpenings, mirrorText, logicalDivs, panelInfillStyle, sashRoles, openingInsetByLeaf);
  for (let i = 0; i < n - 1; i++) {
    if (c.split === 'vertical') els.push(BarVDesign(cb[i].x + cb[i].w, b.y, b.h, gk, frameColor));
    else els.push(BarHDesign(b.x, cb[i].y + cb[i].h, b.w, gk, frameColor));
  }
}

/** Renders only opening triangles (and DUMMY text) so they can be drawn on top of Georgian bars. */
function renderDesignTreeOpenings(c, b, els, openings, slideOpenings, gk, mirrorText = false, frameColor = null, sashRoles = null, clipInsetByLeaf = null) {
  if (c.split === 'none') {
    const val = openings ? openings[c.id] : null;
    const slideDir = getSlideDirForLeaf(openings, slideOpenings, c.id);
    const inset = clipInsetByLeaf && clipInsetByLeaf[c.id] ? clipInsetByLeaf[c.id] : { l: 0, r: 0, t: 0, b: 0 };
    const tx = b.x + inset.l;
    const ty = b.y + inset.t;
    const tw = Math.max(0, b.w - inset.l - inset.r);
    const th = Math.max(0, b.h - inset.t - inset.b);
    if (isPanelOpening(val)) {
      renderPanePanelInfill(c.id, { x: tx, y: ty, w: tw, h: th }, els, frameColor, 0);
      const panelLabel = makePanelLabelElement(c.id, { x: tx, y: ty, w: tw, h: th }, mirrorText, '-design-openings');
      if (panelLabel) els.push(panelLabel);
    }
    if (val || slideDir) {
      if (val === 'dummy') {
        const fontSize = 2 * Math.min(Math.max(9, Math.min(tw * 0.22, th * 0.26)), 19);
        const cx = tx + tw / 2;
        const unMirror = mirrorText ? `translate(${2 * cx},0) scale(-1,1)` : undefined;
        els.push(
          <text
            key={`dum-${c.id}`}
            x={cx}
            y={ty + th / 2 + fontSize * 0.35}
            textAnchor="middle"
            fill="#e04040"
            fontSize={fontSize}
            fontFamily="'JetBrains Mono',monospace"
            fontWeight="700"
            opacity="0.7"
            transform={unMirror}
          >
            DUMMY
          </text>
        );
      }
      if (isTriangleOpening(val)) {
        const tri = OpeningTri(tx, ty, tw, th, val, c.id);
        if (tri) {
          const clipId = `${gk}-opclip-${c.id}-tri`;
          els.push(
            <defs key={`opd-${c.id}-tri`}>
              <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
                <rect x={tx} y={ty} width={tw} height={th} />
              </clipPath>
            </defs>
          );
          els.push(<g key={`opg-${c.id}-tri`} clipPath={`url(#${clipId})`}>{React.cloneElement(tri, { key: `op-${c.id}` })}</g>);
        }
      }
      if (isTiltTurnOpening(val)) {
        const tt = TiltTurnIndicator(tx, ty, tw, th, val, c.id);
        if (tt) {
          const clipId = `${gk}-opclip-${c.id}-tt`;
          els.push(
            <defs key={`opd-${c.id}-tt`}>
              <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
                <rect x={tx} y={ty} width={tw} height={th} />
              </clipPath>
            </defs>
          );
          els.push(<g key={`opg-${c.id}-tt`} clipPath={`url(#${clipId})`}>{React.cloneElement(tt, { key: `op-tt-${c.id}` })}</g>);
        }
      }
      if (slideDir) {
        const ar = OpeningArrow(tx, ty, tw, th, slideDir, c.id);
        if (ar) {
          const clipId = `${gk}-opclip-${c.id}-slide`;
          els.push(
            <defs key={`opd-${c.id}-slide`}>
              <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
                <rect x={tx} y={ty} width={tw} height={th} />
              </clipPath>
            </defs>
          );
          els.push(<g key={`opg-${c.id}-slide`} clipPath={`url(#${clipId})`}>{React.cloneElement(ar, { key: `op-slide-${c.id}` })}</g>);
        }
      }
    }
    const role = sashRoles ? sashRoles[c.id] : null;
    if (!isPanelOpening(val) && val !== 'dummy' && role) {
      const roleEl = SashRoleLabel(c.id, b, role, mirrorText, '-design-openings');
      if (roleEl) els.push(roleEl);
    }
    return;
  }
  const cb = childBounds(c, b);
  for (let i = 0; i < c.children.length; i++) renderDesignTreeOpenings(c.children[i], cb[i], els, openings, slideOpenings, gk, mirrorText, frameColor, sashRoles, clipInsetByLeaf);
}

function renderTree(c, b, els, openings, gk, opts) {
  if (c.split === 'none') {
    const opening = openings ? openings[c.id] : null;
    const hasLogicalForLeaf = !!(opts?.logicalDivs && opts.logicalDivs[c.id]);
    const slideDir = getSlideDirForLeaf(openings, opts?.slideOpenings || null, c.id);
    const openingDir = isTriangleOpening(opening)
      ? opening
      : opening === 'tilt-turn-left'
        ? 'left'
        : opening === 'tilt-turn-right'
          ? 'right'
          : (slideDir ? 'top' : null);
    const hasSash = !!openingDir || opening === 'dummy';
    const mirror = opts?.view === 'internal';
    const frameCol = opts?.frameColor || null;
    const glassType = opts?.glassType || 'clear';
    const glassStyleTile = opts?.glassStyleTile || null;
    const handleStyle = (() => {
      const baseStyle = opts?.handleStyle || 'lever';
      const paneKinds = (opts?.paneHandleKinds && typeof opts.paneHandleKinds === 'object') ? opts.paneHandleKinds : {};
      const doorStyle = opts?.doorHandleType || 'lever';
      const kind = paneKinds[c.id];
      if (kind === 'door') return doorStyle === 'rim-cylinder' ? 'rim-cylinder' : 'upvc';
      if (kind === 'window') return baseStyle === 'upvc' ? 'offset' : baseStyle;
      return baseStyle;
    })();
    const handleCol = opts?.handleColor || '#F0F0F0';
    const handleSide = opts?.handleDirs && opts?.handleDirs[c.id] ? opts.handleDirs[c.id] : (opts?.handleSide || 'left');
    const ventPos = opts?.ventPos || 'none';
    const paneVents = opts?.paneVents || {};
    const hasCentre = !!paneVents?._centre;

    if (hasSash) {
      els.push(SashFrame(b.x, b.y, b.w, b.h, gk, frameCol));
    }
    if (!isPanelOpening(opening)) {
      els.push(Glass(b.x, b.y, b.w, b.h, gk, glassType, glassStyleTile));
    }
    if (hasLogicalForLeaf) renderLogicalBarsForLeaf(c.id, b, opts.logicalDivs[c.id], els, opts?.panelInfillStyle || 'plain', opts?.frameColor || null);

    // Internal handle (approx) on non-dummy sash
    if (mirror && hasSash && (isTriangleOpening(opening) || isTiltTurnOpening(opening))) {
      const hx = b.x + b.w/2 + (handleSide === 'right' ? b.w*0.23 : -b.w*0.23);
      const hy = b.y + b.h/2;
      els.push(Handle(hx, hy, handleSide, handleStyle, handleCol, gk));
    }

    // Vents (approx)
    const isTopRow = Math.abs(b.y - (opts?.innerTopY ?? b.y)) < 2;
    const pv = paneVents && paneVents[c.id];
    const sashOverride = (pv && pv.sash !== undefined) ? !!pv.sash : undefined;
    const jambOverride = (pv && pv.jamb !== undefined) ? !!pv.jamb : undefined;
    const activeSash = (sashOverride !== undefined) ? sashOverride : (ventPos === 'sash' && isTopRow && hasSash);
    const activeJamb = !hasCentre && ((jambOverride !== undefined) ? jambOverride : (ventPos === 'jamb' && isTopRow));
    if (activeSash && hasSash) {
      els.push(Vent(b.x + b.w*0.25, b.y + 6, b.w*0.5, 4, frameCol || '#c8cdd6', gk));
    }
    if (activeJamb && isTopRow) {
      els.push(Vent(b.x + b.w*0.25, (opts?.outerY ?? 0) + 8, b.w*0.5, 4, frameCol || '#c8cdd6', gk));
    }

    // Openings (DUMMY / triangles) drawn last so they sit on top of glass, handles, and vents
    if (isPanelOpening(opening)) {
      if (!hasLogicalForLeaf) {
        renderPanePanelInfill(c.id, b, els, opts?.frameColor || null, 0);
      }
      const panelLabel = makePanelLabelElement(c.id, b, mirror, '-tree');
      if (panelLabel) els.push(panelLabel);
    }
    if (opening || slideDir) {
      const val = opening;
      if (val === 'dummy') {
        const fontSize = 2 * Math.min(Math.max(9, Math.min(b.w * 0.22, b.h * 0.26)), 19);
        const cx = b.x + b.w / 2;
        const unMirror = mirror ? `translate(${2 * cx},0) scale(-1,1)` : undefined;
        els.push(
          <text
            key={`dum-${c.id}`}
            x={cx}
            y={b.y + b.h/2 + fontSize * 0.35}
            textAnchor="middle"
            fill="#e04040"
            fontSize={fontSize}
            fontFamily="'JetBrains Mono',monospace"
            fontWeight="700"
            opacity="0.7"
            transform={unMirror}
          >
            DUMMY
          </text>
        );
      }
      if (isTriangleOpening(val)) {
        const tri = OpeningTri(b.x, b.y, b.w, b.h, val, c.id);
        if (tri) els.push(React.cloneElement(tri, { key: `op-${c.id}` }));
      }
      if (isTiltTurnOpening(val)) {
        const tt = TiltTurnIndicator(b.x, b.y, b.w, b.h, val, c.id);
        if (tt) els.push(React.cloneElement(tt, { key: `op-tt-${c.id}` }));
      }
      if (slideDir) {
        const ar = OpeningArrow(b.x, b.y, b.w, b.h, slideDir, c.id);
        if (ar) els.push(React.cloneElement(ar, { key: `op-slide-${c.id}` }));
      }
    }
    const role = opts?.sashRoles ? opts.sashRoles[c.id] : null;
    if (!isPanelOpening(opening) && opening !== 'dummy' && role) {
      const roleEl = SashRoleLabel(c.id, b, role, mirror, '-tree');
      if (roleEl) els.push(roleEl);
    }
    return;
  }
  const n = c.children.length; if (!n) return;
  const cb = childBounds(c, b);
  for (let i = 0; i < n; i++) renderTree(c.children[i], cb[i], els, openings, gk, opts);
  for (let i = 0; i < n-1; i++) {
    if (c.split === 'vertical') els.push(BarV(cb[i].x+cb[i].w, b.y, b.h, gk));
    else els.push(BarH(b.x, cb[i].y+cb[i].h, b.w, gk));
  }
}

/** Trickle vents for static design-mode renders (matches presentation vent rules; no interactive ghosts). */
function renderDesignTrickleVents({
  ww,
  wh,
  rootCell,
  ox = 0,
  oy = 0,
  openings = null,
  slideOpenings = null,
  vp = 'none',
  pVents = {},
  fc = null,
  extTopPx = 0,
  idPrefix = 'wg',
}) {
  const hasAnyVentOverrides = Object.keys(pVents || {}).some((k) =>
    k === '_centre' || (!!pVents[k] && (pVents[k].sash === true || pVents[k].jamb === true))
  );
  if (vp === 'none' && !hasAnyVentOverrides) return null;

  const inner = { x: ox + OB, y: oy + OB, w: ww - OB * 2, h: wh - OB * 2 };
  const presRoot = isRectUnionRoot(rootCell)
    ? (rootCell.regions.length === 1 ? rootCell.regions[0].root : rootCell)
    : rootCell;
  const frameVDark = darken(fc || '#c8cdd6', 55);
  const pid = String(idPrefix || 'wg');
  const isCustomMode = vp === 'custom' || (vp === 'none' && hasAnyVentOverrides);
  const els = [];

  const renderVent = (ventX, ventY, ventW, ventH, keyPfx) => (
    <g key={keyPfx}>
      <rect x={ventX} y={ventY} width={ventW} height={ventH} rx="1.5" fill={frameVDark} />
      <rect x={ventX + 1.5} y={ventY + 1} width={ventW - 3} height={ventH - 2} rx="0.5" fill={darken(fc || '#c8cdd6', 80)} />
    </g>
  );

  const leaves = [];
  eachRegion(presRoot, inner, (rg, rb) => colLeaves(rg.root, rb, leaves));
  const topY = leaves.length
    ? leaves.reduce((m, l) => Math.min(m, l.b.y), Number.POSITIVE_INFINITY)
    : inner.y;
  const hasJambCentre = !!(pVents && pVents._centre);

  for (const l of leaves) {
    const b = l.b;
    const leafOpening = openings ? openings[l.id] : null;
    const isOpener = isTriangleOpening(leafOpening) || isTiltTurnOpening(leafOpening);
    const isSlide = !!(slideOpenings && isSlidingOpening(slideOpenings[l.id]));
    const isDummy = leafOpening === 'dummy';
    const hasSash = isOpener || isDummy || isSlide;

    const isTopRow = Math.abs(b.y - topY) < 2;
    const pv = pVents && pVents[l.id];

    const activeSash = vp === 'sash'
      ? (hasSash && isTopRow && (pv ? pv.sash !== false : true))
      : (isCustomMode && pv && !!pv.sash);
    const activeJamb = isJambLikeVentMode(vp)
      ? (!hasJambCentre && isTopRow && (pv ? pv.jamb !== false : true) && extenderVentModeActive(vp, extTopPx))
      : (isCustomMode && !hasJambCentre && pv && !!pv.jamb);

    const sashVentW = b.w * 0.5;
    const sashVentH = 4;
    const sashVentX = b.x + b.w / 2 - sashVentW / 2;
    const sashVentY = b.y + 6;

    const jambVentW = b.w * 0.5;
    const jambVentH = 4;
    const jambVentX = b.x + b.w / 2 - jambVentW / 2;
    const jambVentY = resolveHeadLikeVentY(vp, inner.y, oy, extTopPx, jambVentH);

    if (activeSash && hasSash) {
      els.push(renderVent(sashVentX, sashVentY, sashVentW, sashVentH, `${pid}-vent-ds-${l.id}`));
    }
    if (activeJamb && isTopRow) {
      els.push(renderVent(jambVentX, jambVentY, jambVentW, jambVentH, `${pid}-vent-dj-${l.id}`));
    }
  }

  const centreJambActive = !!(pVents && pVents._centre);
  const canJamb = vp === 'custom' || isJambLikeVentMode(vp) || (vp === 'none' && hasAnyVentOverrides);
  if (centreJambActive && canJamb && extenderVentModeActive(vp, extTopPx)) {
    const topRowCount = leaves.filter((leaf) => Math.abs(leaf.b.y - topY) < 2).length;
    if (topRowCount >= 2) {
      const cjVentW = inner.w * 0.33;
      const cjVentH = 4;
      const cjVentX = inner.x + inner.w / 2 - cjVentW / 2;
      const cjVentY = resolveHeadLikeVentY(vp, inner.y, oy, extTopPx, cjVentH);
      els.push(renderVent(cjVentX, cjVentY, cjVentW, cjVentH, `${pid}-vent-dc`));
    }
  }

  if (!els.length) return null;
  return <g key={`${pid}-design-vents`} data-wg-element="design-trickle-vents">{els}</g>;
}

/** Design-tab style: FrameOuter + simplified tree (Glass, flat BarH/BarV only; openings drawn separately on top) */
function renderDesignWin(ww, wh, root, ox = 0, oy = 0, openings = null, slideOpenings = null, idPrefix = 'wg', opts = null, skipOpenings = false) {
  const gk = makeGk(idPrefix);
  const els = [];
  const isUnion = isRectUnionRoot(root) && root.regions.length > 1;
  const shadowExtraH = Math.max(0, Number(opts?.shadowExtraH) || 0);
  const includeDropShadow = opts?.includeDropShadow !== false;
  if (includeDropShadow && !isUnion) {
    els.push(<rect key="shd" x={ox+3} y={oy+3} width={ww} height={wh + shadowExtraH} rx="1" fill="rgba(0,0,0,0.2)"/>);
  }
  const inner = { x: ox+OB, y: oy+OB, w: ww-OB*2, h: wh-OB*2 };
  const glassType = opts?.glassType || 'clear';
  const glassStyleTile = opts?.glassStyleTile || null;
  const frameColor = opts?.frameColor || null;
  const jointStyle = opts?.jointStyle || 'mitre-45';
  const mirrorText = !!opts?.mirrorText;
  const logicalDivs = normalizeLogicalDivs(opts?.logicalDivs || {});
  const openingInsetByLeaf = getOpeningInsetByLeaf(root, inner);
  eachRegion(root, inner, (rg, rb) => {
    renderDesignTree(rg.root, rb, els, openings, slideOpenings, gk, glassType, glassStyleTile, frameColor, skipOpenings, mirrorText, logicalDivs, opts?.panelInfillStyle || 'plain', opts?.sashRoles || null, openingInsetByLeaf);
  });
  if (isUnion) {
    const edges = collectSharedEdges(root.regions, inner);
    const preset = root.preset || 'rectangle';
    const isL = preset === 'l';
    const isT = preset === 't' || preset === 't-door';
    const isTDoor = preset === 't-door';
    const isU = preset === 'u';
    const lCutX = isL ? (inner.x + inner.w * clamp(Number(root.shapeParams?.cutX), 0.2, 0.8)) : null;
    const lVoidLeft = isL && String(root.shapeParams?.voidSide || '').toLowerCase() === 'left';
    const tMidX = isTDoor ? (inner.x + inner.w / 2) : null;
    const uMidX = isU ? (inner.x + inner.w / 2) : null;
    for (const e of edges) {
      if (e.o === 'v') {
        let bx = e.x - IB / 2;
        if (isL && lCutX !== null && Math.abs(e.x - lCutX) < 0.75) bx = lVoidLeft ? (e.x - IB) : e.x;
        else if (isTDoor && tMidX !== null) bx = e.x < tMidX ? (e.x - IB) : e.x;
        else if (isU && uMidX !== null) bx = e.x < uMidX ? e.x : (e.x - IB);
        els.push(BarVDesign(bx, e.y0, e.y1 - e.y0, gk, frameColor));
      } else {
        const by = isT ? e.y : (e.y - IB / 2);
        els.push(BarHDesign(e.x0, by, e.x1 - e.x0, gk, frameColor));
      }
    }
    els.push(FrameOuterUnionDesign(ox, oy, ww, wh, inner, root.regions, root, frameColor, jointStyle, idPrefix));
  } else {
    els.push(FrameOuter(ox, oy, ww, wh, frameColor, jointStyle));
  }
  return els;
}

function renderWin(ww, wh, root, ox = 0, oy = 0, openings = null, idPrefix = 'wg', opts = null) {
  const gk = makeGk(idPrefix);
  const els = [];
  els.push(<rect key="shd" x={ox+3} y={oy+3} width={ww} height={wh} rx="1" fill="rgba(0,0,0,0.2)"/>);
  const inner = { x: ox+OB, y: oy+OB, w: ww-OB*2, h: wh-OB*2 };
  const fc = opts?.frameColor || null;
  const logicalDivs = normalizeLogicalDivs(opts?.logicalDivs || {});
  eachRegion(root, inner, (rg, rb) => {
    renderTree(rg.root, rb, els, openings, gk, { ...(opts || {}), logicalDivs, innerTopY: inner.y, outerY: oy });
  });
  els.push(FrameTop(ox+OB, oy, ww-OB*2, gk, fc)); els.push(FrameBot(ox+OB, oy+wh-OB, ww-OB*2, gk, fc));
  els.push(FrameLeft(ox, oy+OB, wh-OB*2, gk, fc)); els.push(FrameRight(ox+ww-OB, oy+OB, wh-OB*2, gk, fc));
  els.push(Mitre(ox, oy, 'tl', fc)); els.push(Mitre(ox+ww-OB, oy, 'tr', fc));
  els.push(Mitre(ox, oy+wh-OB, 'bl', fc)); els.push(Mitre(ox+ww-OB, oy+wh-OB, 'br', fc));
  return els;
}

export function renderWindowToElements(ww, wh, root, openings = null, idPrefix = 'wg') {
  return renderWin(ww, wh, root, 0, 0, openings, idPrefix);
}

/** Renders only the SVG element (for export to PNG). Use with renderWindowGeneratorPayloadToPngBlob. */
export function WindowGeneratorSvg({ payload, widthPx = 400, view = 'external', mode = 'design', contentOffsetXPx = 0, contentOffsetYPx = 0, showBayWarnings = false }) {
  const inst = React.useRef(null);
  if (!inst.current) {
    _wgSvgInstanceCounter += 1;
    inst.current = `wgsvg-${_wgSvgInstanceCounter}`;
  }
  if (!payload || !payload.root || payload.w == null || payload.h == null) return null;
  const rootForRender = normalizeRootForRender(payload.root);
  const pres = (payload.presentation || {});
  const bayFallback = createDefaultBayConfig({
    tangentWidthMm: Math.max(200, Math.round(Number(payload.w || 400) * PX2MM)),
    heightMm: Math.max(200, Math.round(Number(payload.h || 300) * PX2MM)),
  });
  const bayConfigForGeometry = String(rootForRender?.preset || '') === 'bay'
    ? ((pres.bayConfig && typeof pres.bayConfig === 'object')
      ? pres.bayConfig
      : normalizeBayConfig(bayFallback, {
          tangentWidthMm: Math.max(200, Math.round(Number(payload.w || 400) * PX2MM)),
          heightMm: Math.max(200, Math.round(Number(payload.h || 300) * PX2MM)),
        }))
    : inferBayConfigFromRoot(rootForRender, payload.w, payload.h, pres.bayConfig || null);
  const bayGeometry = String(rootForRender?.preset || '') === 'bay' ? computeBayGeometry(bayConfigForGeometry) : null;
  const normalizedOpenMaps = normalizeRenderOpenMaps(
    rootForRender,
    payload.opens || null,
    payload.slideOpens || payload.slide_openings || payload.slide_opens || null,
    pres
  );
  const opens = normalizedOpenMaps.opens;
  const slideOpenings = normalizedOpenMaps.slideOpens;
  const panelInfillStyle = pres.panelInfillStyle || 'plain';
  const letterplateStyle = pres.letterplateStyle || 'none';

  const extTop = Number(pres.extTop || 0);
  const extBot = Number(pres.extBot || 0);
  const extLeft = Number(pres.extLeft || 0);
  const extRight = Number(pres.extRight || 0);
  const extLock = pres.extLock !== undefined ? !!pres.extLock : true;
  const cillOn = !!pres.cillOn;
  const cillHeight = Number(pres.cillHeight || 30);
  const cillDepth = Number(pres.cillDepth || 0);
  const cillStub = !!pres.cillStub;
  const cillUnionMode = normalizeCillUnionMode(pres.cillUnionMode ?? pres.cill_union_mode, pres.cillUnionIncludeUpper);
  const cillUnionIncludeUpper = !!pres.cillUnionIncludeUpper;
  const cillLock = pres.cillLock !== undefined ? !!pres.cillLock : true;

  const ePxT = extenderMmToPx(extTop), ePxB = extenderMmToPx(extBot), ePxL = extenderMmToPx(extLeft), ePxR = extenderMmToPx(extRight);
  const cillPx = cillOn ? mm2px(cillHeight) : 0;
  const totalExtW = ePxL + ePxR;
  const totalExtH = ePxT + ePxB;
  const lockedExtW = extLock ? totalExtW : 0;
  const lockedExtH = extLock ? totalExtH : 0;
  const lockedCillH = cillLock ? cillPx : 0;

  const wW = Number(payload.w);
  const wH = Number(payload.h);
  const effW = wW - lockedExtW;
  const effH = wH - lockedExtH - lockedCillH;
  const drawW = wW + (extLock ? 0 : totalExtW);
  const drawH = wH + (extLock ? 0 : totalExtH) + (cillLock ? 0 : cillPx);

  const isBayPresetForCanvas = String(rootForRender?.preset || '') === 'bay';
  const isLPresetForCanvas = isRectUnionRoot(rootForRender) && String(rootForRender?.preset || '').toLowerCase() === 'l';
  const hasDesignDimsOverlay = mode === 'design' && view === 'external' && !!pres.designDimsOn;
  const designDimsRightPad = hasDesignDimsOverlay ? (isLPresetForCanvas ? 110 : 78) : 0;
  const designDimsBottomPad = hasDesignDimsOverlay ? 34 : 0;
  const bayPresentationYOffset = (mode === 'presentation' && isBayPresetForCanvas) ? 28 : 0;
  const margin = 36;
  const cW = Math.max(1, Math.round(drawW + margin * 2 + designDimsRightPad));
  const cHBase = Math.max(1, Math.round(drawH + margin * 2 + (cillOn ? 8 : 0) + designDimsBottomPad));
  const cH = Math.max(1, Math.round(cHBase + bayPresentationYOffset));
  const px = (cW - drawW) / 2;
  // Bottom design-dimension rows need more space than top; bias content upward.
  const designDimsTopBias = hasDesignDimsOverlay ? 18 : 0;
  const py = ((cHBase - drawH) / 2) - designDimsTopBias;
  const wx = px + ePxL;
  const wy = py + ePxT;
  const iB = { x: wx + OB, y: wy + OB, w: Math.max(20, effW - OB * 2), h: Math.max(20, effH - OB * 2) };

  const aspect = cW / cH;
  const heightPx = Math.round(widthPx / aspect);
  const prefix = `${inst.current}-${mode}-${view}`;

  const fc = view === 'internal' ? (pres.frameColInt || '#F6F6F6') : (pres.frameColExt || '#F6F6F6');
  const glassType = pres.glassType || 'clear';
  const glassStyleTile = pres.glassStyleTile || null;
  const jointStyle = pres.jointStyle || 'mitre-45';
  const handleCol = pres.handleCol || '#F0F0F0';
  const handleStyle = pres.handleStyle || 'lever';
  const doorHandleType = pres.doorHandleType || 'lever';
  const rimCylinderLevel = pres.rimCylinderLevel || 'half';
  const handleSide = pres.handleSide || 'left';
  const handleDirs = remapHandleDirsForRender(
    (pres.handleDirs && typeof pres.handleDirs === 'object') ? pres.handleDirs : {},
    payload.root,
    rootForRender,
    iB
  );
  const rawPaneHandleKinds = (pres.paneHandleKinds && typeof pres.paneHandleKinds === 'object') ? pres.paneHandleKinds : {};
  const paneHandleKinds = remapPaneHandleKindsForRender(rawPaneHandleKinds, payload.root, rootForRender, iB);
  const sashRoles = normalizeSashRoles(pres.sashRoles || {});
  const ventPos = pres.ventPos || 'none';
  const paneVents = pres.paneVents || {};

  const normalizeBarCount = (value, fallback) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(0, Math.round(n));
  };
  const gBarOn = !!pres.gBarOn;
  const gBarCols = normalizeBarCount(pres.gBarCols, 3);
  const gBarRows = normalizeBarCount(pres.gBarRows, 4);
  const gBarWidthMm = Number(pres.gBarWidth || 18);
  const gBarOffX = Number(pres.gBarOffX || 0);
  const gBarOffY = Number(pres.gBarOffY || 0);

  const aBarOn = !!pres.aBarOn;
  const aBarCols = normalizeBarCount(pres.aBarCols, 3);
  const aBarRows = normalizeBarCount(pres.aBarRows, 4);
  const aBarWidthMm = Number(pres.aBarWidth || 18);
  const aBarOffX = Number(pres.aBarOffX || 0);
  const aBarOffY = Number(pres.aBarOffY || 0);
  const designDimsOn = !!pres.designDimsOn;

  const renderDimsOverlay = () => {
    if (mode !== 'design' || !designDimsOn) return null;
    // Keep modal/design previews clean: show dimensions on external only.
    if (view === 'internal') return null;
    const isHighResExport = widthPx >= 900;
    const dc = isHighResExport ? "#111827" : "#0f172a";
    // Scale overlays with export size so supplier PDFs remain legible.
    const scale =
      widthPx >= 900 ? 2.2 :
      widthPx >= 760 ? 1.7 :
      widthPx >= 620 ? 1.45 :
      widthPx >= 320 ? 1.15 :
      widthPx >= 260 ? 1.0 : 0.92;
    const dfs = Math.max(10, Math.round(13 * scale));
    const arrL = isHighResExport ? 7 : 5;
    const px2mm = (px0) => String(Math.round((Number(px0) || 0) * PX2MM));
    const labelBg = isHighResExport ? '#ffffff' : 'rgba(255, 255, 255, 0.95)';
    const labelStroke = isHighResExport ? '#111827' : '#94a3b8';
    const labelText = '#0f172a';
    const mainLabelFs = isHighResExport ? Math.max(18, Math.round(11.5 * scale)) : dfs;

    const dimLineH = (x1, x2, y, label, key) => {
      const mx = (x1 + x2) / 2;
      const tw = Math.max(
        46,
        Math.round(String(label).length * mainLabelFs * 0.64 + (isHighResExport ? 28 : 18))
      );
      const boxH = Math.max(19, Math.round(mainLabelFs * 1.28));
      const lineW = isHighResExport ? 1.8 : 1.15;
      return {
        lineEl: (
          <g key={`${key}-line`}>
          <line x1={x1} y1={y} x2={x2} y2={y} stroke={dc} strokeWidth={lineW} />
          <line x1={x1} y1={y - 4} x2={x1} y2={y + 4} stroke={dc} strokeWidth={lineW} />
          <line x1={x2} y1={y - 4} x2={x2} y2={y + 4} stroke={dc} strokeWidth={lineW} />
          <polygon points={`${x1},${y} ${x1 + arrL},${y - 2.5} ${x1 + arrL},${y + 2.5}`} fill={dc} />
          <polygon points={`${x2},${y} ${x2 - arrL},${y - 2.5} ${x2 - arrL},${y + 2.5}`} fill={dc} />
          </g>
        ),
        labelEl: (
          <g key={`${key}-label`}>
          <rect x={mx - tw / 2} y={y - boxH / 2} width={tw} height={boxH} rx="3" fill={labelBg} stroke={labelStroke} strokeWidth={isHighResExport ? 1.35 : 1.05} />
          <text
            x={mx}
            y={y + Math.round(mainLabelFs * 0.34)}
            textAnchor="middle"
            fill={labelText}
            fontSize={mainLabelFs}
            fontFamily={isHighResExport ? "Arial, Helvetica, sans-serif" : "monospace"}
            fontWeight="700"
          >
            {label}
          </text>
          </g>
        ),
      };
    };

    const dimLineV = (y1, y2, x, label, key) => {
      const my = (y1 + y2) / 2;
      const tw = Math.max(
        46,
        Math.round(String(label).length * mainLabelFs * 0.64 + (isHighResExport ? 28 : 18))
      );
      const boxH = Math.max(19, Math.round(mainLabelFs * 1.28));
      // Keep label fully inside canvas without changing overall scaling.
      const pad = 6;
      const minX = tw / 2 + pad;
      const maxX = cW - tw / 2 - pad;
      const xSafe = Math.min(Math.max(x, minX), maxX);
      const lineW = isHighResExport ? 1.8 : 1.15;
      return {
        lineEl: (
          <g key={`${key}-line`}>
          <line x1={xSafe} y1={y1} x2={xSafe} y2={y2} stroke={dc} strokeWidth={lineW} />
          <line x1={xSafe - 4} y1={y1} x2={xSafe + 4} y2={y1} stroke={dc} strokeWidth={lineW} />
          <line x1={xSafe - 4} y1={y2} x2={xSafe + 4} y2={y2} stroke={dc} strokeWidth={lineW} />
          <polygon points={`${xSafe},${y1} ${xSafe - 2.5},${y1 + arrL} ${xSafe + 2.5},${y1 + arrL}`} fill={dc} />
          <polygon points={`${xSafe},${y2} ${xSafe - 2.5},${y2 - arrL} ${xSafe + 2.5},${y2 - arrL}`} fill={dc} />
          </g>
        ),
        labelEl: (
          <g key={`${key}-label`}>
          <rect x={xSafe - tw / 2} y={my - boxH / 2} width={tw} height={boxH} rx="3" fill={labelBg} stroke={labelStroke} strokeWidth={isHighResExport ? 1.35 : 1.05} />
          <text
            x={xSafe}
            y={my + Math.round(mainLabelFs * 0.34)}
            textAnchor="middle"
            fill={labelText}
            fontSize={mainLabelFs}
            fontFamily={isHighResExport ? "Arial, Helvetica, sans-serif" : "monospace"}
            fontWeight="700"
          >
            {label}
          </text>
          </g>
        ),
      };
    };

    // Overall dimensions (outside the drawn extenders/cill frame)
    const isLPreset = isRectUnionRoot(rootForRender) && String(rootForRender.preset || '').toLowerCase() === 'l';
    // Keep overall height as the outermost right-side dimension lane.
    const oGap = isLPreset ? 58 : 30;
    const overall = [
      dimLineH(px, px + drawW, py + drawH + oGap, px2mm(drawW), "dim-ow"),
      dimLineV(py, py + drawH, px + drawW + oGap, px2mm(drawH), "dim-oh"),
    ];

    // L-shape Cut Y dimension (shape-level height), aligned with WG visual dimension scaling.
    const shapeEls = [];
    if (isLPreset) {
      const cutY = clamp(Number(rootForRender.shapeParams?.cutY), 0.2, 0.8);
      const voidLeft = String(rootForRender.shapeParams?.voidSide || '').toLowerCase() === 'left';
      const scaleY = iB.h > 0 ? (effH / iB.h) : 1;
      const cutYInnerPx = cutY * iB.h;
      const cutYpx = cutYInnerPx * scaleY;
      const cutYmm = px2mm(cutYpx);
      // On right-handed L, keep Cut Y outside-right but inside the overall-height lane.
      const cutYX = voidLeft ? (px - 14) : (px + drawW + 20);
      shapeEls.push(dimLineV(py, py + cutYpx, cutYX, cutYmm, "dim-l-cutY"));
    }

    // Leaf panes
    const leaves = [];
    colLeaves(rootForRender, iB, leaves);
    // Stable order: top-to-bottom then left-to-right
    leaves.sort((a, b) => (a.b.y - b.b.y) || (a.b.x - b.b.x));
    // Match WG "visual dimension" methodology for pane labels.
    const scaleX = iB.w > 0 ? (effW / iB.w) : 1;
    const scaleY = iB.h > 0 ? (effH / iB.h) : 1;

    const paneEls = [];
    for (let i = 0; i < leaves.length; i++) {
      const p = leaves[i];
      const ref = `A${i + 1}`;
      const b = p.b;
      const pad = 3;
      const paneLabelFs = isHighResExport ? Math.max(15, Math.round(9.6 * scale)) : Math.max(10, Math.round(12 * scale));
      const paneBoxH = Math.max(18, Math.round(paneLabelFs * 1.28));
      const refFs = isHighResExport ? Math.max(13, Math.round(8.2 * scale)) : Math.max(9, Math.round(10 * scale));
      const tagH = Math.max(13, Math.round(refFs * 1.35));
      const tagW = Math.max(22, Math.round(String(ref).length * refFs * 0.72 + 10));
      const refTextY = b.y + pad + Math.round(tagH * 0.74);

      // Ref tag
      paneEls.push(
        <g key={`ref-${ref}`}>
          <rect x={b.x + pad} y={b.y + pad} width={tagW} height={tagH} rx="3" fill={labelBg} stroke={labelStroke} strokeWidth={isHighResExport ? 1.05 : 0.9} />
          <text
            x={b.x + pad + tagW / 2}
            y={refTextY}
            textAnchor="middle"
            fill={labelText}
            fontSize={refFs}
            fontFamily={isHighResExport ? "Arial, Helvetica, sans-serif" : "monospace"}
            fontWeight="700"
          >
            {ref}
          </text>
        </g>
      );

      const wTxt = px2mm(b.w * scaleX);
      const hTxt = px2mm(b.h * scaleY);

      // Width label (bottom centre)
      const wLabelY = b.y + b.h - 10;
      const hitW = Math.max(40, Math.round(String(wTxt).length * paneLabelFs * 0.64 + 16));
      paneEls.push(
        <g key={`pw-${ref}`}>
          <rect x={b.x + b.w / 2 - hitW / 2} y={wLabelY - paneBoxH / 2} width={hitW} height={paneBoxH} rx="3" fill={labelBg} stroke={labelStroke} strokeWidth="1.0" />
          <text
            x={b.x + b.w / 2}
            y={wLabelY + Math.round(paneLabelFs * 0.34)}
            textAnchor="middle"
            fill={labelText}
            fontSize={paneLabelFs}
            fontFamily={isHighResExport ? "Arial, Helvetica, sans-serif" : "monospace"}
            fontWeight="700"
          >
            {wTxt}
          </text>
        </g>
      );

      // Height label (right centre, rotated)
      const hLabelX = b.x + b.w - 10;
      const hLabelY = b.y + b.h / 2;
      const hitH = Math.max(40, Math.round(String(hTxt).length * paneLabelFs * 0.64 + 16));
      paneEls.push(
        <g key={`ph-${ref}`} transform={`translate(${hLabelX},${hLabelY}) rotate(-90)`}>
          <rect x={-hitH / 2} y={-paneBoxH / 2} width={hitH} height={paneBoxH} rx="3" fill={labelBg} stroke={labelStroke} strokeWidth="1.0" />
          <text
            x={0}
            y={Math.round(paneLabelFs * 0.34)}
            textAnchor="middle"
            fill={labelText}
            fontSize={paneLabelFs}
            fontFamily={isHighResExport ? "Arial, Helvetica, sans-serif" : "monospace"}
            fontWeight="700"
          >
            {hTxt}
          </text>
        </g>
      );
    }

    return (
      <g key="dims-overlay">
        {paneEls}
        {overall.map((d) => d.lineEl)}
        {shapeEls.map((d) => d.lineEl)}
        {overall.map((d) => d.labelEl)}
        {shapeEls.map((d) => d.labelEl)}
      </g>
    );
  };

  const bayPresentationDepthMult = pres.bayPresentationDepthMult;
  const bayPresentationStarkness = pres.bayPresentationStarkness;
  const isBayPreset = String(rootForRender?.preset || '') === 'bay';
  const baseGroup =
    mode === 'presentation'
      ? (
          <>
            {!isBayPreset && renderAssemblyShadow({ px, py, drawW, drawH, offset: 4, fill: 'rgba(0,0,0,0.18)', rx: 2 })}
            {!isBayPreset && renderExtenders({ px, py, drawW, drawH, ePxT, ePxB, ePxL, ePxR, effH, cillPx, fillCol: fc, root: rootForRender, wx, wy, effW, part: 'sides' })}
            {renderPresentationWin({
              ww: effW, wh: effH, rootCell: rootForRender, ox: wx, oy: wy, openings: opens || null,
              slideOpenings: slideOpenings || null,
              view, fc, hc: handleCol, hs: handleStyle, doorHs: doorHandleType, doorCylinderLevel: rimCylinderLevel, vp: ventPos, hSide: handleSide, gt: glassType,
              glassStyleTile, pVents: paneVents, hDirs: handleDirs, pHandleKinds: paneHandleKinds,
              gBar: { on: gBarOn, cols: gBarCols, rows: gBarRows, widthMm: gBarWidthMm, offX: gBarOffX, offY: gBarOffY },
              aBar: { on: aBarOn, cols: aBarCols, rows: aBarRows, widthMm: aBarWidthMm, offX: aBarOffX, offY: aBarOffY },
              logicalDivs: payload.logicalDivs || {},
              panelInfillStyle,
              letterplateStyle,
              jointStyle,
              idPrefix: prefix,
              bayGeometry,
              bayPresentationDepthMult,
              bayPresentationStarkness,
              extTopPx: ePxT,
              extBottomPx: ePxB,
              extLeftPx: ePxL,
              extRightPx: ePxR,
              cillOnForBay: cillOn,
              cillPxForBay: cillPx,
              cillStubForBay: cillStub,
              cillDepthMmForBay: cillDepth,
              skipDropShadow: !isBayPreset,
            })}
            {!isBayPreset && renderCill({ cillOn, cillPx, cillDepth, cillStub, px, py, drawW, drawH, fillCol: fc, viewType: view, root: rootForRender, ox: wx, oy: wy, ow: effW, oh: effH, cillUnionMode, cillUnionIncludeUpper, segmentedUnionCill: true, extBottomPx: ePxB })}
            {!isBayPreset && renderExtenders({ px, py, drawW, drawH, ePxT, ePxB, ePxL, ePxR, effH, cillPx, fillCol: fc, root: rootForRender, wx, wy, effW, part: 'bottom' })}
          </>
        )
      : (
          <>
            {!isBayPreset && renderAssemblyShadow({ px, py, drawW, drawH })}
            {renderExtenders({ px, py, drawW, drawH, ePxT, ePxB, ePxL, ePxR, effH, cillPx, fillCol: fc, root: rootForRender, wx, wy, effW, part: 'sides' })}
            {renderGeorgianBars({
              root: rootForRender,
              iB,
              fillCol: fc,
              sashInset: 2,
              gBarOn,
              gBarCols,
              gBarRows,
              gBarWidthMm,
              gBarOffX,
              gBarOffY,
              idPrefix: prefix,
              logicalDivs: payload.logicalDivs || {},
            })}
            {renderDesignWin(effW, effH, rootForRender, wx, wy, opens || null, slideOpenings || null, prefix, {
              glassType,
              glassStyleTile,
              frameColor: fc,
              handleStyle,
              doorHandleType,
              handleColor: handleCol,
              handleSide,
              handleDirs,
              paneHandleKinds,
              mirrorText: view === 'internal',
              logicalDivs: payload.logicalDivs || {},
              panelInfillStyle,
              jointStyle,
              sashRoles,
              shadowExtraH: cillOn ? cillPx : 0,
              includeDropShadow: isBayPreset,
            }, true)}
            {renderDesignTrickleVents({
              ww: effW,
              wh: effH,
              rootCell: rootForRender,
              ox: wx,
              oy: wy,
              openings: opens || null,
              slideOpenings: slideOpenings || null,
              vp: ventPos,
              pVents: paneVents,
              fc,
              extTopPx: ePxT,
              idPrefix: prefix,
            })}
            {renderAstragalBars({
              root: rootForRender,
              iB,
              fillCol: fc,
              sashInset: 2,
              aBarOn,
              aBarCols,
              aBarRows,
              aBarWidthMm,
              aBarOffX,
              aBarOffY,
              idPrefix: prefix,
              logicalDivs: payload.logicalDivs || {},
            })}
            {renderCill({ cillOn, cillPx, cillDepth, cillStub, px, py, drawW, drawH, fillCol: fc, viewType: view, root: rootForRender, ox: wx, oy: wy, ow: effW, oh: effH, cillUnionMode, cillUnionIncludeUpper, segmentedUnionCill: mode === 'design', extBottomPx: ePxB })}
            {renderExtenders({ px, py, drawW, drawH, ePxT, ePxB, ePxL, ePxR, effH, cillPx, fillCol: fc, root: rootForRender, wx, wy, effW, part: 'bottom' })}
            {(() => {
              const openingEls = [];
              const clipInsetByLeaf = getOpeningInsetByLeaf(rootForRender, iB);
              eachRegion(rootForRender, iB, (rg, rb) => {
                renderDesignTreeOpenings(rg.root, rb, openingEls, opens || null, slideOpenings || null, prefix, view === 'internal', fc, sashRoles, clipInsetByLeaf);
              });
              return openingEls.length ? <g key="openings-top">{openingEls}</g> : null;
            })()}
          </>
        );

  const isBayPresentation = mode === 'presentation' && isBayPresetForCanvas;
  const contentBase = (view === 'internal' && !isBayPresentation)
    ? <g transform={`translate(${cW},0) scale(-1,1)`}>{baseGroup}</g>
    : (isBayPresentation && bayPresentationYOffset > 0)
      ? <g transform={`translate(0,${bayPresentationYOffset})`}>{baseGroup}</g>
      : baseGroup;
  const content = (Number(contentOffsetXPx) || Number(contentOffsetYPx))
    ? <g transform={`translate(${Number(contentOffsetXPx) || 0},${Number(contentOffsetYPx) || 0})`}>{contentBase}</g>
    : contentBase;
  return (
    <svg viewBox={`0 0 ${cW} ${cH}`} width={widthPx} height={heightPx} xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width={cW} height={cH} fill={DESIGN_CANVAS_BG} />
      {content}
      {!!(showBayWarnings && bayGeometry && Array.isArray(bayGeometry?.derived?.warnings) && bayGeometry.derived.warnings.length) && (
        <g>
          <rect x={8} y={8} width={Math.min(cW - 16, 420)} height={18 + bayGeometry.derived.warnings.length * 14} rx="4" fill="rgba(119,22,22,0.82)" />
          <text x={14} y={22} fill="#ffd8d8" fontSize="10.5" fontFamily="monospace" fontWeight="700">Bay geometry warning</text>
          {bayGeometry.derived.warnings.slice(0, 3).map((w, i) => (
            <text key={`bay-warn-${i}`} x={14} y={36 + i * 13} fill="#ffe9e9" fontSize="9.4" fontFamily="monospace">
              {String(w)}
            </text>
          ))}
        </g>
      )}
      {renderDimsOverlay()}
    </svg>
  );
}

function buildBaySchematicPreview(payload) {
  if (!payload || !payload.root || payload.w == null || payload.h == null) return null;
  const rootForRender = normalizeRootForRender(payload.root);
  if (String(rootForRender?.preset || '').toLowerCase() !== 'bay') return null;
  const pres = payload.presentation || {};
  const bayFallback = createDefaultBayConfig({
    tangentWidthMm: Math.max(200, Math.round(Number(payload.w || 400) * PX2MM)),
    heightMm: Math.max(200, Math.round(Number(payload.h || 300) * PX2MM)),
  });
  const bayConfig = (pres.bayConfig && typeof pres.bayConfig === 'object')
    ? normalizeBayConfig(pres.bayConfig, {
        tangentWidthMm: Math.max(200, Math.round(Number(payload.w || 400) * PX2MM)),
        heightMm: Math.max(200, Math.round(Number(payload.h || 300) * PX2MM)),
      })
    : normalizeBayConfig(bayFallback, {
        tangentWidthMm: Math.max(200, Math.round(Number(payload.w || 400) * PX2MM)),
        heightMm: Math.max(200, Math.round(Number(payload.h || 300) * PX2MM)),
      });
  const bayGeometry = computeBayGeometry(bayConfig);
  if (!bayGeometry) return null;
  if (bayGeometry?.derived?.hasCurvedFace) {
    const viewW = 300;
    const viewH = 210;
    const pad = 8;
    const chord = Math.max(1, Number(bayGeometry?.derived?.tangentWidth || bayConfig?.tangentWidth || 1));
    const projection = Math.max(1, Number(bayGeometry?.derived?.projection || bayConfig?.projectionDepth || 1));
    const controlY = 2 * projection;
    const apexY = projection;
    const minX = 0;
    const maxX = chord;
    const minY = Math.min(0, controlY);
    const maxY = Math.max(0, controlY);
    const srcW = Math.max(1, maxX - minX);
    const srcH = Math.max(1, maxY - minY);
    const scale = Math.min((viewW - pad * 2) / srcW, (viewH - pad * 2) / srcH);
    const offsetX = (viewW - srcW * scale) / 2;
    const offsetY = (viewH - srcH * scale) / 2;
    const mapPt = (p) => ({
      x: offsetX + (p.x - minX) * scale,
      y: offsetY + (p.y - minY) * scale,
    });
    const start = mapPt({ x: 0, y: 0 });
    const end = mapPt({ x: chord, y: 0 });
    const control = mapPt({ x: chord / 2, y: controlY });
    const apex = mapPt({ x: chord / 2, y: apexY });
    return {
      curved: true,
      start,
      end,
      control,
      apex,
      tangentMm: Math.round(chord),
      projectionMm: Math.round(projection),
      valid: !!bayGeometry?.derived?.isValid,
      facets: null,
      joints: null,
      mapped: null,
    };
  }
  const facets = Array.isArray(bayGeometry?.facets) ? bayGeometry.facets : [];
  if (!facets.length) return null;
  const rawPoints = [{ x: 0, y: 0 }];
  for (const f of facets) {
    const a = (Number(f.rotation || 0) * Math.PI) / 180;
    const last = rawPoints[rawPoints.length - 1];
    rawPoints.push({
      x: last.x + Number(f.width || 0) * Math.cos(a),
      y: last.y - Number(f.width || 0) * Math.sin(a),
    });
  }
  const start = rawPoints[0];
  const end = rawPoints[rawPoints.length - 1];
  const dx = Number(end?.x || 0) - Number(start?.x || 0);
  const dy = Number(end?.y || 0) - Number(start?.y || 0);
  const displayRotation = Math.hypot(dx, dy) < 1e-6 ? 0 : (-(Math.atan2(dy, dx) * 180) / Math.PI);
  const dr = (displayRotation * Math.PI) / 180;
  const cosR = Math.cos(dr);
  const sinR = Math.sin(dr);
  const points = rawPoints.map((p) => ({
    x: p.x * cosR - p.y * sinR,
    y: p.x * sinR + p.y * cosR,
  }));
  const minX = points.reduce((m, p) => Math.min(m, p.x), Infinity);
  const maxX = points.reduce((m, p) => Math.max(m, p.x), -Infinity);
  const minY = points.reduce((m, p) => Math.min(m, p.y), Infinity);
  const maxY = points.reduce((m, p) => Math.max(m, p.y), -Infinity);
  const viewW = 300;
  const viewH = 210;
  const pad = 6;
  const scale = Math.min((viewW - pad * 2) / Math.max(1, maxX - minX), (viewH - pad * 2) / Math.max(1, maxY - minY));
  const offsetX = (viewW - (maxX - minX) * scale) / 2;
  const offsetY = (viewH - (maxY - minY) * scale) / 2;
  const mapPt = (p) => ({ x: offsetX + (p.x - minX) * scale, y: offsetY + (p.y - minY) * scale });
  const mapped = points.map(mapPt);
  const centroid = mapped.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  centroid.x /= Math.max(1, mapped.length);
  centroid.y /= Math.max(1, mapped.length);
  const norm = (vx, vy) => {
    const mag = Math.hypot(vx, vy) || 1;
    return { x: vx / mag, y: vy / mag };
  };
  const estimateTextWidth = (text, fontSize = 10) => Math.max(16, String(text || '').length * fontSize * 0.62);
  const intersects = (a, b, padPx = 2) => (
    a.x < (b.x + b.w + padPx) &&
    (a.x + a.w + padPx) > b.x &&
    a.y < (b.y + b.h + padPx) &&
    (a.y + a.h + padPx) > b.y
  );
  const clampLabelCenter = (cx, cy, bw, bh) => ({
    x: clamp(cx, bw / 2 + 2, viewW - bw / 2 - 2),
    y: clamp(cy, bh / 2 + 2, viewH - bh / 2 - 2),
  });
  const toBox = (cx, cy, bw, bh) => ({ x: cx - bw / 2, y: cy - bh / 2, w: bw, h: bh });
  const occupied = (mapped || []).slice(1, -1).map((p) => ({ x: p.x - 6, y: p.y - 6, w: 12, h: 12 }));
  const placeLabelBlock = ({ anchorX, anchorY, dirPrimary, dirSecondary, boxW, boxH }) => {
    const pri = norm(dirPrimary.x, dirPrimary.y);
    const sec = norm(dirSecondary.x, dirSecondary.y);
    const distances = [14, 20, 26, 32, 38, 46, 56];
    const laterals = [0, -10, 10, -18, 18, -26, 26];
    for (const d of distances) {
      for (const l of laterals) {
        const cand = clampLabelCenter(anchorX + pri.x * d + sec.x * l, anchorY + pri.y * d + sec.y * l, boxW, boxH);
        const box = toBox(cand.x, cand.y, boxW, boxH);
        if (!occupied.some((o) => intersects(box, o))) {
          occupied.push(box);
          return cand;
        }
      }
    }
    const fallback = clampLabelCenter(anchorX + pri.x * 32, anchorY + pri.y * 32, boxW, boxH);
    occupied.push(toBox(fallback.x, fallback.y, boxW, boxH));
    return fallback;
  };
  const segs = facets.map((f, i) => ({
    id: f.id || `f-${i}`,
    width: Math.round(Number(f.width || 0)),
    p0: mapped[i],
    p1: mapped[i + 1],
  }));
  const joints = (Array.isArray(bayGeometry?.joints) ? bayGeometry.joints : []).map((j, i) => {
    const p = mapped[i + 1] || { x: 0, y: 0 };
    const outward = norm(p.x - centroid.x, p.y - centroid.y);
    const prev = mapped[i] || p;
    const next = mapped[i + 2] || p;
    const tangent = norm((next.x - prev.x) || 1, next.y - prev.y);
    const tag = `J${i + 1}-${i + 2}`;
    const angle = Math.round(Number((j.manualInternalAngle ?? j.derivedInternalAngle) || 0));
    const boxW = Math.max(estimateTextWidth(tag, 9.6), estimateTextWidth(`${angle}°`, 10)) + 4;
    const boxH = 24;
    const center = placeLabelBlock({
      anchorX: p.x,
      anchorY: p.y,
      dirPrimary: outward,
      dirSecondary: tangent,
      boxW,
      boxH,
    });
    return {
      id: j.id || `j-${i}`,
      p,
      tag,
      angle,
      tagX: center.x,
      tagY: center.y - 3,
      angleX: center.x,
      angleY: center.y + 8,
    };
  });
  return {
    curved: false,
    start: null,
    end: null,
    control: null,
    apex: null,
    tangentMm: Math.round(Number(bayGeometry?.derived?.tangentWidth || bayConfig?.tangentWidth || 0)),
    projectionMm: Math.round(Number(bayGeometry?.derived?.projection || 0)),
    valid: !!bayGeometry?.derived?.isValid,
    facets: segs,
    joints,
    mapped,
  };
}

export function WindowGeneratorBaySchematicSvg({ payload, widthPx = 320 }) {
  const preview = buildBaySchematicPreview(payload);
  if (!preview) return null;
  const viewW = 300;
  const viewH = 210;
  const heightPx = Math.round((Number(widthPx || 320) * viewH) / viewW);
  return (
    <svg viewBox={`0 0 ${viewW} ${viewH}`} width={widthPx} height={heightPx} xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width={viewW} height={viewH} fill={DESIGN_CANVAS_BG} rx="4" />
      {preview.curved ? (
        <>
          <line x1={preview.start.x} y1={preview.start.y} x2={preview.end.x} y2={preview.end.y} stroke="#5a6070" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.9" />
          <path d={`M ${preview.start.x} ${preview.start.y} Q ${preview.control.x} ${preview.control.y} ${preview.end.x} ${preview.end.y}`} fill="none" stroke="#3d6be0" strokeWidth="3" strokeLinecap="round" />
          <line x1={preview.apex.x} y1={preview.start.y} x2={preview.apex.x} y2={preview.apex.y} stroke="#2a3342" strokeWidth="1" strokeDasharray="3,3" />
          <line x1={preview.apex.x - 5} y1={preview.start.y} x2={preview.apex.x + 5} y2={preview.start.y} stroke="#2a3342" strokeWidth="1" />
          <line x1={preview.apex.x - 5} y1={preview.apex.y} x2={preview.apex.x + 5} y2={preview.apex.y} stroke="#2a3342" strokeWidth="1" />
          <text x={(preview.start.x + preview.end.x) / 2} y={preview.start.y - 6} textAnchor="middle" fill="#2a3342" fontSize="9.5" fontFamily="monospace" fontWeight="700">{`${preview.tangentMm} mm`}</text>
          <text x={preview.apex.x + 8} y={(preview.start.y + preview.apex.y) / 2} dominantBaseline="middle" fill="#2a3342" fontSize="9.5" fontFamily="monospace" fontWeight="700">{`${preview.projectionMm} mm`}</text>
        </>
      ) : (
        <>
          {preview.facets.map((s) => (
            <line key={`bay-sch-seg-${s.id}`} x1={s.p0.x} y1={s.p0.y} x2={s.p1.x} y2={s.p1.y} stroke="#3d6be0" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
          ))}
          <line
            x1={preview.mapped?.[0]?.x ?? 0}
            y1={preview.mapped?.[0]?.y ?? 0}
            x2={preview.mapped?.[preview.mapped.length - 1]?.x ?? 0}
            y2={preview.mapped?.[preview.mapped.length - 1]?.y ?? 0}
            stroke="#5a6070"
            strokeWidth="1.1"
            strokeDasharray="4,3"
            opacity="0.9"
          />
          <text
            x={((preview.mapped?.[0]?.x ?? 0) + (preview.mapped?.[preview.mapped.length - 1]?.x ?? 0)) / 2}
            y={(Math.min(preview.mapped?.[0]?.y ?? 0, preview.mapped?.[preview.mapped.length - 1]?.y ?? 0)) - 6}
            textAnchor="middle"
            fill="#2a3342"
            fontSize="9.5"
            fontFamily="monospace"
            fontWeight="700"
          >
            {`${preview.tangentMm} mm`}
          </text>
          {preview.facets.map((s) => {
            const mx = (s.p0.x + s.p1.x) / 2;
            const my = (s.p0.y + s.p1.y) / 2;
            return (
              <text key={`bay-sch-w-${s.id}`} x={mx} y={my - 6} textAnchor="middle" fill="#2a3342" fontSize="9.6" fontFamily="monospace" fontWeight="700">
                {s.width}
              </text>
            );
          })}
          {preview.joints.map((j) => (
            <g key={`bay-sch-j-${j.id}`}>
              <circle cx={j.p.x} cy={j.p.y} r="3.2" fill="#f0c050" stroke="#7a5a12" strokeWidth="0.8" />
              <text x={j.tagX} y={j.tagY} textAnchor="middle" fill="#3c4452" fontSize="9.6" fontFamily="monospace" fontWeight="700">{j.tag}</text>
              <text x={j.angleX} y={j.angleY} textAnchor="middle" fill="#3c4452" fontSize="10" fontFamily="monospace" fontWeight="700">{j.angle}°</text>
            </g>
          ))}
        </>
      )}
      {!preview.valid && <text x={8} y={14} fill="#9c2f2f" fontSize="9" fontFamily="monospace" fontWeight="700">Invalid bay geometry</text>}
    </svg>
  );
}

/**
 * Renders a Window Generator payload at a given height (same scale as static image previews).
 * Uses the same margin as ImageCanvasViewer (default 36px) so WG thumbnails match static style card scale.
 */
const DEFAULT_THUMB_MARGIN = 36;

export function WindowGeneratorThumbnail({ payload, heightPx = 120, marginPx = DEFAULT_THUMB_MARGIN, idPrefix = undefined, showBayWarnings = false }) {
  const uniqueId = React.useId();
  const prefix = idPrefix != null ? idPrefix : `wg-${uniqueId.replace(/:/g, '')}`;
  if (!payload || !payload.root || payload.w == null || payload.h == null) {
    return <div style={{ height: heightPx, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#666' }}>No design</div>;
  }
  const rootForRender = normalizeRootForRender(payload.root);
  const w = Number(payload.w);
  const h = Number(payload.h);
  const normalizedOpenMaps = normalizeRenderOpenMaps(
    rootForRender,
    payload.opens || null,
    payload.slideOpens || payload.slide_openings || payload.slide_opens || null,
    payload.presentation || null
  );
  const opens = normalizedOpenMaps.opens || null;
  const slideOpenings = normalizedOpenMaps.slideOpens || null;
  const aspect = w / h;
  const m = Math.max(0, Math.floor(marginPx));
  const innerH = Math.max(1, heightPx - m * 2);
  const innerW = Math.max(1, innerH * aspect);
  const pres = (payload.presentation || {});
  const normalizedBayConfig = inferBayConfigFromRoot(rootForRender, w, h, pres.bayConfig || null);
  const bayGeometry = String(rootForRender?.preset || '') === 'bay' ? computeBayGeometry(normalizedBayConfig) : null;
  const iB = { x: OB, y: OB, w: Math.max(20, w - OB * 2), h: Math.max(20, h - OB * 2) };
  const remappedPaneKinds = remapPaneHandleKindsForRender(
    (pres.paneHandleKinds && typeof pres.paneHandleKinds === 'object') ? pres.paneHandleKinds : {},
    payload.root,
    rootForRender,
    iB
  );
  const opts = pres ? {
    frameColor: pres.frameColExt || '#F6F6F6',
    glassType: pres.glassType || 'clear',
    glassStyleTile: pres.glassStyleTile || null,
    handleColor: pres.handleCol || '#F0F0F0',
    handleStyle: pres.handleStyle || 'lever',
    doorHandleType: pres.doorHandleType || 'lever',
    handleSide: pres.handleSide || 'left',
    handleDirs: remapHandleDirsForRender(
      (pres.handleDirs && typeof pres.handleDirs === 'object') ? pres.handleDirs : {},
      payload.root,
      rootForRender,
      iB
    ),
    paneHandleKinds: remappedPaneKinds,
    ventPos: pres.ventPos || 'none',
    paneVents: pres.paneVents || {},
    logicalDivs: payload.logicalDivs || {},
    panelInfillStyle: pres.panelInfillStyle || 'plain',
    jointStyle: pres.jointStyle || 'mitre-45',
    sashRoles: normalizeSashRoles(pres.sashRoles || {}),
  } : null;
  const normalizeBarCount = (value, fallback) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(0, Math.round(n));
  };
  const gBar = {
    on: !!pres.gBarOn,
    cols: normalizeBarCount(pres.gBarCols, 3),
    rows: normalizeBarCount(pres.gBarRows, 4),
    widthMm: Number(pres.gBarWidth || 18),
    offX: Number(pres.gBarOffX || 0),
    offY: Number(pres.gBarOffY || 0),
  };
  const aBar = {
    on: !!pres.aBarOn,
    cols: normalizeBarCount(pres.aBarCols, 3),
    rows: normalizeBarCount(pres.aBarRows, 4),
    widthMm: Number(pres.aBarWidth || 18),
    offX: Number(pres.aBarOffX || 0),
    offY: Number(pres.aBarOffY || 0),
  };
  const extTopPx = extenderMmToPx(Number(pres.extTop || 0));
  const elements = renderDesignWin(w, h, rootForRender, 0, 0, opens, slideOpenings, prefix, opts);
  const designVents = renderDesignTrickleVents({
    ww: w,
    wh: h,
    rootCell: rootForRender,
    ox: 0,
    oy: 0,
    openings: opens,
    slideOpenings,
    vp: pres.ventPos || 'none',
    pVents: pres.paneVents || {},
    fc: pres.frameColExt || '#F6F6F6',
    extTopPx,
    idPrefix: prefix,
  });
  return (
    <div
      style={{
        height: heightPx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: m,
        boxSizing: 'border-box',
        backgroundColor: DESIGN_CANVAS_BG,
      }}
    >
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width={innerW}
        height={innerH}
        style={{ display: 'block', maxWidth: '100%' }}
      >
        {elements}
        {designVents}
        {!!(showBayWarnings && bayGeometry && Array.isArray(bayGeometry?.derived?.warnings) && bayGeometry.derived.warnings.length) && (
          <g>
            <rect x={6} y={6} width={Math.min(w - 12, 210)} height={16} rx="3" fill="rgba(119,22,22,0.86)" />
            <text x={10} y={17} fill="#ffd8d8" fontSize="7.4" fontFamily="monospace" fontWeight="700">Invalid bay geometry</text>
          </g>
        )}
      </svg>
    </div>
  );
}

export default WindowGeneratorThumbnail;
